import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { useToastStore } from "../../stores/toastStore";
import { useUiContextStore, type PageView } from "../../stores/uiContextStore";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { formatVersion } from "../../utils/formatters";
import { COUNTRIES, getCountryName } from "../../utils/countries";
import {
  Search,
  Plus,
  ChevronRight,
  Upload,
  AlertTriangle,
  CheckCircle,
  X,
  RefreshCw,
  Loader2,
  ArrowLeft,
  FileText,
  Link2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Send,
  ShieldCheck,
  XCircle,
  Globe,
  Archive,
  Ban,
  MessageSquare,
  Pencil,
  Save,
  Download,
  Import,
  Info,
  Check,
  Clock,
  Layers,
  LayoutGrid,
  BookOpen,
  Cpu,
  Workflow,
} from "lucide-react";
import {
  DataTable,
  Column,
  cycleSortDir,
} from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import {
  EntityCardGrid,
  StatTile,
  StatTiles,
  MetaDates,
  FlagChip,
  JurisdictionArt,
  preloadCountryGeometries,
  catalogFlagAccent,
  VersionChip,
  TypeBadge,
  CardActions,
  TitleTooltip,
} from "../components/EntityCardGrid";
import type { CardAccent } from "../components/EntityCardGrid";
import { Dropdown } from "../components/Dropdown";
import {
  resolveCloudBrand,
  CloudBrandBadge,
  CloudBrandArt,
  accentTextVariants,
} from "../../utils/cloudBrands";
import { StatusBadge } from "../components/StatusBadge";
import { Tip, Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { ControlTreeView, ControlNode } from "../components/ControlTreeView";
import { ResizablePanels } from "../components/ResizablePanels";
import {
  SkeletonList,
  SkeletonCard,
  SkeletonControlPanel,
} from "../components/Skeleton";
import {
  Modal,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Switch } from "../components/ui/switch";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import {
  searchCatalogs,
  getCatalog,
  createCatalog,
  deleteCatalog,
  createGroup,
  deleteGroup,
  createControl,
  deleteControlApi,
  listGroups,
  listControls,
  getControl,
  importCatalog,
  commitImportSession,
  abortImportSession,
  getImportSession,
  validateImport,
  transitionDocument,
  exportCatalogDoc,
  getExportJob,
  getExportDownloadUrl,
  type CatalogResponse,
  type CatalogType,
  type ImportJobResponse,
  type ImportSessionResponse,
  type ValidateResponse,
  type GroupResponse,
  type ControlResponse,
  type PartResponse,
  type PartPayload,
  type ParamPayload,
  type ParameterConstraint,
  type ParameterSelection,
  updateCatalog,
} from "../../api/frameworksApi";

/* ── UI row type for the catalogs table ── */
interface CatalogRow {
  [key: string]: unknown;
  id: string;
  documentId: string;
  name: string;
  version: number | string;
  group: number | string;
  controls: number | string;
  status: "active" | "draft" | "inactive";
  lifecycleStatus: string;
  catalogType?: CatalogType;
  countryName?: string | null;
  countryCode?: string | null;
  lastUpdated: string;
  created: string;
}

/** Vendor brand (AWS / Azure / GCP / ...) behind a Technology catalog,
 * resolved from its name. Only Technology catalogs are branded — a
 * regulatory catalog that merely mentions a vendor keeps its country art. */
function catalogBrand(row: CatalogRow) {
  return row.catalogType === "TECHNOLOGY" ? resolveCloudBrand(row.name) : null;
}

/** The card's own identity color - the one thing that makes a catalog card
 * look like *itself* rather than like its lifecycle status. Technology
 * catalogs take their vendor's brand color, Regulatory ones their country's
 * flag color; both then paint the same set of card parts (chips, stat
 * icons, meta icons, hover, accent bar) so the two families read as one
 * design language. Undefined for a card with no identity of its own -
 * Integration catalogs, unrecognised vendors, and catalogs saved without a
 * country - which stay deliberately neutral: no wash, no map, no hover
 * tint, and a grey accent bar rather than a color the card never shows. */
function catalogAccent(row: CatalogRow): CardAccent | undefined {
  if (row.catalogType === "TECHNOLOGY") return catalogBrand(row) ?? undefined;
  if (row.catalogType === "INTEGRATION") return undefined;
  return catalogFlagAccent(row.countryCode, row.countryName);
}

/** `catalogAccent` as a bare "r,g,b" triplet, for the props that take a
 * color rather than a full accent. */
function catalogRowAccentRgb(row: CatalogRow): string | undefined {
  return catalogAccent(row)?.rgb;
}

/** The module tint each catalog type already uses elsewhere on its card. */
const CHIP_TINT_RGB: Record<string, string> = {
  TECHNOLOGY: "2,132,199",
  INTEGRATION: "124,58,237",
  REGULATORY: "79,70,229",
};

const chipAccentCache = new Map<string, CardAccent>();

/** Colour for the card's chips (type badge + version chip). Falls back to the
 * card's module tint when it has no identity colour of its own, so the pair
 * always reads as one set - a blue "Technology" badge sitting next to a grey
 * "v1" chip looked like two unrelated things.
 *
 * Deliberately not folded into `catalogAccent`: this is chips only. The
 * card's bar, wash and hover still key off the real identity colour and stay
 * neutral without one. */
function catalogChipAccent(row: CatalogRow): CardAccent {
  const identity = catalogAccent(row);
  if (identity) return identity;
  const rgb = CHIP_TINT_RGB[row.catalogType ?? ""] ?? CHIP_TINT_RGB.REGULATORY;
  let accent = chipAccentCache.get(rgb);
  if (!accent) {
    accent = { rgb, ...accentTextVariants(rgb) };
    chipAccentCache.set(rgb, accent);
  }
  return accent;
}

/** Map backend status to UI badge variant */
function mapStatus(s: string): "active" | "draft" | "inactive" {
  switch (s) {
    case "ACTIVE":
      return "active";
    case "DEPRECATED":
      return "inactive";
    default:
      return "draft";
  }
}

/** Extract all controlIds from nested groups */
function extractControlIds(groups?: GroupResponse[]): string[] {
  if (!groups) return [];
  const ids: string[] = [];
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      ids.push(c.controlId);
    }
    ids.push(...extractControlIds(g.groups));
  }
  return ids;
}

/** Find the group's database UUID (id) that contains a given controlId */
function findGroupForControl(
  groups: GroupResponse[],
  controlId: string,
): string | null {
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      if (c.controlId.toUpperCase() === controlId.toUpperCase()) return g.id;
      for (const enh of c.enhancements ?? []) {
        if (enh.controlId.toUpperCase() === controlId.toUpperCase())
          return g.id;
      }
    }
    if (g.groups) {
      const found = findGroupForControl(g.groups, controlId);
      if (found) return found;
    }
  }
  return null;
}

/** Find the parent controlId for an enhancement */
function findParentControl(
  groups: GroupResponse[],
  controlId: string,
): string | null {
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      for (const enh of c.enhancements ?? []) {
        if (enh.controlId.toUpperCase() === controlId.toUpperCase())
          return c.controlId;
      }
    }
    if (g.groups) {
      const found = findParentControl(g.groups, controlId);
      if (found) return found;
    }
  }
  return null;
}

/** Extract all controls (with full data including enhancements) from nested groups */
function extractAllControls(groups?: GroupResponse[]): ControlResponse[] {
  if (!groups) return [];
  const result: ControlResponse[] = [];
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      result.push(c);
      if (c.enhancements) result.push(...c.enhancements);
    }
    result.push(...extractAllControls(g.groups));
  }
  return result;
}

/** Filter tree nodes based on filter mode */
function filterTree(
  nodes: ControlNode[],
  mode: "all" | "groups" | "controls" | "params",
): ControlNode[] {
  if (mode === "all") return nodes;
  return nodes.reduce<ControlNode[]>((acc, node) => {
    const isGroup = node.children && node.children.length > 0;
    if (mode === "groups") {
      if (isGroup) {
        // Keep only sub-group children (nodes that themselves have children), strip leaf controls
        const subGroups = node.children!.filter(
          (c) => c.children && c.children.length > 0,
        );
        acc.push({ ...node, children: filterTree(subGroups, mode) });
      }
    } else if (mode === "controls") {
      // Collect only leaf controls (no group containers), flat list
      if (isGroup) {
        acc.push(...filterTree(node.children!, mode));
      } else {
        acc.push(node);
      }
    } else if (mode === "params") {
      if (isGroup) {
        // Recursively filter children to only keep param nodes/branches
        const filtered = filterTree(node.children!, mode);
        if (node.hasParams || filtered.length > 0) {
          acc.push({ ...node, children: filtered });
        }
      } else if (node.hasParams) {
        acc.push(node);
      }
    }
    return acc;
  }, []);
}

/** Search/filter tree nodes by query string (matches id or title) */
function searchTree(nodes: ControlNode[], query: string): ControlNode[] {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes.reduce<ControlNode[]>((acc, node) => {
    const matches =
      node.id.toLowerCase().includes(q) || node.title.toLowerCase().includes(q);
    if (node.children && node.children.length > 0) {
      const filteredChildren = searchTree(node.children, query);
      if (matches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
        });
      }
    } else if (matches) {
      acc.push(node);
    }
    return acc;
  }, []);
}

/** Convert API catalog to table row */
function toRow(c: CatalogResponse): CatalogRow {
  return {
    id: c.id,
    documentId: c.documentId,
    name: c.title ?? "-",
    version: c.versionNumber ?? "-",
    group: c.groupCount ?? "-",
    controls: c.controlCount ?? "-",
    status: mapStatus(c.status ?? "DRAFT"),
    lifecycleStatus: c.lifecycleStatus ?? "draft",
    catalogType: c.catalogType,
    countryName: c.countryName,
    countryCode: c.countryCode,
    lastUpdated: c.updatedAt
      ? c.updatedAt.split("T")[0].split("-").reverse().join("/")
      : "-",
    created: c.createdAt
      ? c.createdAt.split("T")[0].split("-").reverse().join("/") 
      : "-",
  };
}

/** Display label for a catalog type */
function catalogTypeLabel(type?: CatalogType | null): string {
  switch (type) {
    case "TECHNOLOGY":
      return "Technology";
    case "INTEGRATION":
      return "Integration";
    case "REGULATORY":
      return "Regulatory";
    default:
      return "Regulatory";
  }
}

/**
 * Sortable DataTable column key -> backend field name sent as `sortByColumn`
 * in POST /catalogs/{page}/{size}. Sorting is fully server-side; a key with no
 * entry here is forwarded as-is.
 */
const CAT_SORT_COLUMNS: Record<string, string> = {
  name: "title",
  catalogType: "catalogType",
  version: "versionNumber",
  group: "groupCount",
  controls: "controlCount",
  created: "createdAt",
  lastUpdated: "updatedAt",
  lifecycleStatus: "lifecycleStatus",
};

/** Default page size per view — cards fill 12 slots, tables show 10 rows. */
const CAT_DEFAULT_CARD_PAGE_SIZE = 12;
const CAT_DEFAULT_TABLE_PAGE_SIZE = 10;

/** Catalog type options for selectors/filters */
const CATALOG_TYPE_OPTIONS: { value: CatalogType; label: string }[] = [
  { value: "REGULATORY", label: "Regulatory" },
  { value: "TECHNOLOGY", label: "Technology" },
  { value: "INTEGRATION", label: "Integration" },
];

/** Catalog type badge (Regulatory / Technology / Integration) */
function CatalogTypeBadge({ type }: { type: CatalogType }) {
  const styles: Record<CatalogType, string> = {
    TECHNOLOGY:
      "bg-sky-50 text-sky-700 border-sky-200/70 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20",
    INTEGRATION:
      "bg-violet-50 text-violet-700 border-violet-200/70 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20",
    REGULATORY:
      "bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-medium border ${
        styles[type] ?? styles.REGULATORY
      }`}
    >
      {catalogTypeLabel(type)}
    </span>
  );
}

/** Country chip — shows country name and/or code; renders nothing if both empty */
function CountryChip({
  name,
  code,
}: {
  name?: string | null;
  code?: string | null;
}) {
  if (!name && !code) return null;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-white/[0.06] dark:text-white/60 text-[10px] font-medium">
      <Globe className="size-2.5 flex-shrink-0" />
      {name || code}
      {name && code && <span className="opacity-60">({code})</span>}
    </span>
  );
}

/* ── Overview stat card (matches the Content Registry detail header) ── */
const catStatColors: Record<
  string,
  { icon: string; iconBg: string; bg: string; ring: string; border: string }
> = {
  indigo: {
    icon: "text-indigo-500 dark:text-indigo-400",
    iconBg: "bg-indigo-100/80 dark:bg-indigo-500/15",
    bg: "bg-indigo-50/40 dark:bg-indigo-500/[0.06]",
    ring: "ring-indigo-200/60 dark:ring-indigo-500/20",
    border: "border-l-indigo-400 dark:border-l-indigo-500",
  },
  emerald: {
    icon: "text-emerald-500 dark:text-emerald-400",
    iconBg: "bg-emerald-100/80 dark:bg-emerald-500/15",
    bg: "bg-emerald-50/40 dark:bg-emerald-500/[0.06]",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/20",
    border: "border-l-emerald-400 dark:border-l-emerald-500",
  },
  sky: {
    icon: "text-sky-500 dark:text-sky-400",
    iconBg: "bg-sky-100/80 dark:bg-sky-500/15",
    bg: "bg-sky-50/40 dark:bg-sky-500/[0.06]",
    ring: "ring-sky-200/60 dark:ring-sky-500/20",
    border: "border-l-sky-400 dark:border-l-sky-500",
  },
  violet: {
    icon: "text-violet-500 dark:text-violet-400",
    iconBg: "bg-violet-100/80 dark:bg-violet-500/15",
    bg: "bg-violet-50/40 dark:bg-violet-500/[0.06]",
    ring: "ring-violet-200/60 dark:ring-violet-500/20",
    border: "border-l-violet-400 dark:border-l-violet-500",
  },
  amber: {
    icon: "text-amber-500 dark:text-amber-400",
    iconBg: "bg-amber-100/80 dark:bg-amber-500/15",
    bg: "bg-amber-50/40 dark:bg-amber-500/[0.06]",
    ring: "ring-amber-200/60 dark:ring-amber-500/20",
    border: "border-l-amber-400 dark:border-l-amber-500",
  },
  rose: {
    icon: "text-rose-500 dark:text-rose-400",
    iconBg: "bg-rose-100/80 dark:bg-rose-500/15",
    bg: "bg-rose-50/40 dark:bg-rose-500/[0.06]",
    ring: "ring-rose-200/60 dark:ring-rose-500/20",
    border: "border-l-rose-400 dark:border-l-rose-500",
  },
  slate: {
    icon: "text-slate-400 dark:text-white/40",
    iconBg: "bg-slate-100/80 dark:bg-white/[0.06]",
    bg: "bg-slate-50/60 dark:bg-white/[0.03]",
    ring: "ring-slate-200/50 dark:ring-white/[0.08]",
    border: "border-l-slate-300 dark:border-l-white/20",
  },
};

function CatStatCard({
  icon,
  label,
  value,
  color = "slate",
  dim = false,
  valueClassName = "",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  dim?: boolean;
  valueClassName?: string;
}) {
  const c = catStatColors[color] ?? catStatColors.slate;
  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${c.bg} ring-1 ${c.ring} border-l-2 ${c.border} transition-colors`}
    >
      <div
        className={`flex-shrink-0 size-7 rounded-lg ${c.iconBg} flex items-center justify-center`}
      >
        <span className={c.icon}>{icon}</span>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] font-bold tracking-widest d">
          {label}
        </span>
        <span
          className={`leading-tight truncate ${dim ? "text-xs italic d/70" : "text-sm font-semibold text-foreground"} ${valueClassName}`}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

// Map a catalog lifecycle status to a meaningful stat-card color.
function catStatusColor(status: string): string {
  switch (status) {
    case "published":
      return "emerald";
    case "approved":
      return "violet";
    case "in_review":
      return "sky";
    case "rejected":
      return "rose";
    case "superseded":
    case "withdrawn":
      return "amber";
    default: // draft
      return "slate";
  }
}

/* ── Helper: convert API groups/controls into ControlNode tree ── */
function controlToNode(ctrl: ControlResponse): ControlNode {
  const children = (ctrl.enhancements ?? []).map(controlToNode);
  return {
    id: ctrl.controlId.toUpperCase(),
    title: ctrl.title,
    children: children.length ? children : undefined,
    hasParams: (ctrl.params?.length ?? 0) > 0,
  };
}

/** Flatten root controls and nested enhancements into one selectable list. */
function flattenControls(controls?: ControlResponse[]): ControlResponse[] {
  if (!controls) return [];
  const result: ControlResponse[] = [];
  for (const control of controls) {
    result.push(control);
    result.push(...flattenControls(control.enhancements));
  }
  return result;
}

function groupToNode(group: GroupResponse): ControlNode {
  const controlChildren = (group.controls ?? []).map(controlToNode);
  const subGroupChildren = (group.groups ?? []).map(groupToNode);
  return {
    id: `group:${group.groupId.toUpperCase()}`,
    label: group.groupId.toUpperCase(),
    title: group.title,
    children: [...subGroupChildren, ...controlChildren],
  };
}

/** Sort nodes so groups with children appear first, empty leaf nodes go to the bottom */
function sortNodesChildrenFirst(nodes: ControlNode[]): ControlNode[] {
  return [...nodes].sort((a, b) => {
    const aHas = (a.children?.length ?? 0) > 0 ? 0 : 1;
    const bHas = (b.children?.length ?? 0) > 0 ? 0 : 1;
    return aHas - bHas;
  });
}
/** Strip the "group:" prefix to get the raw groupId from a group node id */
function rawGroupId(nodeId: string): string {
  return nodeId.startsWith("group:") ? nodeId.slice(6) : nodeId;
}

/** The `PageView` each type tab publishes to the AI Companion. Module scope so the effect that reads it
    has a stable dependency, and so the union is checked against `uiContextStore`'s `PageView` here rather
    than inside the effect. Keyed by `catTypeFilter`, whose empty string is the "All Catalogs" tab. */
const PAGE_VIEW_BY_CATALOG_TYPE = {
  "": "CATALOG_LIST",
  REGULATORY: "CATALOG_LIST_REGULATORY",
  TECHNOLOGY: "CATALOG_LIST_TECHNOLOGY",
  INTEGRATION: "CATALOG_LIST_INTEGRATION",
} as const satisfies Record<"" | CatalogType, PageView>;

/** Extract param IDs referenced in prose via OSCAL {{ insert: param, id-ref="" }} or {{ insert: param, id }} syntax */
function extractParamIdsFromProse(parts: PartPayload[]): string[] {
  const ids: string[] = [];
  const regex =
    /\{\{\s*insert:\s*param\s*,\s*(?:id-ref\s*=\s*"([^"]+)"|([\w.-]+))\s*\}\}/gi;
  for (const part of parts ?? []) {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(part.prose ?? "")) !== null) {
      const id = match[1] ?? match[2];
      if (id && !ids.includes(id)) ids.push(id);
    }
    if (part.parts?.length) {
      for (const id of extractParamIdsFromProse(part.parts)) {
        if (!ids.includes(id)) ids.push(id);
      }
    }
  }
  return ids;
}

/** UI-only param shape that also remembers which part it was added under */
type UIParamPayload = ParamPayload & { _partIdx?: number };

/** Drops params whose paramId no longer has a matching prose reference; never auto-adds new ones */
function pruneParamsToProseRefs(
  currentParams: UIParamPayload[],
  paramIds: string[],
): UIParamPayload[] {
  const idSet = new Set(paramIds);
  return currentParams.filter((p) => !p.paramId || idSet.has(p.paramId));
}

/** Sync params array to match param IDs found in prose, preserving existing data */
function syncParamsToProseRefs(
  currentParams: UIParamPayload[],
  paramIds: string[],
  partIndexMap?: Record<string, number>,
): UIParamPayload[] {
  return paramIds.map((id, idx) => {
    const existing = currentParams.find((p) => p.paramId === id);
    const partIdx = partIndexMap?.[id];
    if (existing) {
      return {
        ...existing,
        sortOrder: idx + 1,
        _partIdx: existing._partIdx ?? partIdx,
      };
    }
    return {
      paramId: id,
      label: "",
      usage: "",
      constraints: [],
      guidelines: [],
      values: [],
      selectConfig: null,
      sortOrder: idx + 1,
      _partIdx: partIdx,
    };
  });
}

/* ── Wizard types ── */
type WizardStep = "catalog" | "groups" | "controls" | "review";

interface GroupDraft {
  groupId: string;
  title: string;
  clazz: string;
  sortOrder: number;
  savedId?: string; // UUID from backend after save
}

interface ControlDraft {
  controlId: string;
  title: string;
  clazz: string;
  groupIndex: number; // index into groups array
  parentControlId: string | null;
  sortOrder: number;
  parts: PartPayload[];
  params: ParamPayload[];
}

export function Catalogs() {
  const { id: catalogIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /* ── List state ──
   * Search, sort, filter and paging are all resolved by the backend via
   * POST /catalogs/{page}/{size}; `catalogs` holds just the current page. */
  const [catalogs, setCatalogs] = useState<CatalogRow[]>([]);
  const [catTotal, setCatTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // True only while a *subsequent* fetch (e.g. after a create/delete) is in
  // flight, so the previous rows stay on screen instead of flashing back to
  // a skeleton.
  const [listRefreshing, setListRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // The keyword actually sent to the backend (committed from `search`).
  const [catKeyword, setCatKeyword] = useState("");
  const [catStatusFilter, setCatStatusFilter] = useState("");
  const [catTypeFilter, setCatTypeFilter] = useState<"" | CatalogType>("");
  const [catPage, setCatPage] = useState(0);
  const [catViewMode, setCatViewMode] = useState<"card" | "table">("card");
  // Each view keeps its own page size so toggling back restores the user's
  // choice instead of snapping to the other view's default.
  const [catCardPageSize, setCatCardPageSize] = useState(
    CAT_DEFAULT_CARD_PAGE_SIZE,
  );
  const [catTablePageSize, setCatTablePageSize] = useState(
    CAT_DEFAULT_TABLE_PAGE_SIZE,
  );
  const catPageSize =
    catViewMode === "table" ? catTablePageSize : catCardPageSize;
  const setCatPageSize = (n: number) => {
    if (catViewMode === "table") setCatTablePageSize(n);
    else setCatCardPageSize(n);
  };
  const [catSortKey, setCatSortKey] = useState<string | null>(null);
  const [catSortDir, setCatSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default order).
  const handleCatSort = (key: string) => {
    // Only a column CAT_SORT_COLUMNS knows about maps onto a real CatalogRow
    // field — anything else is left alone rather than sorting on nothing.
    if (!CAT_SORT_COLUMNS[key]) return;
    const next = cycleSortDir(catSortKey, catSortDir, key);
    setCatPage(0); // any order change restarts at the first page
    setCatSortKey(next.sortKey);
    setCatSortDir(next.sortDir);
  };

  /** Per-type totals for the type tabs. The backend supplies these alongside
   * the page when it can; a tab whose total we've never been told stays
   * unbadged rather than showing a count derived from one page. */
  const [catTypeCounts, setCatTypeCounts] = useState<
    Partial<Record<string, number>>
  >({});

  /* ── Detail state ── */
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogRow | null>(
    null,
  );

  const [detailLoading, setDetailLoading] = useState(false);
  const [controlTree, setControlTree] = useState<ControlNode[]>([]);
  const [allControls, setAllControls] = useState<ControlResponse[]>([]);
  const [selectedControl, setSelectedControl] =
    useState<ControlResponse | null>(null);

  // Declared here rather than with the rest of the wizard state below because the effect that follows
  // reads it, and a dependency array is evaluated during render — referencing it before its `const`
  // would be a temporal-dead-zone error, not a stale value. Same for the selected group, whose other
  // state lives with the group editor further down.
  const [showWizard, setShowWizard] = useState(false);
  const [selectedGroupNode, setSelectedGroupNode] =
    useState<ControlNode | null>(null);

  // Publish where the user is so the AI Companion can offer questions about it. Needed because the URL
  // stays on the list path when a row is selected (see handleSelectCatalog below), so the route alone can
  // tell the companion neither which record is on screen nor which of this page's faces it is showing —
  // the list under one of its four type tabs, the control-groups tree, one group, one control, or the
  // create wizard. Both are published in one call so the companion cannot observe a half-state and fetch
  // for it. Cleared on unmount so leaving this page cannot leak the selection into the next one.
  //
  // Ordered most specific first, because more than one of these is true at a time: a control is open
  // *inside* a group *inside* a catalog, and the innermost thing the user is looking at is the one the
  // questions should be about.
  useEffect(() => {
    const store = useUiContextStore.getState();
    if (showWizard) {
      // The wizard covers the page, so what is selected behind it is not what the user is working on.
      store.setPageContext("CATALOG_CREATE", null);
    } else if (selectedControl && selectedCatalog) {
      store.setPageContext("CONTROL_DETAIL", {
        id: String(selectedControl.controlId),
        name: selectedControl.controlId.toUpperCase(),
        parentName: selectedCatalog.name,
      });
    } else if (selectedGroupNode && selectedCatalog) {
      // Published as a record and not only as a view, so a question can name the group rather than
      // merely being worded differently. It costs a suggestions request per group opened, which is the
      // same price a control already costs and the reason the panel changes at all.
      store.setPageContext("CONTROL_GROUP_DETAIL", {
        id: rawGroupId(selectedGroupNode.id),
        name: selectedGroupNode.title,
        parentName: selectedCatalog.name,
      });
    } else if (selectedCatalog) {
      store.setPageContext("CATALOG_DETAIL", {
        id: String(selectedCatalog.id),
        name: selectedCatalog.name,
      });
    } else {
      // The type tab is part of which page this is: the four tabs share a route and a screen, so
      // without this a user filtered to regulatory catalogs is offered the same questions as one
      // looking at every catalog on the platform.
      store.setPageContext(PAGE_VIEW_BY_CATALOG_TYPE[catTypeFilter], null);
    }
    return () => useUiContextStore.getState().clearPageContext();
  }, [selectedCatalog, selectedControl, selectedGroupNode, showWizard, catTypeFilter]);
  const [treeFilter, setTreeFilter] = useState<"all" | "params">("all");
  const [treeSearch, setTreeSearch] = useState("");
  const [treeExpandAll, setTreeExpandAll] = useState<boolean | undefined>(
    undefined,
  );
  const [treeExpandTrigger, setTreeExpandTrigger] = useState(0);

  /* ── Inline rename state ── */
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [editingCatalogType, setEditingCatalogType] =
    useState<CatalogType>("REGULATORY");
  const [editingCountryName, setEditingCountryName] = useState("");
  const [editingCountryCode, setEditingCountryCode] = useState("");
  const [pendingCatalogTypeChange, setPendingCatalogTypeChange] =
    useState<CatalogType | null>(null);
  const [savingName, setSavingName] = useState(false);

  /* ── Delete state ── */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  /* ── Export state ── */
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportModalDocId, setExportModalDocId] = useState<string | null>(null);
  const [exportModalName, setExportModalName] = useState("");
  const [exportFormat, setExportFormat] = useState<string>("");

  /* ── Wizard state ── */
  // `showWizard` is declared with the page-context effect above, which needs it in its dependency array.
  const [wizardStep, setWizardStep] = useState<WizardStep>("catalog");
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);

  // Step 1: Catalog
  const [wizCatalogTitle, setWizCatalogTitle] = useState("");
  const [wizCatalogId, setWizCatalogId] = useState<string | null>(null);
  const [wizCatalogType, setWizCatalogType] = useState<CatalogType | "">("");
  const [wizCountryName, setWizCountryName] = useState("");
  const [wizCountryCode, setWizCountryCode] = useState("");

  /* ── Organization state ── */
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [wizCatalogOrgId, setWizCatalogOrgId] = useState("");
  const [importOrgId, setImportOrgId] = useState("");
  const [importCatalogType, setImportCatalogType] =
    useState<CatalogType | "">("");
  const [importCountryCode, setImportCountryCode] = useState("");
  // Organization is only a real user choice once there's more than one accessible org.
  const canPickImportSource =
    (accessibleOrgs.length <= 1 || !!importOrgId) && !!importCatalogType;

  // Step 2: Groups
  const [wizGroups, setWizGroups] = useState<GroupDraft[]>([
    { groupId: "", title: "", clazz: "family", sortOrder: 1 },
  ]);

  // Step 3: Controls
  const [wizControls, setWizControls] = useState<ControlDraft[]>([]);
  const [expandedControl, setExpandedControl] = useState<number | null>(null);

  /* ── OSCAL Import pipeline state ── */
  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState<
    | "select"
    | "url"
    | "validating"
    | "validated"
    | "importing"
    | "findings"
    | "committed"
  >("select");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importUrl, setImportUrl] = useState("");
  const [importMode, setImportMode] = useState<"full" | "delta">("full");
  const [importError, setImportError] = useState<string | null>(null);
  const [importErrorTitle, setImportErrorTitle] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportJobResponse | null>(
    null,
  );
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(
    null,
  );
  const [validating, setValidating] = useState(false);

  /* ── Import Session modal state ── */
  const [showSession, setShowSession] = useState(false);
  const [sessionData, setSessionData] = useState<ImportSessionResponse | null>(
    null,
  );
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [aborting, setAborting] = useState(false);

  /* ── Lifecycle transition state ── */
  const [showTransition, setShowTransition] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<string>("");
  const [transitionLabel, setTransitionLabel] = useState("");
  const [transitionComments, setTransitionComments] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionRequireComments, setTransitionRequireComments] =
    useState(false);

  /* ── Unsaved-changes guard ── */
  const [pendingSelectNode, setPendingSelectNode] = useState<ControlNode | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  /* ── Inline edit state ── */
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editClazz, setEditClazz] = useState("");
  const [editSortOrder, setEditSortOrder] = useState(1);
  const [editParts, setEditParts] = useState<(PartPayload & { id?: string })[]>(
    [],
  );
  const [editParams, setEditParams] = useState<
    (ParamPayload & { id?: string })[]
  >([]);
  const [savingEdit, setSavingEdit] = useState(false);

  /* ── Group edit state ── */
  const [editGroupMode, setEditGroupMode] = useState(false);
  const [editGroupId, setEditGroupId] = useState("");
  const [editGroupTitle, setEditGroupTitle] = useState("");
  const [editGroupClazz, setEditGroupClazz] = useState("");
  const [editGroupSortOrder, setEditGroupSortOrder] = useState(1);
  const [savingGroup, setSavingGroup] = useState(false);

  /* ── Stored groups from API (for UUID lookups) ── */
  const [catalogGroups, setCatalogGroups] = useState<GroupResponse[]>([]);

  /* ── Add Group modal state ── */
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupTitle, setNewGroupTitle] = useState("");
  const [newGroupClazz, setNewGroupClazz] = useState("family");
  const [newGroupSortOrder, setNewGroupSortOrder] = useState(1);
  const [savingNewGroup, setSavingNewGroup] = useState(false);

  /* ── Add Control modal state ── */
  const [showAddControl, setShowAddControl] = useState(false);
  const [addControlGroupId, setAddControlGroupId] = useState("");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newControlId, setNewControlId] = useState("");
  const [newControlTitle, setNewControlTitle] = useState("");
  const [newControlClazz, setNewControlClazz] = useState("SP800-53");
  const [newControlSortOrder, setNewControlSortOrder] = useState(1);
  const [newControlParts, setNewControlParts] = useState<PartPayload[]>([
    {
      partId: "",
      name: "statement",
      ns: "http://csrc.nist.gov/ns/oscal",
      clazz: "statement",
      title: null,
      prose: "",
      sortOrder: 1,
      parts: [],
    },
  ]);
  const [newControlParams, setNewControlParams] = useState<UIParamPayload[]>(
    [],
  );
  const [savingNewControl, setSavingNewControl] = useState(false);

  /* ── Fetch one page of catalogs ──
   * POST /catalogs/{page}/{size} with { keyword, sortByColumn, sortingOrder,
   * lifecycleStatus, catalogType }. Nothing is filtered, sorted or sliced on
   * this side. */
  // The skeleton is only shown before the very first load ever lands; every
  // later fetch keeps the current rows visible (no blank page on refresh).
  const catFirstLoadDoneRef = useRef(false);

  // ── Expand/Collapse Callback (Catalog Controls) ──
  // Sync button state with tree expansion when user manually expands/collapses nodes
  const handleCatalogAllNodesExpandedChange = useCallback((allExpanded: boolean) => {
    setTreeExpandAll(allExpanded);
  }, []);

  const fetchCatalogs = useCallback(async () => {
    setError(null);
    if (catFirstLoadDoneRef.current) setListRefreshing(true);
    else setLoading(true);
    try {
      const res = await searchCatalogs(catPage, catPageSize, {
        keyword: catKeyword,
        sortByColumn: catSortKey
          ? (CAT_SORT_COLUMNS[catSortKey] ?? catSortKey)
          : "",
        sortingOrder: catSortKey ? catSortDir : "",
        lifecycleStatus: catStatusFilter || null,
        catalogType: catTypeFilter || null,
      });
      setCatalogs((res.items ?? []).map((c) => toRow(c)));
      setCatTotal(res.totalItems);
      // Remember whatever totals the backend volunteered; when it sends none,
      // record this tab's own total so its badge is at least right.
      setCatTypeCounts((prev) => ({
        ...prev,
        ...(res.typeCounts ?? {
          [catTypeFilter || "ALL"]: res.totalItems,
        }),
      }));
      // A narrowed filter can leave us past the last page — step back.
      if (res.items.length === 0 && catPage > 0 && res.totalItems > 0) {
        setCatPage(Math.max(0, Math.ceil(res.totalItems / catPageSize) - 1));
      }
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { errors?: { detail?: string }[] } } })
          ?.response?.data?.errors?.[0]?.detail;
      const message =
        detail ??
        (err instanceof Error ? err.message : "Failed to load catalogs");
      setError(message);
      setCatalogs([]);
      setCatTotal(0);
    } finally {
      catFirstLoadDoneRef.current = true;
      setLoading(false);
      setListRefreshing(false);
    }
  }, [
    catPage,
    catPageSize,
    catKeyword,
    catSortKey,
    catSortDir,
    catStatusFilter,
    catTypeFilter,
  ]);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  // Fetch all type counts on initial load so all tabs show their counts immediately.
  // Make separate targeted requests for each catalog type to ensure we get counts for all.
  useEffect(() => {
    const fetchAllTypeCounts = async () => {
      try {
        // Fetch counts for each type separately
        const types: Array<"" | CatalogType> = ["", "REGULATORY", "TECHNOLOGY", "INTEGRATION"];
        const counts: Partial<Record<string, number>> = {};

        for (const type of types) {
          try {
            const res = await searchCatalogs(0, 1, {
              keyword: "",
              sortByColumn: "",
              sortingOrder: "",
              lifecycleStatus: null,
              catalogType: type || null,
            });
            // Defensive: ensure response exists before accessing properties
            if (!res) continue;

            // Use the specific type count from response, or if backend returned
            // typeCounts, merge those too
            if (res.typeCounts) {
              Object.assign(counts, res.typeCounts);
            } else if (typeof res.totalItems === "number" && res.totalItems >= 0) {
              // Fall back to the response's total for this type (only if valid number)
              counts[type || "ALL"] = res.totalItems;
            }
          } catch {
            // Individual type fetch failed, skip it (graceful degradation)
          }
        }

        // Only update state if we actually got some counts
        if (Object.keys(counts).length > 0) {
          setCatTypeCounts((prev) => ({
            ...prev,
            ...counts,
          }));
        }
      } catch {
        // Silently ignore — if this fails, the counts will populate as tabs are clicked
      }
    };

    fetchAllTypeCounts();
  }, []);

  // Country map geometry is needed once for the flag chips, not on every
  // page/sort/filter change that refires the list query.
  useEffect(() => {
    preloadCountryGeometries();
  }, []);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared
   * (so emptying the field brings the full list straight back). */
  const submitCatSearch = (value: string) => {
    const next = value.trim();
    if (next === catKeyword) return; // nothing new to ask the backend for
    setCatKeyword(next);
    setCatPage(0); // a new keyword always restarts at the first page
  };

  /* ── Show toast on error change ── */
  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  /* ── Fetch accessible organizations ── */
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION&org_type=GROUP`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setWizCatalogOrgId(orgs[0].id);
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  /* ── Load catalog from URL parameter ── */
  // Tracks which catalog id we've already fetched detail for, so that the
  // list-loading (catalogs change) doesn't trigger a second identical
  // GET /catalogs/{id}. Reset when leaving the detail view.
  const fetchedDetailIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!catalogIdFromUrl) {
      fetchedDetailIdRef.current = null;
      return;
    }
    // Try to find it in the already-loaded catalogs so we can show the real
    // row (name, lifecycle, etc.) instead of the temporary placeholder.
    const catalog = catalogs.find((c) => c.id === catalogIdFromUrl);
    if (catalog) {
      setSelectedCatalog(catalog);
    } else if (fetchedDetailIdRef.current !== catalogIdFromUrl) {
      // Not in the list yet — show a temporary row until the list/detail loads
      const nowIso = new Date().toISOString();
      const tempCatalog: CatalogRow = {
        id: catalogIdFromUrl,
        documentId: catalogIdFromUrl,
        name: "Loading",
        version: 1,
        group: 0,
        controls: 0,
        status: "draft",
        lifecycleStatus: "draft",
        lastUpdated: nowIso,
        created: nowIso,
      };
      setSelectedCatalog(tempCatalog);
    }
    // Fetch the detail tree only once per catalog id.
    if (fetchedDetailIdRef.current !== catalogIdFromUrl) {
      fetchedDetailIdRef.current = catalogIdFromUrl;
      fetchCatalogDetail(catalogIdFromUrl);
    }
  }, [catalogIdFromUrl, catalogs]);

  /* ── Fetch catalog detail (groups + controls tree) ── */
  const fetchCatalogDetail = useCallback(async (catalogId: string) => {
    setEditMode(false);
    setEditGroupMode(false);
    setSelectedGroupNode(null);
    setDetailLoading(true);
    setSelectedControl(null);
    setAllControls([]);
    setCatalogGroups([]);
    // Always open a catalog in the normal (non-parameter) view, so the param
    // filter from a previously viewed catalog doesn't carry over.
    setTreeFilter("all");
    setTreeExpandAll(undefined);
    try {
      let catalogData;
      try {
        const res = await getCatalog(catalogId);
        catalogData = res.data;
      } catch (error) {
        // Fallback for view-only users: try to fetch controls via listControls if getCatalog fails
        try {
          const ctrlRes = await listControls(catalogId, { flat: true });
          const controls = ctrlRes.data ?? [];
          setControlTree(controls.map(controlToNode));
          setAllControls(controls);
          setCatalogGroups([]);
          // Set a minimal catalog object so the detail view can render
          catalogData = {
            id: catalogId,
            name: "Catalog",
            title: "Catalog",
            groups: [],
            controls: controls,
          } as any;
        } catch {
          setControlTree([]);
          setAllControls([]);
          setCatalogGroups([]);
          setDetailLoading(false);
          return;
        }
      }

      if (catalogData?.catalogType) {
        setSelectedCatalog((prev) =>
          prev && prev.id === catalogId
            ? { ...prev, catalogType: catalogData.catalogType }
            : prev,
        );
      }

      if (catalogData.groups && catalogData.groups.length > 0) {
        setCatalogGroups(catalogData.groups);
        setControlTree(
          sortNodesChildrenFirst(catalogData.groups.map(groupToNode)),
        );
        setAllControls(extractAllControls(catalogData.groups));
      } else if (catalogData.controls && catalogData.controls.length > 0) {
        // Catalog with controls at the root (no groups, e.g. CIS). getCatalog
        // already returns these controls in full — statement, guidance, parts
        // and params — so use them directly. No need for the extra
        // /groups and /controls?flat=true calls, and the detail panel gets the
        // complete content.
        setCatalogGroups([]);
        setControlTree(catalogData.controls.map(controlToNode));
        // Flatten incl. enhancements so nested controls (e.g. CISC-1.1) resolve
        // to a control in the detail panel instead of falling back to a group.
        setAllControls(flattenControls(catalogData.controls));
      } else {
        const groupsRes = await listGroups(catalogId);
        const groups = groupsRes.data ?? [];
        if (groups.length > 0) {
          setCatalogGroups(groups);
          setControlTree(sortNodesChildrenFirst(groups.map(groupToNode)));
          setAllControls(extractAllControls(groups));
        } else {
          const ctrlRes = await listControls(catalogId, { flat: true });
          const controls = ctrlRes.data ?? [];
          setControlTree(controls.map(controlToNode));
          setAllControls(controls);
        }
      }
    } catch {
      setControlTree([]);
      setAllControls([]);
      setCatalogGroups([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /* ── Handlers ── */
  const handleSelectCatalog = (row: CatalogRow) => {
    setSelectedCatalog(row);
    // Selection is kept in state; the URL stays on the list path (no id).
    fetchedDetailIdRef.current = row.id;
    fetchCatalogDetail(row.id);
  };

  const handleDeleteCatalog = (id: string) => {
    setDeleteTargetId(id);
    setDeleteReason("");
    setDeleteModalOpen(true);
  };

  const confirmDeleteCatalog = async (reason?: string) => {
    if (!deleteTargetId) return;
    setDeleteModalOpen(false);
    setDeleting(deleteTargetId);
    try {
      await deleteCatalog(deleteTargetId, reason);
      fetchCatalogs();
      if (selectedCatalog?.id === deleteTargetId) {
        setSelectedCatalog(null);
        setControlTree([]);
        setAllControls([]);
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to delete catalog",
        );
    } finally {
      setDeleting(null);
      setDeleteTargetId(null);
      setDeleteReason("");
    }
  };

  /* ── Catalog type change — calls API immediately on confirm ── */
  const handleCatalogTypeChange = async (
    id: string,
    newType: CatalogType,
  ) => {
    const title = editingNameValue.trim() || selectedCatalog?.name || "";
    const nextCountryName = editingCountryName.trim();
    const nextCountryCode = editingCountryCode.trim();
    const countryChanged =
      selectedCatalog?.id === id &&
      (nextCountryName !== (selectedCatalog?.countryName ?? "") ||
        nextCountryCode !== (selectedCatalog?.countryCode ?? ""));
    const countryPatch = countryChanged
      ? {
          countryName: nextCountryName || null,
          countryCode: nextCountryCode || null,
        }
      : {};
    setSavingName(true);
    try {
      await updateCatalog(id, {
        title,
        catalogType: newType,
        ...countryPatch,
      });
      setCatalogs((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, name: title, catalogType: newType, ...countryPatch }
            : c,
        ),
      );
      if (selectedCatalog?.id === id) {
        setSelectedCatalog((prev) =>
          prev
            ? { ...prev, name: title, catalogType: newType, ...countryPatch }
            : prev,
        );
      }
      useToastStore.getState().showSuccess("Success", "Catalog updated");
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update catalog",
        );
    } finally {
      setSavingName(false);
      setPendingCatalogTypeChange(null);
      setEditingNameId(null);
    }
  };

  /* ── Rename handler ── */
  const handleRenameCatalog = async (id: string) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    const currentType = selectedCatalog?.catalogType;
    const nameChanged = trimmed !== selectedCatalog?.name;
    const typeChanged =
      selectedCatalog?.id === id && editingCatalogType !== currentType;
    const nextCountryName = editingCountryName.trim();
    const nextCountryCode = editingCountryCode.trim();
    const countryChanged =
      selectedCatalog?.id === id &&
      (nextCountryName !== (selectedCatalog?.countryName ?? "") ||
        nextCountryCode !== (selectedCatalog?.countryCode ?? ""));
    setSavingName(true);
    try {
      if (nameChanged || typeChanged || countryChanged) {
        await updateCatalog(id, {
          title: trimmed,
          catalogType: editingCatalogType,
          // Send only when changed; empty string clears the value
          ...(countryChanged
            ? {
                countryName: nextCountryName || null,
                countryCode: nextCountryCode || null,
              }
            : {}),
        });
      }
      const countryPatch = countryChanged
        ? {
            countryName: nextCountryName || null,
            countryCode: nextCountryCode || null,
          }
        : {};
      setCatalogs((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                name: trimmed,
                ...(typeChanged ? { catalogType: editingCatalogType } : {}),
                ...countryPatch,
              }
            : c,
        ),
      );
      if (selectedCatalog?.id === id)
        setSelectedCatalog((prev) =>
          prev
            ? {
                ...prev,
                name: trimmed,
                ...(typeChanged ? { catalogType: editingCatalogType } : {}),
                ...countryPatch,
              }
            : prev,
        );
      if (nameChanged || typeChanged || countryChanged)
        useToastStore.getState().showSuccess("Success", "Catalog updated");
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update catalog",
        );
    } finally {
      setSavingName(false);
      setEditingNameId(null);
    }
  };

  /* ── Export handler ── */
  const handleExportCatalog = async (
    documentId: string,
    format: "json" | "xml" | "yaml",
  ) => {
    setExportingId(documentId);
    setExportModalDocId(null);
    setExportFormat(format);
    try {
      const catalogName =
        catalogs.find((c) => c.id === documentId)?.name || "catalog";
      const res = await exportCatalogDoc(documentId, format);
      let job = res.data;

      // Poll if pending
      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(job.jobId);
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }

      if (job.status === "completed") {
        const downloadUrl = job.downloadUrl || getExportDownloadUrl(job.jobId);
        const downloadRes = await downloadFile(downloadUrl);
        if (!downloadRes) throw new Error("No response from download");

        // Extract documentTitle from response headers
        let documentTitle = catalogName;
        const xDocumentTitle = downloadRes.headers?.["x-document-title"];
        if (xDocumentTitle) {
          documentTitle = xDocumentTitle;
        } else {
          // Try to extract from Content-Disposition header
          const contentDisposition =
            downloadRes.headers?.["content-disposition"] || "";
          const filenameMatch = contentDisposition.match(
            /filename\*?=(?:"([^"]*)"|([^;,\s]*))/,
          );
          if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
            const filename = (filenameMatch[1] || filenameMatch[2]).split(
              ".",
            )[0];
            documentTitle = filename;
          }
        }

        // Get MIME type from API response Content-Type header first.
        // Axios types header values as AxiosHeaderValue, so coerce to the
        // string that Blob's `type` option expects.
        let mimeType = String(downloadRes.headers?.["content-type"] || "");
        if (!mimeType) {
          // Fallback: use selected format
          const mimeTypes: Record<string, string> = {
            json: "application/json",
            xml: "application/xml",
            yaml: "application/yaml",
          };
          mimeType = mimeTypes[format] || "application/octet-stream";
        }
        const blob = new Blob([downloadRes.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentTitle}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        useToastStore
          .getState()
          .showSuccess("Success", "Export ready - download started");
      } else if (job.status === "failed") {
        useToastStore.getState().showError("Error", "Export failed");
      } else {
        useToastStore
          .getState()
          .showError("Error", "Export timed out - please try again");
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Export failed",
        );
    } finally {
      setExportingId(null);
    }
  };

  /* ── Lifecycle transition logic ── */
  const lifecycleActions: Record<
    string,
    {
      label: string;
      target: string;
      color: string;
      icon: React.ReactNode;
      requireComments?: boolean;
      warning?: string;
    }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color:
          "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <ShieldCheck className="size-4" />,
      },
      {
        label: "Reject",
        target: "rejected",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-4" />,
        requireComments: true,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/20",
        icon: <Globe className="size-4" />,
        warning: "This action will freeze the content permanently.",
      },
    ],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color:
          "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    published: [
      {
        label: "Supersede",
        target: "superseded",
        color: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/20",
        icon: <Archive className="size-4" />,
      },
      {
        label: "Withdraw",
        target: "withdrawn",
        color: "bg-rose-700 hover:bg-rose-800 shadow-rose-500/20",
        icon: <Ban className="size-4" />,
      },
    ],
    superseded: [],
    withdrawn: [],
  };

  const openTransitionModal = (
    target: string,
    label: string,
    requireComments = false,
  ) => {
    setTransitionTarget(target);
    setTransitionLabel(label);
    setTransitionComments("");
    setTransitionRequireComments(requireComments);
    setShowTransition(true);
  };

  const handleTransition = async () => {
    if (!selectedCatalog) return;
    if (transitionRequireComments && !transitionComments.trim()) return;
    setTransitioning(true);
    try {
      await transitionDocument(selectedCatalog.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComments.trim() || undefined,
      });
      // Refresh the open catalog to pick up its new lifecycleStatus, and
      // re-run the list query so the row behind the detail view matches.
      const res = await getCatalog(selectedCatalog.id);
      if (res?.data) setSelectedCatalog(toRow(res.data));
      fetchCatalogs();
      setShowTransition(false);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
        );
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Transition failed",
        );
    } finally {
      setTransitioning(false);
    }
  };

  // Get lifecycle actions for current state (permission-based filtering for approve/reject is done at render time)
  const hasUpdatePermission = useRBACPermission("catalog", "update");
  const hasApprovePermission = useRBACPermission("catalog", "approve");
  const hasPublishPermission = useRBACPermission("catalog", "publish");
  const currentActions = selectedCatalog
    ? (lifecycleActions[selectedCatalog.lifecycleStatus] ?? []).filter((a) => {
      // Check if catalog is empty: cannot submit empty catalog for review
      if (a.target === "in_review" && controlTree.length === 0) return false;
      // Show "Submit for Review" only if user has update permission
      if (a.label === "Submit for Review") return hasUpdatePermission;
      // Show "Approve" and "Reject" only if user has approve permission
      if (["Approve", "Reject"].includes(a.label))
        return hasApprovePermission;
      // Show "Publish" only if user has publish permission
      if (a.label === "Publish") return hasPublishPermission;
      // Show "Supersede" and "Withdraw" only if user has update permission
      if (["Supersede", "Withdraw"].includes(a.label))
        return hasUpdatePermission;
      return true;
    })
    : [];
  const isEditable = selectedCatalog
    ? selectedCatalog.lifecycleStatus === "draft" ||
    selectedCatalog.lifecycleStatus === "rejected"
    : false;

  // Catalog name + type can only be renamed while the catalog is a draft.
  const canEditCatalogMeta = selectedCatalog?.lifecycleStatus === "draft";

  const applyControlSelect = (node: ControlNode) => {
    if (!selectedCatalog) return;
    setEditMode(false);
    setEditGroupMode(false);
    const found = allControls.find(
      (c) => c.controlId.toUpperCase() === node.id.toUpperCase(),
    );
    if (found) {
      setSelectedGroupNode(null);
      setSelectedControl(found);
    } else {
      setSelectedControl(null);
      setSelectedGroupNode(node);
    }
  };

  const handleControlSelect = (node: ControlNode) => {
    if (!selectedCatalog) return;
    if (editMode || editGroupMode) {
      setPendingSelectNode(node);
      setShowUnsavedWarning(true);
      return;
    }
    applyControlSelect(node);
  };

  /* ── Inline control edit helpers ── */
  const partResponseToPayload = (
    p: PartPayload & { id?: string },
  ): PartPayload & { id?: string } => ({
    ...(p.id ? { id: p.id } : {}),
    partId: p.partId,
    name: p.name,
    ns: p.ns ?? undefined,
    clazz: p.clazz ?? undefined,
    title: p.title ?? null,
    prose: p.prose,
    sortOrder: p.sortOrder,
    parts: (p.parts ?? []).map(partResponseToPayload),
  });

  const startEditControl = () => {
    if (!selectedControl) return;
    setEditTitle(selectedControl.title);
    setEditClazz(selectedControl.clazz ?? "");
    setEditSortOrder(selectedControl.sortOrder ?? 1);
    setEditParts(selectedControl.parts?.map(partResponseToPayload) ?? []);
    setEditParams(
      selectedControl.params?.map((p) => ({
        ...(p.id ? { id: p.id } : {}),
        paramId: p.paramId,
        label: p.label,
        usage: p.usage ?? "",
        clazz: p.clazz ?? null,
        constraints: p.constraints ?? [],
        guidelines: p.guidelines ?? [],
        values: p.values ?? [],
        selectConfig: p.selectConfig ?? null,
        sortOrder: p.sortOrder,
      })) ?? [],
    );
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const saveControlEdit = async () => {
    if (!selectedCatalog || !selectedControl) return;
    setSavingEdit(true);
    try {
      // Find the group and parent control so the control stays in its place
      const groupId = findGroupForControl(
        catalogGroups,
        selectedControl.controlId,
      );
      const parentControlId = findParentControl(
        catalogGroups,
        selectedControl.controlId,
      );
      await createControl(selectedCatalog.id, {
        controlId: selectedControl.controlId,
        title: editTitle,
        clazz: editClazz || null,
        groupId: groupId || null,
        parentControlId: parentControlId || null,
        sortOrder: editSortOrder,
        parts: editParts,
        params: editParams,
      });
      // Refresh the control detail
      const res = await getControl(
        selectedCatalog.id,
        selectedControl.controlId.toLowerCase(),
      );
      const updatedControl = res.data;
      setEditMode(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Control updated successfully");
      // Refresh tree in case title changed - then restore the selected control
      // (fetchCatalogDetail clears selectedControl, so we re-set it after)
      const catalogId = selectedCatalog.id;
      try {
        const catRes = await getCatalog(catalogId);
        const catalog = catRes.data;
        if (catalog.groups && catalog.groups.length > 0) {
          setCatalogGroups(catalog.groups);
          setControlTree(
            sortNodesChildrenFirst(catalog.groups.map(groupToNode)),
          );
          setAllControls(extractAllControls(catalog.groups));
        }
      } catch {
        /* tree refresh is best-effort */
      }
      setSelectedControl(updatedControl);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update control",
        );
    } finally {
      setSavingEdit(false);
    }
  };

  const addEditPart = () => {
    setEditParts((p) => [
      ...p,
      {
        partId: "",
        name: "statement",
        prose: "",
        sortOrder: p.length + 1,
        parts: [],
      },
    ]);
  };

  type EditPart = PartPayload & { id?: string };
  type EditParam = ParamPayload & { id?: string };

  const updateEditPart = (idx: number, field: string, value: string) => {
    setEditParts((parts: EditPart[]) => {
      const updated = parts.map((p: EditPart, i: number) =>
        i === idx ? { ...p, [field]: value } : p,
      );
      if (field === "prose") {
        const paramIds = extractParamIdsFromProse(updated);
        setEditParams((prev: EditParam[]) =>
          syncParamsToProseRefs(prev, paramIds),
        );
      }
      return updated;
    });
  };

  const removeEditPart = (idx: number) => {
    setEditParts((parts: EditPart[]) => {
      const updated = parts.filter((_: EditPart, i: number) => i !== idx);
      const paramIds = extractParamIdsFromProse(updated);
      setEditParams((prev: EditParam[]) =>
        syncParamsToProseRefs(prev, paramIds),
      );
      return updated;
    });
  };

  const updateEditParam = (idx: number, field: string, value: unknown) => {
    setEditParams((params: EditParam[]) =>
      params.map((p: EditParam, i: number) =>
        i === idx ? { ...p, [field]: value } : p,
      ),
    );
  };

  /* ── Inline group edit helpers ── */
  const startEditGroup = () => {
    if (!selectedGroupNode) return;
    setEditGroupId(rawGroupId(selectedGroupNode.id).toLowerCase());
    setEditGroupTitle(selectedGroupNode.title);
    setEditGroupClazz("");
    setEditGroupSortOrder(1);
    setEditGroupMode(true);
  };

  const cancelGroupEdit = () => {
    setEditGroupMode(false);
  };

  const saveGroupEdit = async () => {
    if (!selectedCatalog) return;
    setSavingGroup(true);
    try {
      await createGroup(selectedCatalog.id, {
        groupId: editGroupId,
        title: editGroupTitle,
        clazz: editGroupClazz || undefined,
        sortOrder: editGroupSortOrder,
      });
      setEditGroupMode(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Group updated successfully");
      fetchCatalogDetail(selectedCatalog.id);
      // Update node title in-place
      if (selectedGroupNode) {
        setSelectedGroupNode({ ...selectedGroupNode, title: editGroupTitle });
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update group",
        );
    } finally {
      setSavingGroup(false);
    }
  };

  /* ── Delete group handler ── */
  const handleDeleteGroup = async () => {
    if (!selectedCatalog || !selectedGroupNode) return;
    const groupData = catalogGroups.find(
      (g: GroupResponse) =>
        g.groupId.toUpperCase() ===
        rawGroupId(selectedGroupNode.id).toUpperCase(),
    );
    if (!groupData) {
      useToastStore
        .getState()
        .showError(
          "Error",
          "Could not find group. Please refresh and try again.",
        );
      return;
    }
    try {
      await deleteGroup(selectedCatalog.id, groupData.id);
      setSelectedGroupNode(null);
      setEditGroupMode(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Group deleted successfully");
      fetchCatalogDetail(selectedCatalog.id);
      fetchCatalogs();
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to delete group",
        );
    }
  };

  /* ── Delete control handler ── */
  const handleDeleteControl = async () => {
    if (!selectedCatalog || !selectedControl) return;
    try {
      await deleteControlApi(selectedCatalog.id, selectedControl.controlId);
      setSelectedControl(null);
      setEditMode(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Control deleted successfully");
      fetchCatalogDetail(selectedCatalog.id);
      fetchCatalogs();
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to delete control",
        );
    }
  };

  /* ── Add new group handler ── */
  const openAddGroup = () => {
    setNewGroupId("");
    setNewGroupTitle("");
    setNewGroupClazz("family");
    setNewGroupSortOrder(controlTree.length + 1);
    setShowAddGroup(true);
  };

  const saveNewGroup = async () => {
    if (!selectedCatalog || !newGroupId.trim() || !newGroupTitle.trim()) return;
    setSavingNewGroup(true);
    try {
      await createGroup(selectedCatalog.id, {
        groupId: newGroupId.trim(),
        title: newGroupTitle.trim(),
        clazz: newGroupClazz || "family",
        sortOrder: newGroupSortOrder,
      });
      setShowAddGroup(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Group added successfully");
      fetchCatalogDetail(selectedCatalog.id);
      // Refresh catalog list to update group count
      fetchCatalogs();
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to add group",
        );
    } finally {
      setSavingNewGroup(false);
    }
  };

  /* ── Add new control handler ── */
  const openAddControl = () => {
    setNewControlId("");
    setNewControlTitle("");
    setNewControlClazz("SP800-53");
    setNewControlSortOrder((selectedGroupNode?.children?.length ?? 0) + 1);
    setNewControlParts([
      {
        partId: "",
        name: "statement",
        ns: "http://csrc.nist.gov/ns/oscal",
        clazz: "statement",
        title: null,
        prose: "",
        sortOrder: 1,
        parts: [],
      },
    ]);
    setNewControlParams([]);
    setAddControlGroupId("");
    if (!selectedGroupNode) {
      setNewGroupId("");
      setNewGroupTitle("");
      setNewGroupClazz("");
      setNewGroupSortOrder(catalogGroups.length + 1);
    }
    setShowAddControl(true);
  };

  const addNewControlPart = () => {
    setNewControlParts((p: PartPayload[]) => [
      ...p,
      {
        partId: "",
        name: "guidance",
        ns: "http://csrc.nist.gov/ns/oscal",
        clazz: "guidance",
        title: null,
        prose: "",
        sortOrder: p.length + 1,
        parts: [],
      },
    ]);
  };

  const updateNewControlPart = (idx: number, field: string, value: string) => {
    setNewControlParts((parts: PartPayload[]) => {
      const updated = parts.map((p: PartPayload, i: number) =>
        i === idx ? { ...p, [field]: value } : p,
      );
      if (field === "prose") {
        const paramIds = extractParamIdsFromProse(updated);
        setNewControlParams((prev: UIParamPayload[]) =>
          pruneParamsToProseRefs(prev, paramIds),
        );
      }
      return updated;
    });
  };

  const removeNewControlPart = (idx: number) => {
    setNewControlParts((parts: PartPayload[]) => {
      const updated = parts.filter((_: PartPayload, i: number) => i !== idx);
      const paramIds = extractParamIdsFromProse(updated);
      setNewControlParams((prev: UIParamPayload[]) =>
        // Removing a part shifts every later part's index down by one, so
        // params tracked against those parts must shift with them or they'd
        // render under the wrong (or no) part afterward.
        pruneParamsToProseRefs(prev, paramIds).map((p) =>
          p._partIdx !== undefined && p._partIdx > idx
            ? { ...p, _partIdx: p._partIdx - 1 }
            : p,
        ),
      );
      return updated;
    });
  };

  const updateNewControlParam = (
    idx: number,
    field: string,
    value: unknown,
  ) => {
    setNewControlParams((params: UIParamPayload[]) =>
      params.map((p: UIParamPayload, i: number) =>
        i === idx ? { ...p, [field]: value } : p,
      ),
    );
  };

  /** Renders a single param editor card (Param ID, Selection Choices, Constraints, Guidelines). */
  const renderNewControlParamCard = (param: UIParamPayload, pi: number) => {
    const proseRefs = new Set(extractParamIdsFromProse(newControlParts));
    const selectCfg = param.selectConfig as
      | ParameterSelection
      | null
      | undefined;
    const constraints = (param.constraints ?? []) as ParameterConstraint[];
    const isUnused =
      proseRefs.size > 0 &&
      param.paramId.trim() !== "" &&
      !proseRefs.has(param.paramId.trim());
    return (
      <div
        key={pi}
        className={`rounded border p-2.5 space-y-2 bg-muted/20 ${isUnused ? "border-amber-400 dark:border-amber-500" : "border-border"}`}
      >
        {isUnused && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 pb-1">
            <AlertTriangle className="size-3 shrink-0" />
            <span>
              The Param ID does not match the parameter reference in the
              Statement. Please enter the exact parameter ID used in the
              Statement (e.g., <code className="font-mono">ac-1_prm_1</code>)
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold d">Param {pi + 1}</span>
          <button
            onClick={() =>
              setNewControlParams((p) => p.filter((_, i) => i !== pi))
            }
            className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
          >
            <X className="size-3" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <FormField label="Param ID">
            <FormInput
              placeholder="e.g. ac-1_prm_1"
              value={param.paramId}
              onChange={(v) => updateNewControlParam(pi, "paramId", v)}
            />
          </FormField>
          <FormField label="Label">
            <FormInput
              placeholder="e.g. organization-defined roles"
              value={param.label}
              onChange={(v) => updateNewControlParam(pi, "label", v)}
            />
          </FormField>
          <FormField label="Class">
            <Dropdown
              variant="form"
              value={param.clazz ?? ""}
              onChange={(v) => updateNewControlParam(pi, "clazz", v || null)}
              options={[
                { value: "", label: "- None -" },
                { value: "str", label: "str (text)" },
                { value: "int", label: "int (integer)" },
              ]}
            />
          </FormField>
        </div>
        <FormField label="Usage">
          <FormInput
            placeholder="e.g. description of how this parameter is used"
            value={param.usage ?? ""}
            onChange={(v) => updateNewControlParam(pi, "usage", v)}
          />
        </FormField>
        <FormField label="Default Values (comma-separated)">
          <input
            type="text"
            placeholder="e.g. 90, 180"
            defaultValue={(param.values ?? []).join(", ")}
            onBlur={(e) =>
              updateNewControlParam(
                pi,
                "values",
                e.target.value
                  ? e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [],
              )
            }
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white"
          />
        </FormField>

        {/* Selection Choices */}
        <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium d">Selection Choices</span>
            {!selectCfg ? (
              <button
                onClick={() =>
                  updateNewControlParam(pi, "selectConfig", {
                    "how-many": "one",
                    choice: [],
                  })
                }
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
              >
                <Plus className="size-3" /> Add Selection
              </button>
            ) : (
              <button
                onClick={() => updateNewControlParam(pi, "selectConfig", null)}
                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          {selectCfg && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <span className="text-[10px] font-medium d">Choices</span>
                  <div
                    className="flex flex-wrap items-center gap-1.5 min-h-[40px] px-3 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] focus-within:border-indigo-500 dark:focus-within:border-indigo-500 transition-all cursor-text"
                    onClick={(e) => {
                      (
                        e.currentTarget.querySelector(
                          "input",
                        ) as HTMLInputElement
                      )?.focus();
                    }}
                  >
                    {selectCfg.choice.map((c, ci) => (
                      <span
                        key={ci}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
                      >
                        {c}
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            updateNewControlParam(pi, "selectConfig", {
                              ...selectCfg,
                              choice: selectCfg.choice.filter(
                                (_, i) => i !== ci,
                              ),
                            });
                          }}
                          className="text-indigo-400 hover:text-red-500 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder={
                        selectCfg.choice.length === 0
                          ? "Type and press Enter"
                          : ""
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const val = (e.target as HTMLInputElement).value.trim();
                          if (val && !selectCfg.choice.includes(val)) {
                            updateNewControlParam(pi, "selectConfig", {
                              ...selectCfg,
                              choice: [...selectCfg.choice, val],
                            });
                            (e.target as HTMLInputElement).value = "";
                          }
                        } else if (
                          e.key === "Backspace" &&
                          !(e.target as HTMLInputElement).value &&
                          selectCfg.choice.length > 0
                        ) {
                          updateNewControlParam(pi, "selectConfig", {
                            ...selectCfg,
                            choice: selectCfg.choice.slice(0, -1),
                          });
                        }
                      }}
                      className="flex-1 min-w-[100px] text-sm bg-transparent outline-none placeholder:text-[#94a3b8] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white"
                    />
                  </div>
                </div>
                <FormField label="How Many">
                  <Dropdown
                    variant="form"
                    value={selectCfg["how-many"]}
                    onChange={(v) =>
                      updateNewControlParam(pi, "selectConfig", {
                        ...selectCfg,
                        "how-many": v,
                      })
                    }
                    options={[
                      { value: "one", label: "Pick exactly one" },
                      { value: "one-or-more", label: "Pick one or more" },
                    ]}
                  />
                </FormField>
              </div>
            </div>
          )}
        </div>

        {/* Constraints */}
        <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium d">Constraints</span>
            <button
              onClick={() =>
                updateNewControlParam(pi, "constraints", [
                  ...constraints,
                  {
                    description: "",
                    tests: [{ expression: "", remarks: "" }],
                  },
                ])
              }
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
            >
              <Plus className="size-3" /> Add Constraint
            </button>
          </div>
          {constraints.map((con, ci) => (
            <div
              key={ci}
              className="rounded border border-border p-2 space-y-2 bg-background/50"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold d">
                  Constraint {ci + 1}
                </span>
                <button
                  onClick={() =>
                    updateNewControlParam(
                      pi,
                      "constraints",
                      constraints.filter((_, i) => i !== ci),
                    )
                  }
                  className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                >
                  <X className="size-3" />
                </button>
              </div>
              <FormField label="Description">
                <FormInput
                  placeholder="e.g. Password minimum length must be between 8 and 128"
                  value={con.description ?? ""}
                  onChange={(v) => {
                    const u = [...constraints];
                    u[ci] = { ...u[ci], description: v };
                    updateNewControlParam(pi, "constraints", u);
                  }}
                />
              </FormField>
              {(con.tests ?? []).map((t, ti) => (
                <div key={ti} className="flex items-end gap-2">
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <FormField label="Expression">
                      <FormInput
                        placeholder="e.g. value >= 8"
                        value={t.expression ?? ""}
                        onChange={(v) => {
                          const ut = [...(con.tests ?? [])];
                          ut[ti] = { ...ut[ti], expression: v };
                          const u = [...constraints];
                          u[ci] = { ...u[ci], tests: ut };
                          updateNewControlParam(pi, "constraints", u);
                        }}
                      />
                    </FormField>
                    <FormField label="Remarks">
                      <FormInput
                        placeholder="e.g. Minimum 8 characters"
                        value={t.remarks ?? ""}
                        onChange={(v) => {
                          const ut = [...(con.tests ?? [])];
                          ut[ti] = { ...ut[ti], remarks: v };
                          const u = [...constraints];
                          u[ci] = { ...u[ci], tests: ut };
                          updateNewControlParam(pi, "constraints", u);
                        }}
                      />
                    </FormField>
                  </div>
                  {(con.tests ?? []).length > 1 && (
                    <button
                      onClick={() => {
                        const ut = (con.tests ?? []).filter(
                          (_, i) => i !== ti,
                        );
                        const u = [...constraints];
                        u[ci] = { ...u[ci], tests: ut };
                        updateNewControlParam(pi, "constraints", u);
                      }}
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mb-2"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => {
                  const u = [...constraints];
                  u[ci] = {
                    ...u[ci],
                    tests: [...(u[ci].tests ?? []), { expression: "", remarks: "" }],
                  };
                  updateNewControlParam(pi, "constraints", u);
                }}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 ml-auto"
              >
                <Plus className="size-3" /> Add Test
              </button>
            </div>
          ))}
        </div>

        {/* Guidelines */}
        <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium d">Guidelines</span>
            <button
              onClick={() =>
                updateNewControlParam(pi, "guidelines", [
                  ...(param.guidelines ?? []),
                  { prose: "" },
                ])
              }
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
            >
              <Plus className="size-3" /> Add Guideline
            </button>
          </div>
          {(param.guidelines ?? []).map((g: { prose: string }, gi: number) => (
            <div key={gi} className="flex items-start gap-2">
              <FormTextarea
                placeholder="Enter guideline prose"
                value={g.prose ?? ""}
                onChange={(v) => {
                  const u = [...(param.guidelines ?? [])];
                  u[gi] = { prose: v };
                  updateNewControlParam(pi, "guidelines", u);
                }}
                rows={2}
              />
              <button
                onClick={() =>
                  updateNewControlParam(
                    pi,
                    "guidelines",
                    (param.guidelines ?? []).filter(
                      (_: { prose: string }, i: number) => i !== gi,
                    ),
                  )
                }
                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mt-1"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const saveNewControl = async () => {
    if (!selectedCatalog || !newControlId.trim() || !newControlTitle.trim())
      return;
    let groupUUID: string;
    if (selectedGroupNode) {
      // Existing group selected
      const groupData = catalogGroups.find(
        (g: GroupResponse) =>
          g.groupId.toUpperCase() ===
          rawGroupId(selectedGroupNode.id).toUpperCase(),
      );
      if (!groupData) {
        useToastStore
          .getState()
          .showError(
            "Error",
            "Could not find group UUID. Please refresh and try again.",
          );
        return;
      }
      groupUUID = groupData.id;
    } else {
      // Create new group first
      if (!newGroupId.trim() || !newGroupTitle.trim()) {
        useToastStore
          .getState()
          .showError("Error", "Group ID and Group Title are required.");
        return;
      }
      try {
        const gRes = await createGroup(selectedCatalog.id, {
          groupId: newGroupId.trim(),
          title: newGroupTitle.trim(),
          clazz: newGroupClazz.trim() || undefined,
          sortOrder: newGroupSortOrder,
        });
        groupUUID = gRes.data.id;
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Failed to create group";
        useToastStore.getState().showError("Error", msg);
        return;
      }
    }
    setSavingNewControl(true);
    try {
      await createControl(selectedCatalog.id, {
        controlId: newControlId.trim(),
        title: newControlTitle.trim(),
        clazz: newControlClazz || "SP800-53",
        groupId: groupUUID,
        parentControlId: null,
        sortOrder: newControlSortOrder,
        parts: newControlParts.filter((p: PartPayload) => p.partId && p.prose),
        params: newControlParams
          .filter((p: UIParamPayload) => p.paramId && p.label)
          .map(({ _partIdx, ...rest }: UIParamPayload) => rest),
      });
      setShowAddControl(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Control added successfully");
      fetchCatalogDetail(selectedCatalog.id);
      // Refresh catalog list to update control count
      fetchCatalogs();
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to add control",
        );
    } finally {
      setSavingNewControl(false);
    }
  };

  /* ── Wizard helpers ── */
  const resetWizard = () => {
    setWizardStep("catalog");
    setWizCatalogTitle("");
    setWizCatalogId(null);
    setWizCatalogType("");
    setWizCountryName("");
    setWizCountryCode("");
    setWizCatalogOrgId("");
    setWizGroups([{ groupId: "", title: "", clazz: "family", sortOrder: 1 }]);
    setWizControls([]);
    setExpandedControl(null);
    setWizardError(null);
    setWizardSaving(false);
  };

  const addGroup = () => {
    setWizGroups((g) => [
      ...g,
      { groupId: "", title: "", clazz: "family", sortOrder: g.length + 1 },
    ]);
  };

  const removeGroup = (i: number) => {
    setWizGroups((g) =>
      g
        .filter((_, idx) => idx !== i)
        .map((g, idx) => ({ ...g, sortOrder: idx + 1 })),
    );
    // Also remove controls linked to this group
    setWizControls((c) =>
      c
        .filter((ctrl) => ctrl.groupIndex !== i)
        .map((ctrl) => ({
          ...ctrl,
          groupIndex:
            ctrl.groupIndex > i ? ctrl.groupIndex - 1 : ctrl.groupIndex,
        })),
    );
  };

  const [groupIdErrors, setGroupIdErrors] = useState<Record<number, string>>(
    {},
  );

  const updateGroup = (i: number, field: keyof GroupDraft, value: string) => {
    if (field === "groupId") {
      setWizGroups((g) =>
        g.map((grp, idx) => (idx === i ? { ...grp, [field]: value } : grp)),
      );
      if (value && /\d/.test(value)) {
        setGroupIdErrors((prev) => ({
          ...prev,
          [i]: "Group ID must contain only letters (e.g. ac, PM, IS). Numbers are not allowed.",
        }));
      } else if (value && !/^[a-zA-Z][a-zA-Z-]*$/.test(value)) {
        setGroupIdErrors((prev) => ({
          ...prev,
          [i]: "Group ID must start with a letter and contain only letters or hyphens (e.g. ac, PM, is).",
        }));
      } else if (value && value.length > 10) {
        setGroupIdErrors((prev) => ({
          ...prev,
          [i]: "Group ID should be a short identifier (max 10 characters).",
        }));
      } else {
        setGroupIdErrors((prev) => {
          const next = { ...prev };
          delete next[i];
          return next;
        });
      }
    } else {
      setWizGroups((g) =>
        g.map((grp, idx) => (idx === i ? { ...grp, [field]: value } : grp)),
      );
    }
  };

  const addControl = (groupIndex: number) => {
    const existingForGroup = wizControls.filter(
      (c) => c.groupIndex === groupIndex,
    );
    setWizControls((c) => [
      ...c,
      {
        controlId: "",
        title: "",
        clazz: "SP800-53",
        groupIndex,
        parentControlId: null,
        sortOrder: existingForGroup.length + 1,
        parts: [
          {
            partId: "",
            name: "statement",
            ns: "http://csrc.nist.gov/ns/oscal",
            clazz: "statement",
            title: null,
            prose: "",
            sortOrder: 1,
            parts: [],
          },
        ],
        params: [],
      },
    ]);
    setExpandedControl(wizControls.length); // expand the newly added one
  };

  const removeControl = (i: number) => {
    setWizControls((c) => c.filter((_, idx) => idx !== i));
    setExpandedControl(null);
  };

  const [controlIdErrors, setControlIdErrors] = useState<
    Record<number, string>
  >({});

  const updateControl = (i: number, field: string, value: unknown) => {
    if (field === "controlId" && typeof value === "string") {
      setWizControls((c) =>
        c.map((ctrl, idx) => (idx === i ? { ...ctrl, [field]: value } : ctrl)),
      );
      if (value && !/^[a-zA-Z][a-zA-Z]+-\d+$/.test(value)) {
        setControlIdErrors((prev) => ({
          ...prev,
          [i]: "Control ID must follow the format: {group-id}-{number} (e.g. ac-1, PM-3, is-11).",
        }));
      } else {
        setControlIdErrors((prev) => {
          const next = { ...prev };
          delete next[i];
          return next;
        });
      }
    } else {
      setWizControls((c) =>
        c.map((ctrl, idx) => (idx === i ? { ...ctrl, [field]: value } : ctrl)),
      );
    }
  };

  const addPartToControl = (controlIdx: number) => {
    setWizControls((c) =>
      c.map((ctrl, idx) => {
        if (idx !== controlIdx) return ctrl;
        return {
          ...ctrl,
          parts: [
            ...ctrl.parts,
            {
              partId: "",
              name: "guidance",
              ns: "http://csrc.nist.gov/ns/oscal",
              clazz: "guidance",
              title: null,
              prose: "",
              sortOrder: ctrl.parts.length + 1,
              parts: [],
            },
          ],
        };
      }),
    );
  };

  const updatePart = (
    controlIdx: number,
    partIdx: number,
    field: string,
    value: string,
  ) => {
    setWizControls((c) =>
      c.map((ctrl, cidx) => {
        if (cidx !== controlIdx) return ctrl;
        const updatedParts = ctrl.parts.map((p, pidx) =>
          pidx === partIdx ? { ...p, [field]: value } : p,
        );
        if (field === "prose") {
          const paramIds = extractParamIdsFromProse(updatedParts);
          return {
            ...ctrl,
            parts: updatedParts,
            params: syncParamsToProseRefs(ctrl.params, paramIds),
          };
        }
        return { ...ctrl, parts: updatedParts };
      }),
    );
  };

  const removePart = (controlIdx: number, partIdx: number) => {
    setWizControls((c) =>
      c.map((ctrl, cidx) => {
        if (cidx !== controlIdx) return ctrl;
        const updatedParts = ctrl.parts.filter((_, pidx) => pidx !== partIdx);
        const paramIds = extractParamIdsFromProse(updatedParts);
        return {
          ...ctrl,
          parts: updatedParts,
          params: syncParamsToProseRefs(ctrl.params, paramIds),
        };
      }),
    );
  };

  const updateParam = (
    controlIdx: number,
    paramIdx: number,
    field: string,
    value: unknown,
  ) => {
    setWizControls((c) =>
      c.map((ctrl, cidx) => {
        if (cidx !== controlIdx) return ctrl;
        return {
          ...ctrl,
          params: ctrl.params.map((p, pidx) =>
            pidx === paramIdx ? { ...p, [field]: value } : p,
          ),
        };
      }),
    );
  };

  /* ── Wizard step handlers ── */
  const handleWizardCreateCatalog = async () => {
    setWizardSaving(true);
    setWizardError(null);
    try {
      const res = await createCatalog({
        title: wizCatalogTitle,
        owningOrgUnitId: wizCatalogOrgId || undefined,
        catalogType: wizCatalogType || undefined,
        countryName: wizCountryName.trim() || undefined,
        countryCode: wizCountryCode.trim() || undefined,
      });
      setWizCatalogId(res.data.id);
      setWizardStep("groups");
    } catch (err: unknown) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to create catalog",
      );
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardSaveGroups = async () => {
    if (!wizCatalogId) return;
    setWizardSaving(true);
    setWizardError(null);
    try {
      const savedGroups = [...wizGroups];
      for (let i = 0; i < savedGroups.length; i++) {
        const g = savedGroups[i];
        if (g.savedId) continue; // already saved
        const res = await createGroup(wizCatalogId, {
          groupId: g.groupId,
          title: g.title,
          clazz: g.clazz || "family",
          sortOrder: g.sortOrder,
        });
        savedGroups[i] = { ...savedGroups[i], savedId: res.data.id };
      }
      setWizGroups(savedGroups);
      // Auto-add one default control for the first group when entering step 3
      setWizControls((prev) => {
        if (prev.length === 0 && savedGroups.length > 0) {
          return [
            {
              controlId: "",
              title: "",
              clazz: "SP800-53",
              groupIndex: 0,
              parentControlId: null,
              sortOrder: 1,
              parts: [
                {
                  partId: "",
                  name: "statement",
                  ns: "http://csrc.nist.gov/ns/oscal",
                  clazz: "statement",
                  title: null,
                  prose: "",
                  sortOrder: 1,
                  parts: [],
                },
              ],
              params: [],
            },
          ];
        }
        return prev;
      });
      setExpandedControl(0);
      setWizardStep("controls");
    } catch (err: unknown) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to create groups",
      );
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardSaveControls = async () => {
    if (!wizCatalogId) return;
    setWizardSaving(true);
    setWizardError(null);
    try {
      for (const ctrl of wizControls) {
        const groupUuid = wizGroups[ctrl.groupIndex]?.savedId;
        if (!groupUuid) continue;
        await createControl(wizCatalogId, {
          controlId: ctrl.controlId,
          title: ctrl.title,
          clazz: ctrl.clazz || "SP800-53",
          groupId: groupUuid,
          parentControlId: ctrl.parentControlId || null,
          sortOrder: ctrl.sortOrder,
          parts: ctrl.parts.filter((p) => p.partId && p.prose),
          params: ctrl.params.filter((p) => p.paramId && p.label),
        });
      }
      setWizardStep("review");
    } catch (err: unknown) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to create controls",
      );
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardDone = () => {
    setShowWizard(false);
    resetWizard();
    fetchCatalogs();
  };

  // Close/cancel the wizard. The catalog is created in step 1, so if the user
  // backs out of a later step without finishing, refresh the list anyway so the
  // already-created catalog shows in the table without a manual page refresh.
  const closeWizard = () => {
    const catalogWasCreated = !!wizCatalogId;
    setShowWizard(false);
    resetWizard();
    if (catalogWasCreated) fetchCatalogs();
  };

  const handleWizardSaveAsDraft = async () => {
    if (wizCatalogId) {
      handleWizardDone();
      return;
    }
    setWizardSaving(true);
    setWizardError(null);
    try {
      await createCatalog({
        title: wizCatalogTitle,
        owningOrgUnitId: wizCatalogOrgId || null,
        catalogType: wizCatalogType || undefined,
        countryName: wizCountryName.trim() || undefined,
        countryCode: wizCountryCode.trim() || undefined,
      });
      setShowWizard(false);
      resetWizard();
      fetchCatalogs();
    } catch (err: unknown) {
      setWizardError(
        err instanceof Error ? err.message : "Failed to save catalog",
      );
    } finally {
      setWizardSaving(false);
    }
  };

  /* ── Import handlers ── */

  /**
   * Process a /import/validate result for catalog imports.
   * Enforces the document type and the catalog-type gate (valid === false)
   * before allowing the import to proceed. Returns true on success.
   */
  const processCatalogValidation = (data: ValidateResponse): boolean => {
    if (data.documentType?.toLowerCase() !== "catalog") {
      const msg = `Expected a catalog document but got "${data.documentType}"`;
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
      return false;
    }
    if (data.valid === false) {
      const errFinding = data.findings?.find(
        (f) => f.severity?.toLowerCase() === "error",
      );
      const msg =
        errFinding?.detail ||
        errFinding?.title ||
        data.message ||
        "Catalog validation failed";
      setImportError(msg);
      useToastStore.getState().showError("Validation Failed", msg);
      return false;
    }
    setValidateResult(data);
    return true;
  };

  /** Builds the {countryName, countryCode} payload for a given country
   * code (defaults to the currently selected import country), or
   * undefined if none is selected. */
  const getImportCountryPayload = (code: string = importCountryCode) => {
    if (!code) return undefined;
    const countryName = getCountryName(code);
    return countryName ? { countryName, countryCode: code } : undefined;
  };

  /**
   * Validate a file against the catalog-type gate and surface the result.
   * Shared by file selection, the type selector, the country selector,
   * and the URL flow. `countryCode` overrides the selected country state
   * directly (avoids a stale-closure read right after setImportCountryCode).
   */
  const runValidation = async (
    file: File,
    type: CatalogType | "" = importCatalogType,
    countryCode: string = importCountryCode,
  ) => {
    setValidating(true);
    setImportError(null);
    setValidateResult(null);
    try {
      const res = await validateImport(
        file,
        type || undefined,
        getImportCountryPayload(countryCode),
      );
      processCatalogValidation(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Validation failed";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setValidating(false);
    }
  };

  /** Step 1: Validate file - stays on same step, shows inline results */
  const startValidation = async () => {
    if (!importFile) return;
    await runValidation(importFile);
  };

  /** Convert a GitHub blob URL to its raw content URL */
  const toRawUrl = (url: string): string => {
    // https://github.com/{owner}/{repo}/blob/{branch}/{path}
    // → https://raw.githubusercontent.com/{owner}/{repo}/{branch}/{path}
    const ghMatch = url.match(
      /^https?:\/\/github\.com\/([^/]+\/[^/]+)\/blob\/(.+)$/,
    );
    if (ghMatch)
      return `https://raw.githubusercontent.com/${ghMatch[1]}/${ghMatch[2]}`;
    return url;
  };

  /** Check whether URL points to an allowed file type */
  const isAllowedUrl = (url: string): boolean =>
    /\.(json|yaml|yml|xml)(\?.*)?$/i.test(url.split("#")[0]);

  /** Fetch file from URL, then validate it */
  const fetchUrl = async () => {
    if (!importUrl.trim()) return;
    if (!isAllowedUrl(importUrl.trim())) {
      setImportError("URL must point to a .json, .yaml, or .xml file");
      useToastStore
        .getState()
        .showError("Error", "URL must point to a .json, .yaml, or .xml file");
      return;
    }
    setValidating(true);
    setImportError(null);
    setValidateResult(null);
    setImportFile(null);

    try {
      // 1. Fetch the file content from the URL
      const rawUrl = toRawUrl(importUrl.trim());
      const response = await fetch(rawUrl);
      if (!response.ok)
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      const blob = await response.blob();
      const filename = importUrl.split("/").pop() || "document";
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      setImportFile(file);

      // 2. Validate the fetched file
      const res = await validateImport(
        file,
        importCatalogType || undefined,
        getImportCountryPayload(),
      );
      processCatalogValidation(res.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to fetch";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setValidating(false);
    }
  };

  /** Step 2: Actual import after validation passes */
  const runImport = async () => {
    if (!importFile) return;
    setImportStep("importing");
    setImportError(null);
    try {
      const res = await importCatalog(
        importFile,
        importMode,
        importOrgId || undefined,
      );
      setImportResult(res.data);
      // Close import modal and open session modal
      setShowImport(false);
      openSessionModal(res.data.id);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Duplicate Error",
          err instanceof Error ? err.message : "This file already exists",
        );
      setShowImport(false);
      resetImport();
    }
  };

  /** Open session modal, fetch session, and start polling */
  const applySessionCatalogTypeDefault = (
    session: ImportSessionResponse,
  ) => {
    if (session.documentType.toLowerCase() !== "catalog") return;
    // Technology marker finding is IMP-0060 (severity suffix varies, e.g. -I/-W)
    if (session.findings.some((f) => f.code?.startsWith("IMP-0060"))) {
      setImportCatalogType("TECHNOLOGY");
    } else {
      setImportCatalogType("REGULATORY");
    }
  };

  const openSessionModal = async (sessionId: string) => {
    setShowSession(true);
    setSessionData(null);
    setSessionError(null);
    setSessionLoading(true);
    setCommitting(false);
    setAborting(false);
    try {
      const res = await getImportSession(sessionId);
      setSessionData(res.data);
      applySessionCatalogTypeDefault(res.data);
    } catch (err: unknown) {
      setSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSessionLoading(false);
    }
  };

  /** Poll session every 10s until status is validated/failed/committed */
  useEffect(() => {
    if (!showSession || !sessionData) return;
    const status = sessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    const timer = setInterval(async () => {
      try {
        const res = await getImportSession(sessionData.id);
        setSessionData(res.data);
        if (res.data.jobStatus.toLowerCase() === "validated") {
          applySessionCatalogTypeDefault(res.data);
        }
      } catch {
        // silently ignore polling errors
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showSession, sessionData]);

  /** Commit the import session */
  const handleCommitSession = async () => {
    if (!sessionData) return;
    setCommitting(true);
    setSessionError(null);
    try {
      await commitImportSession(
        sessionData.id,
        importOrgId || undefined,
        importCatalogType || undefined,
        getImportCountryPayload(),
      );
      fetchCatalogs();
      useToastStore
        .getState()
        .showSuccess("Success", "Catalog Imported Successfully");
      // Close the session modal after successful commit
      setShowSession(false);
      setSessionData(null);
      resetImport();
    } catch (err: unknown) {
      const code = err instanceof Error ? (err as any).errorCode : undefined;
      if (code === "IMP-0035-E") {
        setSessionError(
          "Cannot import as TECHNOLOGY — the file is missing the technology marker prop. Change type to REGULATORY or re-upload with the prop added.",
        );
      } else if (code === "IMP-0036-E") {
        setSessionError(
          "Cannot import as REGULATORY — the file contains the technology marker prop. Change type to TECHNOLOGY.",
        );
      } else if (code === "IMP-0031-E") {
        setSessionError(
          "Import blocked by validation error(s). Review findings and fix before saving.",
        );
      } else {
        setSessionError(err instanceof Error ? err.message : "Save failed");
      }
    } finally {
      setCommitting(false);
    }
  };

  /** Abort the import session */
  const handleAbortSession = async () => {
    if (!sessionData) return;
    setAborting(true);
    setSessionError(null);
    try {
      await abortImportSession(sessionData.id);
      // Close popup and refresh catalog list
      setShowSession(false);
      setSessionData(null);
      resetImport();
      fetchCatalogs();
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setAborting(false);
    }
  };

  const resetImport = () => {
    setImportStep("select");
    setImportFile(null);
    setImportUrl("");
    setImportMode("full");
    setValidateResult(null);
    setValidating(false);
    setImportError(null);
    setImportErrorTitle(null);
    setImportResult(null);
    setImportOrgId("");
    setImportCatalogType("");
    setImportCountryCode("");
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, status + type filters and page window. */
  const paginatedCatalogs = catalogs;
  /** Serial number continues across pages (page 2 with 10/page starts at 11). */
  const catRowSerial = (index: number) => catPage * catPageSize + index + 1;

  /* ── Wizard step indicator ── */
  const wizardSteps: { key: WizardStep; label: string; num: number }[] = [
    { key: "catalog", label: "Catalog", num: 1 },
    { key: "groups", label: "Groups", num: 2 },
    { key: "controls", label: "Controls", num: 3 },
    { key: "review", label: "Review", num: 4 },
  ];

  const currentStepIndex = wizardSteps.findIndex((s) => s.key === wizardStep);

  /* ════════════════════════════════════════════════════════
     Loading Skeleton (when navigating via URL)
  ════════════════════════════════════════════════════════ */
  if (catalogIdFromUrl && !selectedCatalog) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <SkeletonCard lines={6} />
        <SkeletonCard lines={4} />
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Catalog Detail View
  ════════════════════════════════════════════════════════ */
  if (selectedCatalog) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditingNameId(null);
              setPendingCatalogTypeChange(null);
              setSelectedCatalog(null);
              setControlTree([]);
              setAllControls([]);
              setSelectedControl(null);
            }}
            className="flex items-center gap-1.5 text-sm d hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Catalog
          </button>
          <ChevronRight className="size-3.5 d" />
          <span className="text-sm font-medium">{selectedCatalog.name}</span>
          {(() => {
            const lifecycle = selectedCatalog.lifecycleStatus;
            const steps = [
              "draft",
              "in_review",
              "approved",
              "published",
            ] as const;
            const stepLabels: Record<string, string> = {
              draft: "Draft",
              in_review: "In Review",
              approved: "Approved",
              published: "Published",
            };
            const terminalStates: Record<
              string,
              { label: string; color: string }
            > = {
              rejected: { label: "Rejected", color: "bg-red-500" },
              superseded: { label: "Superseded", color: "bg-orange-500" },
              withdrawn: { label: "Withdrawn", color: "bg-rose-700" },
            };
            const terminal = terminalStates[lifecycle];
            const activeIdx = steps.indexOf(
              lifecycle as (typeof steps)[number],
            );
            return (
              <div className="ml-3 flex items-center gap-1 shrink-0 whitespace-nowrap">
                {terminal ? (
                  <>
                    {steps.map((s, i) => {
                      const isRejectedFromReview = lifecycle === "rejected";
                      const filledUpTo = isRejectedFromReview
                        ? 1
                        : steps.length;
                      const filled = i < filledUpTo;
                      return (
                        <React.Fragment key={s}>
                          <div
                            className={`flex items-center gap-1 ${filled ? "d" : "d/40"}`}
                          >
                            <div
                              className={`size-2 rounded-full ${filled ? "bg-muted-foreground/50" : "bg-muted-foreground/20"}`}
                            />
                            <span className="text-[10px] font-medium">
                              {stepLabels[s]}
                            </span>
                          </div>
                          {i < steps.length - 1 && (
                            <div
                              className={`w-4 h-px ${filled && i < filledUpTo - 1 ? "bg-muted-foreground/40" : "bg-muted-foreground/15"}`}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div className="w-4 h-px bg-muted-foreground/15" />
                    <div className="flex items-center gap-1">
                      <div
                        className={`size-2 rounded-full ${terminal.color}`}
                      />
                      <span
                        className={`text-[10px] font-bold ${lifecycle === "rejected" ? "text-red-600 dark:text-red-400" : lifecycle === "superseded" ? "text-orange-600 dark:text-orange-400" : "text-rose-700 dark:text-rose-400"}`}
                      >
                        {terminal.label}
                      </span>
                    </div>
                  </>
                ) : (
                  steps.map((s, i) => {
                    const isCurrent = s === lifecycle;
                    const isPast = activeIdx >= 0 && i < activeIdx;
                    return (
                      <React.Fragment key={s}>
                        <div
                          className={`flex items-center gap-1 ${isCurrent && s === "published" ? "text-emerald-600 dark:text-emerald-400" : isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/60 dark:text-white"}`}
                        >
                          <div
                            className={`size-2 rounded-full ${isCurrent ? "bg-emerald-500 ring-2 ring-emerald-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/30 dark:bg-white/20"}`}
                          />
                          <span
                            className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
                          >
                            {stepLabels[s]}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <div
                            className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/20 dark:bg-white/15"}`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            );
          })()}
        </div>

        {/* Catalog overview */}
        <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
          <div className="p-4 space-y-3.5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {editingNameId === selectedCatalog.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleRenameCatalog(selectedCatalog.id);
                        if (e.key === "Escape") setEditingNameId(null);
                      }}
                      style={{
                        width: `${Math.max(editingNameValue.length + 2, 20)}ch`,
                      }}
                      className="text-lg font-semibold px-2 py-1 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500"
                      disabled={savingName}
                    />
                    <Tip label="Save">
                      <button
                        onClick={() => handleRenameCatalog(selectedCatalog.id)}
                        onMouseDown={(e) => e.preventDefault()}
                        disabled={savingName || !editingNameValue.trim()}
                        className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                      >
                        {savingName ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Check className="size-4" />
                        )}
                      </button>
                    </Tip>
                    <Tip label="Cancel">
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setEditingNameId(null)}
                        disabled={savingName}
                        className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                      >
                        <X className="size-4" />
                      </button>
                    </Tip>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">
                      {selectedCatalog.name}
                    </h2>
                    {canEditCatalogMeta && (
                      <Tip label="Edit Catalog" side="bottom">
                    <button
                      onClick={() => {
                        setEditingNameId(selectedCatalog.id);
                        setEditingNameValue(selectedCatalog.name);
                        setEditingCatalogType(
                          selectedCatalog.catalogType ?? "REGULATORY",
                        );
                        setEditingCountryName(selectedCatalog.countryName ?? "");
                        setEditingCountryCode(selectedCatalog.countryCode ?? "");
                      }}
                      className="p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                    >
                      <Pencil className="size-3.5 d hover:text-indigo-600" />
                    </button>
                    </Tip>
                  )}
                </div>
              )}
              {editingNameId === selectedCatalog.id ? (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium d">Catalog Type:</span>
                    <div className="w-44">
                      <FormSelect
                        options={CATALOG_TYPE_OPTIONS}
                        value={editingCatalogType}
                        onChange={(v) => {
                          const next = v as CatalogType;
                          const current =
                            selectedCatalog.catalogType ?? "REGULATORY";
                          // Only confirm when the type actually changes
                          if (next === current) return;
                          setEditingCatalogType(next);
                          setPendingCatalogTypeChange(next);
                        }}
                        placeholder="Select catalog type"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium d">Country:</span>
                    <input
                      value={editingCountryName}
                      onChange={(e) => setEditingCountryName(e.target.value)}
                      placeholder="e.g. Singapore"
                      className="w-36 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-2.5 py-1.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-violet-500 transition-all"
                    />
                    <input
                      value={editingCountryCode}
                      onChange={(e) =>
                        setEditingCountryCode(
                          e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z]/g, "")
                            .slice(0, 2),
                        )
                      }
                      maxLength={2}
                      placeholder="SG"
                      className="w-16 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-2.5 py-1.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  {selectedCatalog.catalogType && (
                    <span>
                      <span className="d">Catalog Type: </span>
                      <span className="font-medium">
                        {catalogTypeLabel(selectedCatalog.catalogType)}
                      </span>
                    </span>
                  )}
                  {(selectedCatalog.countryName ||
                    selectedCatalog.countryCode) && (
                    <span>
                      <span className="d">Country: </span>
                      <span className="font-medium">
                        {selectedCatalog.countryName}
                        {selectedCatalog.countryCode && (
                          <span className="d ml-1">
                            ({selectedCatalog.countryCode})
                          </span>
                        )}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Lifecycle transition buttons */}
              {currentActions.map((action) => {
                const button = (
                  <button
                    key={action.target}
                    onClick={() =>
                      openTransitionModal(
                        action.target,
                        action.label,
                        action.requireComments,
                      )
                    }
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium whitespace-nowrap transition-colors shadow-md ${action.color}`}
                  >
                    {action.icon}
                    {action.label}
                  </button>
                );

                  // Determine required permission based on action label
                  if (["Approve", "Reject"].includes(action.label)) {
                    return (
                      <RBACAction
                        key={action.target}
                        module="catalog"
                        action="approve"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (action.label === "Publish") {
                    return (
                      <RBACAction
                        key={action.target}
                        module="catalog"
                        action="publish"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (action.label === "Submit for Review") {
                    return (
                      <RBACAction
                        key={action.target}
                        module="catalog"
                        action="update"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  if (["Supersede", "Withdraw"].includes(action.label)) {
                    return (
                      <RBACAction
                        key={action.target}
                        module="catalog"
                        action="update"
                      >
                        {button}
                      </RBACAction>
                    );
                  }
                  return button;
                })}
                {/* Export button - commented out until backend assembly is ready */}
                {/* <button
                onClick={handleExportCatalog}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
              >
                {exporting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                {exporting ? "Exporting…" : "Export"}
              </button> */}
              </div>
            </div>

            {/* Stat cards — shared detail-page order (see the Content Registry
                and Profile detail headers): version, created, updated, status,
                then the module-specific counts. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <CatStatCard
                icon={<Info className="size-4" />}
                label="Version"
                value={formatVersion(selectedCatalog.version)}
                color="sky"
              />
              <CatStatCard
                icon={<Clock className="size-4" />}
                label="Created On"
                value={selectedCatalog.created}
                color="emerald"
              />
              <CatStatCard
                icon={<Clock className="size-4" />}
                label="Updated On"
                value={selectedCatalog.lastUpdated}
                color="amber"
              />
              <CatStatCard
                icon={<ShieldCheck className="size-4" />}
                label="Status"
                value={(selectedCatalog.lifecycleStatus ?? "").replace(/_/g, " ")}
                color={catStatusColor(selectedCatalog.lifecycleStatus)}
                valueClassName="capitalize"
              />
              <CatStatCard
                icon={<Layers className="size-4" />}
                label="Groups"
                value={
                  selectedCatalog.group !== "-"
                    ? String(selectedCatalog.group)
                    : "—"
                }
                color="violet"
              />
              <CatStatCard
                icon={<FileText className="size-4" />}
                label="Controls"
                value={String(selectedCatalog.controls)}
                color="indigo"
              />
            </div>

            {/* Rejection notice */}
            {selectedCatalog.lifecycleStatus === "rejected" && (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                <XCircle className="size-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">This catalog was rejected</p>
                  <p className="text-xs mt-0.5">
                    You can edit the catalog and submit it for review again.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Control tree + detail panel */}
        {detailLoading ? (
          <SkeletonControlPanel />
        ) : (
          <ResizablePanels
            left={
              <div className="rounded-lg border border-border bg-card flex flex-col min-h-[420px] max-h-[calc(100vh-12rem)]">
                <div className="p-4 pb-0 shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">Control Groups</h3>
                    <div className="flex items-center gap-2">
                      {(() => {
                        // Both views use the same ControlTreeView expand model.
                        // One button toggles between the two states, matching
                        // the Organization module.
                        const isAllExpanded = treeExpandAll === true;
                        return (
                          <button
                            onClick={() => {
                              setTreeExpandAll(!isAllExpanded);
                              setTreeExpandTrigger((t) => t + 1);
                            }}
                            className="text-xs d hover:text-foreground transition-colors"
                          >
                            {isAllExpanded ? "Collapse all" : "Expand all"}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 d" />
                    <input
                      type="text"
                      placeholder="Search controls"
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      className="w-full text-sm pl-8 pr-3 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-500 dark:focus:border-indigo-500 transition-colors placeholder:d"
                    />
                  </div>
                  {/* Filter */}
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <Switch
                      checked={treeFilter === "params"}
                      onCheckedChange={(checked) => {
                        setTreeFilter(checked ? "params" : "all");
                        setSelectedGroupNode(null);
                        // Reset to the collapsed default when switching views.
                        setTreeExpandAll(undefined);
                      }}
                    />
                    <span className="text-[11px] font-medium d select-none">
                      Show Controls with Parameters
                    </span>
                  </label>
                </div>
                <div className="flex-1 overflow-y-auto p-4 pt-0">
                  {(() => {
                    // Both views render the same hierarchical tree component so
                    // the params view keeps the main-group → sub-group → control
                    // hierarchy; it just prunes to param-bearing branches.
                    const filtered = searchTree(
                      filterTree(controlTree, treeFilter === "params" ? "params" : "all"),
                      treeSearch,
                    );
                    if (treeFilter === "params" && filtered.length === 0)
                      return (
                        <p className="text-xs d py-8 text-center">
                          No controls with parameters.
                        </p>
                      );
                    return filtered.length > 0 ? (
                      <ControlTreeView
                        nodes={filtered}
                        selectable
                        selectedId={
                          selectedControl?.controlId?.toUpperCase() ??
                          selectedGroupNode?.id
                        }
                        onSelect={handleControlSelect}
                        expandAll={treeExpandAll}
                        expandTrigger={treeExpandTrigger}
                        onAllNodesExpandedChange={handleCatalogAllNodesExpandedChange}
                      />
                    ) : (
                      <p className="text-xs d py-8 text-center">
                        No controls found in this catalog.
                      </p>
                    );
                  })()}
                </div>
              </div>
            }
            right={
              <div className="rounded-lg border border-border bg-card p-5 overflow-y-auto h-full min-h-[420px] max-h-[calc(100vh-12rem)]">
                {selectedControl && editMode ? (
                  /* ── Control Edit Mode ── */
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm px-3 py-1 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold whitespace-nowrap shrink-0">
                          {selectedControl.controlId.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveControlEdit}
                          disabled={savingEdit || !editTitle.trim()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                        >
                          <Save className="size-3.5" />
                          {savingEdit ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>

                    {/* Title & Class */}
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Title" required>
                        <FormInput
                          value={editTitle}
                          onChange={(v) => setEditTitle(v)}
                          placeholder="Control title"
                        />
                      </FormField>
                      <FormField label="Class">
                        <FormInput
                          value={editClazz}
                          onChange={(v) => setEditClazz(v)}
                          placeholder="e.g. SP800-53"
                        />
                      </FormField>
                    </div>

                    {/* Parts */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold d">Parts</span>
                        <button
                          onClick={addEditPart}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                        >
                          <Plus className="size-3" /> Add Part
                        </button>
                      </div>
                      {editParts.map((part, pi) => (
                        <div
                          key={pi}
                          className="rounded border border-border p-2.5 space-y-2 bg-muted/20"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold d">
                              Part {pi + 1}
                            </span>
                            <button
                              onClick={() => removeEditPart(pi)}
                              className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                            >
                              <X className="size-3" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <FormField label="Part ID">
                              <FormInput
                                placeholder="e.g. ac-1_smt"
                                value={part.partId}
                                onChange={(v) =>
                                  updateEditPart(pi, "partId", v)
                                }
                              />
                            </FormField>
                            <FormField label="Name">
                              <FormInput
                                placeholder="statement / guidance"
                                value={part.name}
                                onChange={(v) => updateEditPart(pi, "name", v)}
                              />
                            </FormField>
                          </div>
                          <FormField label="Prose">
                            <FormTextarea
                              placeholder="Enter the control statement text"
                              value={part.prose}
                              onChange={(v) => updateEditPart(pi, "prose", v)}
                              rows={4}
                            />
                          </FormField>
                          {/* Sub-parts */}
                          <div className="space-y-2 pt-1">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-semibold d">
                                Sub-parts
                              </span>
                              <button
                                onClick={() =>
                                  setEditParts((prev: EditPart[]) =>
                                    prev.map((p: EditPart, i: number) =>
                                      i === pi
                                        ? {
                                          ...p,
                                          parts: [
                                            ...(p.parts ?? []),
                                            {
                                              partId: "",
                                              name: "item",
                                              prose: "",
                                              sortOrder:
                                                (p.parts ?? []).length + 1,
                                              parts: [],
                                            },
                                          ],
                                        }
                                        : p,
                                    ),
                                  )
                                }
                                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                              >
                                <Plus className="size-3" /> Add Sub-part
                              </button>
                            </div>
                            {(part.parts ?? []).map((sub, si) => (
                              <div
                                key={si}
                                className="rounded border border-border/60 p-2 space-y-2 bg-muted/10 ml-2"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] d">
                                    Sub-part {si + 1}
                                  </span>
                                  <button
                                    onClick={() =>
                                      setEditParts((prev: EditPart[]) =>
                                        prev.map((p: EditPart, i: number) =>
                                          i === pi
                                            ? {
                                              ...p,
                                              parts: (p.parts ?? []).filter(
                                                (_: PartPayload, j: number) =>
                                                  j !== si,
                                              ),
                                            }
                                            : p,
                                        ),
                                      )
                                    }
                                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                  >
                                    <X className="size-3" />
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <FormField label="Part ID">
                                    <FormInput
                                      placeholder="e.g. ac-1_smt.1"
                                      value={sub.partId}
                                      onChange={(v) =>
                                        setEditParts((prev: EditPart[]) =>
                                          prev.map((p: EditPart, i: number) =>
                                            i === pi
                                              ? {
                                                ...p,
                                                parts: (p.parts ?? []).map(
                                                  (
                                                    s: PartPayload,
                                                    j: number,
                                                  ) =>
                                                    j === si
                                                      ? { ...s, partId: v }
                                                      : s,
                                                ),
                                              }
                                              : p,
                                          ),
                                        )
                                      }
                                    />
                                  </FormField>
                                  <FormField label="Name">
                                    <FormInput
                                      placeholder="item"
                                      value={sub.name}
                                      onChange={(v) =>
                                        setEditParts((prev: EditPart[]) =>
                                          prev.map((p: EditPart, i: number) =>
                                            i === pi
                                              ? {
                                                ...p,
                                                parts: (p.parts ?? []).map(
                                                  (
                                                    s: PartPayload,
                                                    j: number,
                                                  ) =>
                                                    j === si
                                                      ? { ...s, name: v }
                                                      : s,
                                                ),
                                              }
                                              : p,
                                          ),
                                        )
                                      }
                                    />
                                  </FormField>
                                </div>
                                <FormField label="Prose">
                                  <FormTextarea
                                    placeholder="Enter sub-part text…"
                                    value={sub.prose}
                                    onChange={(v) =>
                                      setEditParts((prev: EditPart[]) =>
                                        prev.map((p: EditPart, i: number) =>
                                          i === pi
                                            ? {
                                              ...p,
                                              parts: (p.parts ?? []).map(
                                                (
                                                  s: PartPayload,
                                                  j: number,
                                                ) =>
                                                  j === si
                                                    ? { ...s, prose: v }
                                                    : s,
                                              ),
                                            }
                                            : p,
                                        ),
                                      )
                                    }
                                    rows={4}
                                  />
                                </FormField>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Params */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold d">Parameters</span>
                        {(() => {
                          const proseRefs = new Set(
                            extractParamIdsFromProse(editParts),
                          );
                          const definedIds = new Set(
                            editParams.map((p) => p.paramId).filter(Boolean),
                          );
                          const canAdd = Array.from(proseRefs).some(
                            (id) => !definedIds.has(id),
                          );
                          return (
                            <div className="relative group">
                              <button
                                disabled={!canAdd}
                                onClick={() =>
                                  setEditParams((p) => [
                                    ...p,
                                    {
                                      paramId: "",
                                      label: "",
                                      usage: "",
                                      clazz: null,
                                      constraints: [],
                                      guidelines: [],
                                      values: [],
                                      selectConfig: null,
                                      sortOrder: p.length + 1,
                                    },
                                  ])
                                }
                                className={`text-xs flex items-center gap-1 ${canAdd ? "text-indigo-600 dark:text-indigo-400 hover:underline" : "d cursor-not-allowed opacity-50"}`}
                              >
                                <Plus className="size-3" /> Add Parameter
                              </button>
                              {!canAdd && (
                                <div className="absolute right-0 top-5 z-10 hidden group-hover:block w-56 px-2.5 py-1.5 rounded-md bg-popover border border-border text-[11px] d shadow-md">
                                  Add a{" "}
                                  <code className="font-mono">
                                    {'{{ insert: param, id-ref="param-id" }}'}
                                  </code>{" "}
                                  placeholder in the statement prose first.
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {(() => {
                        const proseRefs = new Set(
                          extractParamIdsFromProse(editParts),
                        );
                        return editParams.length === 0 &&
                          proseRefs.size === 0 ? (
                          <p className="text-xs d italic">
                            Use{" "}
                            <code className="font-mono text-[10px]">
                              {'{{ insert: param, id-ref="param-id" }}'}
                            </code>{" "}
                            in the statement prose to define a parameter
                            reference, then add it here.
                          </p>
                        ) : null;
                      })()}
                      {editParams.map((param, pi) => {
                        const proseRefs = new Set(
                          extractParamIdsFromProse(editParts),
                        );
                        const selectCfg = param.selectConfig as
                          | ParameterSelection
                          | null
                          | undefined;
                        const constraints = (param.constraints ??
                          []) as ParameterConstraint[];
                        const paramClazz = param.clazz;
                        const isUnused =
                          proseRefs.size > 0 &&
                          param.paramId.trim() !== "" &&
                          !proseRefs.has(param.paramId.trim());
                        return (
                          <div
                            key={pi}
                            className={`rounded border p-2.5 space-y-2 bg-muted/20 ${isUnused ? "border-amber-400 dark:border-amber-500" : "border-border"}`}
                          >
                            {isUnused && (
                              <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 pb-1">
                                <AlertTriangle className="size-3 shrink-0" />
                                <span>
                                  The Param ID does not match the parameter
                                  reference in the Statement. Please enter the
                                  exact parameter ID used in the Statement (e.g.,{" "}
                                  <code className="font-mono">ac-1_prm_1</code>)
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold d">
                                  Param {pi + 1}
                                </span>
                                {paramClazz && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium">
                                    {paramClazz}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() =>
                                  setEditParams((p) =>
                                    p.filter((_, i) => i !== pi),
                                  )
                                }
                                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                              >
                                <X className="size-3" />
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <FormField label="Param ID">
                                <FormInput
                                  value={param.paramId}
                                  onChange={(v) =>
                                    updateEditParam(pi, "paramId", v)
                                  }
                                />
                              </FormField>
                              <FormField label="Label">
                                <FormInput
                                  placeholder="e.g. organization-defined roles"
                                  value={param.label}
                                  onChange={(v) =>
                                    updateEditParam(pi, "label", v)
                                  }
                                />
                              </FormField>
                              <FormField label="Class">
                                <Dropdown
                                  variant="form"
                                  value={param.clazz ?? ""}
                                  onChange={(v) =>
                                    updateEditParam(pi, "clazz", v || null)
                                  }
                                  options={[
                                    { value: "", label: "- None -" },
                                    { value: "str", label: "str (text)" },
                                    { value: "int", label: "int (integer)" },
                                    { value: "bool", label: "bool" },
                                  ]}
                                />
                              </FormField>
                            </div>

                            {/* Usage */}
                            <FormField label="Usage">
                              <FormInput
                                placeholder="e.g. description of how this parameter is used"
                                value={param.usage ?? ""}
                                onChange={(v) =>
                                  updateEditParam(pi, "usage", v)
                                }
                              />
                            </FormField>

                            {/* Default Value(s) */}
                            <div className="space-y-1.5">
                              <span className="text-xs font-medium d">
                                Default Value{" "}
                                {paramClazz ? `(${paramClazz})` : ""}
                              </span>

                              {selectCfg ? (
                                /* ── SelectConfig: show choices as clickable chips ── */
                                <div className="space-y-1">
                                  <span className="text-[10px] d">
                                    {selectCfg["how-many"] === "one"
                                      ? "Select one:"
                                      : "Select one or more:"}
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {selectCfg.choice.map((c) => {
                                      const selected = (
                                        param.values ?? []
                                      ).includes(c);
                                      return (
                                        <button
                                          key={c}
                                          type="button"
                                          onClick={() => {
                                            let newValues: string[];
                                            if (
                                              selectCfg["how-many"] === "one"
                                            ) {
                                              newValues = selected ? [] : [c];
                                            } else {
                                              newValues = selected
                                                ? (param.values ?? []).filter(
                                                  (v) => v !== c,
                                                )
                                                : [...(param.values ?? []), c];
                                            }
                                            updateEditParam(
                                              pi,
                                              "values",
                                              newValues,
                                            );
                                          }}
                                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected
                                              ? "bg-indigo-100 dark:bg-indigo-500/20 border-indigo-400 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 font-semibold"
                                              : "bg-muted/50 border-border d hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                                            }`}
                                        >
                                          {selected && (
                                            <Check className="size-3 inline mr-0.5 -mt-0.5" />
                                          )}
                                          {c}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                /* ── Free text / numeric input ── */
                                <input
                                  type="text"
                                  placeholder={
                                    paramClazz === "num" || paramClazz === "int"
                                      ? "Enter numeric value"
                                      : "Enter default value"
                                  }
                                  value={(param.values ?? []).join(", ")}
                                  onChange={(e) => {
                                    // Store raw value as single-element array to preserve spaces while typing
                                    updateEditParam(pi, "values", [
                                      e.target.value,
                                    ]);
                                  }}
                                  onBlur={(e) => {
                                    // On blur, split by comma and clean up
                                    const newValues = e.target.value
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean);
                                    updateEditParam(pi, "values", newValues);
                                  }}
                                  className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white"
                                />
                              )}

                              {/* Constraints (editable) */}
                              <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium d">
                                    Constraints
                                  </span>
                                  <button
                                    onClick={() =>
                                      updateEditParam(pi, "constraints", [
                                        ...constraints,
                                        {
                                          description: "",
                                          tests: [
                                            { expression: "", remarks: "" },
                                          ],
                                        },
                                      ])
                                    }
                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                  >
                                    <Plus className="size-3" /> Add Constraint
                                  </button>
                                </div>
                                {constraints.map((con, ci) => (
                                  <div
                                    key={ci}
                                    className="rounded border border-border p-2 space-y-2 bg-background/50"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] font-bold d">
                                        Constraint {ci + 1}
                                      </span>
                                      <button
                                        onClick={() =>
                                          updateEditParam(
                                            pi,
                                            "constraints",
                                            constraints.filter(
                                              (_, i) => i !== ci,
                                            ),
                                          )
                                        }
                                        className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                      >
                                        <X className="size-3" />
                                      </button>
                                    </div>
                                    <FormField label="Description">
                                      <FormInput
                                        placeholder="e.g. Password minimum length must be between 8 and 128"
                                        value={con.description ?? ""}
                                        onChange={(v) => {
                                          const u = [...constraints];
                                          u[ci] = { ...u[ci], description: v };
                                          updateEditParam(pi, "constraints", u);
                                        }}
                                      />
                                    </FormField>
                                    {(con.tests ?? []).map((t, ti) => (
                                      <div
                                        key={ti}
                                        className="flex items-end gap-2"
                                      >
                                        <div className="grid grid-cols-2 gap-2 flex-1">
                                          <FormField label="Expression">
                                            <FormInput
                                              placeholder="e.g. value >= 8"
                                              value={t.expression ?? ""}
                                              onChange={(v) => {
                                                const ut = [
                                                  ...(con.tests ?? []),
                                                ];
                                                ut[ti] = {
                                                  ...ut[ti],
                                                  expression: v,
                                                };
                                                const u = [...constraints];
                                                u[ci] = { ...u[ci], tests: ut };
                                                updateEditParam(
                                                  pi,
                                                  "constraints",
                                                  u,
                                                );
                                              }}
                                            />
                                          </FormField>
                                          <FormField label="Remarks">
                                            <FormInput
                                              placeholder="e.g. Minimum 8 characters"
                                              value={t.remarks ?? ""}
                                              onChange={(v) => {
                                                const ut = [
                                                  ...(con.tests ?? []),
                                                ];
                                                ut[ti] = {
                                                  ...ut[ti],
                                                  remarks: v,
                                                };
                                                const u = [...constraints];
                                                u[ci] = { ...u[ci], tests: ut };
                                                updateEditParam(
                                                  pi,
                                                  "constraints",
                                                  u,
                                                );
                                              }}
                                            />
                                          </FormField>
                                        </div>
                                        {(con.tests ?? []).length > 1 && (
                                          <button
                                            onClick={() => {
                                              const ut = (
                                                con.tests ?? []
                                              ).filter((_, i) => i !== ti);
                                              const u = [...constraints];
                                              u[ci] = { ...u[ci], tests: ut };
                                              updateEditParam(
                                                pi,
                                                "constraints",
                                                u,
                                              );
                                            }}
                                            className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mb-2"
                                          >
                                            <X className="size-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => {
                                        const u = [...constraints];
                                        u[ci] = {
                                          ...u[ci],
                                          tests: [
                                            ...(u[ci].tests ?? []),
                                            { expression: "", remarks: "" },
                                          ],
                                        };
                                        updateEditParam(pi, "constraints", u);
                                      }}
                                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 ml-auto"
                                    >
                                      <Plus className="size-3" /> Add Test
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Guidelines */}
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium d">
                                  Guidelines
                                </span>
                                <button
                                  onClick={() =>
                                    updateEditParam(pi, "guidelines", [
                                      ...(param.guidelines ?? []),
                                      { prose: "" },
                                    ])
                                  }
                                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                >
                                  <Plus className="size-3" /> Add Guideline
                                </button>
                              </div>
                              {(param.guidelines ?? []).map(
                                (g: { prose: string }, gi: number) => (
                                  <div
                                    key={gi}
                                    className="flex items-start gap-2"
                                  >
                                    <FormTextarea
                                      placeholder="Enter guideline prose"
                                      value={g.prose ?? ""}
                                      onChange={(v) => {
                                        const updated = [
                                          ...(param.guidelines ?? []),
                                        ];
                                        updated[gi] = { prose: v };
                                        updateEditParam(
                                          pi,
                                          "guidelines",
                                          updated,
                                        );
                                      }}
                                      rows={2}
                                    />
                                    <button
                                      onClick={() => {
                                        const updated = (
                                          param.guidelines ?? []
                                        ).filter(
                                          (_: { prose: string }, i: number) =>
                                            i !== gi,
                                        );
                                        updateEditParam(
                                          pi,
                                          "guidelines",
                                          updated,
                                        );
                                      }}
                                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mt-1"
                                    >
                                      <X className="size-3" />
                                    </button>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : selectedControl ? (
                  /* ── Control Read-Only View ── */
                  <div className="space-y-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="font-mono text-sm px-3 py-1 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold whitespace-nowrap shrink-0">
                          {selectedControl.controlId.toUpperCase()}
                        </span>
                        <div className="flex flex-col">
                          {(() => {
                            // Hide the header title when it just duplicates the
                            // Statement shown in full below (regulatory catalogs
                            // often set the control title to the statement text).
                            const titleText = (selectedControl.title ?? "")
                              .replace(/[.…\s]+$/, "")
                              .trim();
                            const stmt =
                              (selectedControl.parts ?? [])
                                .find((p) => p.name === "statement")
                                ?.prose?.trim() ?? "";
                            const isDupOfStatement =
                              titleText.length > 0 &&
                              stmt.length > 0 &&
                              stmt
                                .toLowerCase()
                                .startsWith(
                                  titleText
                                    .toLowerCase()
                                    .slice(0, Math.min(titleText.length, 40)),
                                );
                            return isDupOfStatement ? null : (
                              <span className="text-sm font-semibold">
                                {selectedControl.title}
                              </span>
                            );
                          })()}
                          {selectedControl.clazz && (
                            <span className="text-xs d">
                              {selectedControl.clazz}
                            </span>
                          )}
                        </div>
                      </div>
                      {isEditable && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={startEditControl}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm font-medium whitespace-nowrap hover:bg-accent transition-colors"
                          >
                            <Pencil className="size-3.5 shrink-0" />
                            Edit
                          </button>
                        </div>
                      )}
                    </div>

                    {selectedControl.parts?.length > 0 && (
                      <div className="space-y-4">
                        {selectedControl.parts.map((part) => (
                          <PartBlock
                            key={part.partId}
                            part={part}
                            depth={0}
                            params={selectedControl.params}
                          />
                        ))}
                      </div>
                    )}

                    {selectedControl.params?.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground capitalize">
                          Parameters
                        </h4>
                        <div className="space-y-2">
                          {selectedControl.params.map((p) => (
                            <div
                              key={p.paramId}
                              className="rounded-md border border-border p-3 bg-muted/20 space-y-2"
                            >
                              {/* Header: paramId + clazz badge */}
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[10px] font-bold d">
                                  {p.paramId}
                                </span>
                                {p.clazz && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium">
                                    {p.clazz}
                                  </span>
                                )}
                              </div>
                              {/* Label */}
                              <p className="text-sm font-medium">{p.label}</p>
                              {/* Usage */}
                              {p.usage && (
                                <p className="text-xs d">{p.usage}</p>
                              )}
                              {/* Default values - always shown as a single badge */}
                              {p.values && p.values.length > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[10px] font-bold d">
                                    Default:
                                  </span>
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono break-words">
                                    {p.values.join(", ")}
                                  </span>
                                </div>
                              )}
                              {/* SelectConfig - choices */}
                              {p.selectConfig && (
                                <div className="space-y-1">
                                  <span className="text-[10px] font-bold d">
                                    Choices (
                                    {p.selectConfig["how-many"] === "one"
                                      ? "pick one"
                                      : "pick one or more"}
                                    ):
                                  </span>
                                  <div className="flex flex-wrap gap-1">
                                    {(
                                      p.selectConfig as ParameterSelection
                                    ).choice.map((c, i) => (
                                      <span
                                        key={i}
                                        className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-slate-300 font-mono"
                                      >
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Constraints */}
                              {p.constraints &&
                                (p.constraints as ParameterConstraint[])
                                  .length > 0 && (
                                  <div className="space-y-1.5">
                                    <span className="text-sm font-bold d">
                                      Constraints
                                    </span>
                                    {(
                                      p.constraints as ParameterConstraint[]
                                    ).map((con, ci) => (
                                      <div
                                        key={ci}
                                        className="d bg-muted/50 rounded px-3 py-2 space-y-1.5"
                                      >
                                        {con.description && (
                                          <p className="text-sm leading-relaxed">
                                            {con.description}
                                          </p>
                                        )}
                                        {con.tests?.map((t, ti) => (
                                          <div key={ti} className="space-y-1">
                                            {t.expression && (
                                              <span className="block font-mono text-xs bg-muted/60 rounded px-2 py-1 break-words">
                                                Pattern: {t.expression}
                                              </span>
                                            )}
                                            {t.remarks && (
                                              <span className="block text-xs italic d/80 leading-relaxed">
                                                {t.remarks}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              {/* Guidelines */}
                              {p.guidelines && p.guidelines.length > 0 && (
                                <div className="space-y-0.5">
                                  <span className="text-[10px] font-bold d">
                                    Guidelines
                                  </span>
                                  {p.guidelines.map((g, i) => (
                                    <p key={i} className="text-xs d italic">
                                      {g.prose}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedControl.enhancements?.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground capitalize">
                          Control Enhancements
                        </h4>
                        <div className="space-y-2">
                          {selectedControl.enhancements.map((enh) => (
                            <button
                              key={enh.id}
                              onClick={() => {
                                setEditMode(false);
                                setSelectedControl(enh);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/20 hover:bg-accent transition-colors text-left"
                            >
                              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                                {enh.controlId.toUpperCase()}
                              </span>
                              <span className="text-xs">{enh.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : selectedGroupNode ? (
                  /* ── Group View / Edit ── */
                  <div className="space-y-5">
                    {editGroupMode ? (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm px-3 py-1 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold">
                              {rawGroupId(selectedGroupNode.id)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={cancelGroupEdit}
                              className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveGroupEdit}
                              disabled={savingGroup || !editGroupTitle.trim()}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                            >
                              <Save className="size-3.5" />
                              {savingGroup ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <FormField label="Group ID" required>
                            <FormInput
                              value={editGroupId}
                              onChange={(v) => setEditGroupId(v)}
                              placeholder="e.g. ac"
                              disabled
                            />
                          </FormField>
                          <FormField label="Title" required>
                            <FormInput
                              value={editGroupTitle}
                              onChange={(v) => setEditGroupTitle(v)}
                              placeholder="e.g. Access Control"
                            />
                          </FormField>
                          <FormField label="Class">
                            <FormInput
                              value={editGroupClazz}
                              onChange={(v) => setEditGroupClazz(v)}
                              placeholder="e.g. SP800-53"
                            />
                          </FormField>
                        </div>
                        <FormField label="Group Order">
                          <FormInput
                            value={String(editGroupSortOrder)}
                            onChange={(v) =>
                              setEditGroupSortOrder(Number(v) || 1)
                            }
                            placeholder="1"
                          />
                        </FormField>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-base font-semibold">
                            Group Details
                          </h3>
                          {isEditable && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openAddControl()}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                              >
                                <Plus className="size-3.5" />
                                Add Control
                              </button>
                              <button
                                onClick={startEditGroup}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
                              >
                                <Pencil className="size-3.5" />
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                        {(() => {
                          function findNodeById(
                            nodes: ControlNode[],
                            id: string,
                          ): ControlNode | undefined {
                            for (const n of nodes) {
                              if (n.id === id) return n;
                              if (n.children) {
                                const found = findNodeById(n.children, id);
                                if (found) return found;
                              }
                            }
                            return undefined;
                          }
                          const fullNode =
                            treeFilter === "params"
                              ? selectedGroupNode
                              : (findNodeById(
                                controlTree,
                                selectedGroupNode.id,
                              ) ?? selectedGroupNode);
                          function collectLeafControls(
                            nodes: ControlNode[],
                          ): ControlNode[] {
                            const leaves: ControlNode[] = [];
                            for (const n of nodes) {
                              if (n.children && n.children.length > 0) {
                                leaves.push(...collectLeafControls(n.children));
                              } else {
                                leaves.push(n);
                              }
                            }
                            return leaves;
                          }
                          const children = collectLeafControls(
                            fullNode.children ?? [],
                          );
                          return (
                            <>
                              {/* Group details table */}
                              <div className="rounded-lg border border-border overflow-hidden">
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-border">
                                      <td className="px-4 py-2.5 font-medium d bg-muted/50 w-40">
                                        Group ID
                                      </td>
                                      <td className="px-4 py-2.5">
                                        <span className="font-mono text-sm px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold">
                                          {rawGroupId(selectedGroupNode.id)?.toUpperCase()}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr className="border-b border-border">
                                      <td className="px-4 py-2.5 font-medium d bg-muted/50 w-40">
                                        Group Name
                                      </td>
                                      <td className="px-4 py-2.5 font-medium">
                                        {selectedGroupNode.title}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-4 py-2.5 font-medium d bg-muted/50 w-40">
                                        No of Controls
                                      </td>
                                      <td className="px-4 py-2.5">
                                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                                          {children.length}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              {/* Placeholder hint */}
                              <div className="flex flex-col items-center justify-center flex-1 py-12 text-center">
                                <FileText className="size-8 d mb-3" />
                                <p className="text-sm font-medium d">
                                  Select a control to view details
                                </p>
                                <p className="text-xs d mt-1">
                                  Click on a control to see its statement,
                                  parameters, and guidance.
                                </p>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    {isEditable && (
                      <div className="flex justify-end mb-4">
                        <RBACAction module="catalog" action="update">
                          <button
                            onClick={openAddControl}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                          >
                            <Plus className="size-3.5" />
                            Add Group
                          </button>
                        </RBACAction>
                      </div>
                    )}
                    <div className="flex flex-col items-center justify-center flex-1 py-16 text-center">
                      <FileText className="size-8 d mb-3" />
                      <p className="text-sm font-medium d">
                        Select a control to view details
                      </p>
                      <p className="text-xs d mt-1">
                        Click on a leaf control in the tree to see its
                        statement, parameters, and guidance.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        )}

        {/* ── Lifecycle Transition Confirmation Modal ── */}
        <Modal
          open={showTransition}
          onClose={() => setShowTransition(false)}
          title={`${transitionLabel}`}
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowTransition(false)}
              onSubmit={handleTransition}
              submitLabel={
                transitioning
                  ? "Processing…"
                  : transitionLabel === "Submit for Review"
                    ? "Submit for Review"
                    : `Submit for ${transitionLabel}`
              }
              submitDisabled={
                transitioning ||
                (transitionRequireComments && !transitionComments.trim())
              }
            />
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              You are about to change the status of{" "}
              <span className="font-semibold text-foreground">
                "{selectedCatalog.name}"
              </span>{" "}
              to{" "}
              <span className="font-semibold text-foreground capitalize">
                {transitionTarget.replace(/_/g, " ")}
              </span>
              .
            </p>

            {/* Warning for destructive transitions */}
            {lifecycleActions[selectedCatalog.lifecycleStatus]?.find(
              (a) => a.target === transitionTarget,
            )?.warning && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">
                    {
                      lifecycleActions[selectedCatalog.lifecycleStatus]?.find(
                        (a) => a.target === transitionTarget,
                      )?.warning
                    }
                  </span>
                </div>
              )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <MessageSquare className="size-3.5" />
                Comments{" "}
                {transitionRequireComments ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="d text-xs">(optional)</span>
                )}
              </label>
              <textarea
                placeholder={
                  transitionRequireComments
                    ? "Please provide a reason…"
                    : "Add a comment…"
                }
                value={transitionComments}
                onChange={(e) => setTransitionComments(e.target.value)}
                rows={3}
                className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white"
              />
            </div>
          </div>
        </Modal>

        {/* ── Unsaved Changes Warning ── */}
        <Modal
          open={showUnsavedWarning}
          onClose={() => setShowUnsavedWarning(false)}
          title="Unsaved Changes"
          description="You have unsaved changes. What would you like to do before navigating away?"
          footer={
            <div className="flex w-full items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowUnsavedWarning(false);
                  setPendingSelectNode(null);
                }}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium d hover:bg-accent transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setShowUnsavedWarning(false);
                  if (pendingSelectNode) applyControlSelect(pendingSelectNode);
                  setPendingSelectNode(null);
                }}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                Discard Changes
              </button>
              <button
                onClick={async () => {
                  setShowUnsavedWarning(false);
                  if (editMode) await saveControlEdit();
                  else await saveGroupEdit();
                  if (pendingSelectNode) applyControlSelect(pendingSelectNode);
                  setPendingSelectNode(null);
                }}
                disabled={editMode ? !editTitle.trim() : !editGroupTitle.trim()}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20"
              >
                Save &amp; Continue
              </button>
            </div>
          }
        >
          <div />
        </Modal>

        {/* ── Add Group Modal ── */}
        <Modal
          open={showAddGroup}
          onClose={() => setShowAddGroup(false)}
          title="New Group"
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowAddGroup(false)}
              onSubmit={saveNewGroup}
              submitLabel={savingNewGroup ? "Saving…" : "Save"}
              submitDisabled={
                savingNewGroup || !newGroupId.trim() || !newGroupTitle.trim()
              }
            />
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Group ID" required>
                <FormInput
                  value={newGroupId}
                  onChange={(v) => setNewGroupId(v)}
                  placeholder="e.g. ac"
                />
              </FormField>
              <FormField label="Title" required>
                <FormInput
                  value={newGroupTitle}
                  onChange={(v) => setNewGroupTitle(v)}
                  placeholder="e.g. Access Control"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Class">
                <FormInput
                  value={newGroupClazz}
                  onChange={(v) => setNewGroupClazz(v)}
                  placeholder="e.g. family"
                />
              </FormField>
              <FormField label="Group Order">
                <FormInput
                  value={String(newGroupSortOrder)}
                  onChange={(v) => setNewGroupSortOrder(Number(v) || 1)}
                  placeholder="1"
                />
              </FormField>
            </div>
          </div>
        </Modal>

        {/* ── Add Control Modal ── */}
        <Modal
          open={showAddControl}
          onClose={() => setShowAddControl(false)}
          title={
            selectedGroupNode
              ? `Add Control to ${selectedGroupNode.id}`
              : "Add Group & Control"
          }
          size="md"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowAddControl(false)}
              onSubmit={saveNewControl}
              submitLabel={savingNewControl ? "Saving" : "Save"}
              submitDisabled={
                savingNewControl ||
                !newControlId.trim() ||
                !newControlTitle.trim() ||
                (!selectedGroupNode &&
                  (!newGroupId.trim() || !newGroupTitle.trim()))
              }
            />
          }
        >
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Group fields when no group is pre-selected */}
            {!selectedGroupNode && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Group ID" required>
                    <FormInput
                      value={newGroupId}
                      onChange={(v) => setNewGroupId(v)}
                      placeholder="e.g. AC"
                    />
                  </FormField>
                  <FormField label="Group Title" required>
                    <FormInput
                      value={newGroupTitle}
                      onChange={(v) => setNewGroupTitle(v)}
                      placeholder="e.g. Access Control"
                    />
                  </FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Group Class">
                    <FormInput
                      value={newGroupClazz}
                      onChange={(v) => setNewGroupClazz(v)}
                      placeholder="e.g. SP800-53"
                    />
                  </FormField>
                  <FormField label="Group Order">
                    <FormInput
                      value={String(newGroupSortOrder)}
                      onChange={(v) => setNewGroupSortOrder(Number(v) || 1)}
                      placeholder="e.g. 1"
                    />
                  </FormField>
                </div>
              </>
            )}
            {/* Basic fields */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Control ID" required>
                <FormInput
                  value={newControlId}
                  onChange={(v) => setNewControlId(v)}
                  placeholder="e.g. ac-1"
                />
              </FormField>
              <FormField label="Title" required>
                <FormInput
                  value={newControlTitle}
                  onChange={(v) => setNewControlTitle(v)}
                  placeholder="e.g. Policy and Procedures"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Class">
                <FormInput
                  value={newControlClazz}
                  onChange={(v) => setNewControlClazz(v)}
                  placeholder="e.g. SP800-53"
                />
              </FormField>
              <FormField label="Sort Order">
                <FormInput
                  value={String(newControlSortOrder)}
                  onChange={(v) => setNewControlSortOrder(Number(v) || 1)}
                  placeholder="1"
                />
              </FormField>
            </div>

            {/* Parts */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold d">Parts</span>
                <button
                  onClick={addNewControlPart}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                >
                  <Plus className="size-3" /> Add Part
                </button>
              </div>
              {newControlParts.map((part, pi) => (
                <div
                  key={pi}
                  className="rounded border border-border p-2.5 space-y-2 bg-muted/20"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold d">
                      Part {pi + 1}
                    </span>
                    <button
                      onClick={() => removeNewControlPart(pi)}
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <FormField label="Part ID">
                      <FormInput
                        placeholder="e.g. ac-1_smt"
                        value={part.partId}
                        onChange={(v) => updateNewControlPart(pi, "partId", v)}
                      />
                    </FormField>
                    <FormField label="Name">
                      <FormInput
                        placeholder="statement / guidance"
                        value={part.name}
                        onChange={(v) => updateNewControlPart(pi, "name", v)}
                      />
                    </FormField>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      Prose{" "}
                      <span className="text-[10px] font-normal d italic">
                        (Use{" "}
                        <code className="font-mono not-italic">
                          {'{{ insert: param, id-ref="param-id" }}'}
                        </code>{" "}
                        to add a parameter reference)
                      </span>
                    </label>
                    <FormTextarea
                      placeholder="Enter the control statement text"
                      value={part.prose}
                      onChange={(v) => updateNewControlPart(pi, "prose", v)}
                      rows={4}
                    />
                  </div>

                  {/* Part-level Parameters */}
                  {(() => {
                    const partProseRefs = new Set(
                      extractParamIdsFromProse([part]),
                    );
                    const definedIds = new Set(
                      newControlParams.map((p) => p.paramId).filter(Boolean),
                    );
                    const canAdd = Array.from(partProseRefs).some(
                      (id) => !definedIds.has(id),
                    );
                    const partParams = newControlParams
                      .map((param, gi) => ({ param, gi }))
                      .filter(
                        ({ param }) =>
                          param._partIdx === pi ||
                          (param._partIdx === undefined &&
                            param.paramId &&
                            partProseRefs.has(param.paramId)),
                      );
                    return (
                      <div className="space-y-2 pt-2 border-t border-border/60">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold d">
                            Parameters
                          </span>
                          <div className="relative group">
                            <button
                              disabled={!canAdd}
                              onClick={() =>
                                setNewControlParams((p) => [
                                  ...p,
                                  {
                                    paramId: "",
                                    label: "",
                                    usage: "",
                                    clazz: null,
                                    constraints: [],
                                    guidelines: [],
                                    values: [],
                                    selectConfig: null,
                                    sortOrder: p.length + 1,
                                    _partIdx: pi,
                                  },
                                ])
                              }
                              className={`text-xs flex items-center gap-1 ${canAdd ? "text-indigo-600 dark:text-indigo-400 hover:underline" : "d cursor-not-allowed opacity-50"}`}
                            >
                              <Plus className="size-3" /> Add Parameter
                            </button>
                            {!canAdd && (
                              <div className="absolute right-0 top-5 z-10 hidden group-hover:block w-56 px-2.5 py-1.5 rounded-md bg-popover border border-border text-[11px] d shadow-md">
                                Add a{" "}
                                <code className="font-mono">
                                  {'{{ insert: param, id-ref="param-id" }}'}
                                </code>{" "}
                                placeholder in this part's prose first.
                              </div>
                            )}
                          </div>
                        </div>
                        {partParams.map(({ param, gi }) =>
                          renderNewControlParamCard(param, gi),
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </Modal>

        {/* ── Catalog Type Change Confirmation ── */}
        <Modal
          open={pendingCatalogTypeChange !== null}
          onClose={() => {
            setPendingCatalogTypeChange(null);
            // Revert the dropdown back to the last confirmed value
            setEditingCatalogType(selectedCatalog.catalogType ?? "REGULATORY");
          }}
          title="Change Catalog Type?"
          size="sm"
          footer={
            <div className="flex justify-end gap-2 w-full">
              <button
                onClick={() => {
                  setPendingCatalogTypeChange(null);
                  // Revert the dropdown back to the last confirmed value
                  setEditingCatalogType(
                    selectedCatalog.catalogType ?? "REGULATORY",
                  );
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={savingName}
                onClick={() => {
                  if (pendingCatalogTypeChange && editingNameId) {
                    handleCatalogTypeChange(
                      editingNameId,
                      pendingCatalogTypeChange,
                    );
                  }
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {savingName ? "Saving…" : "Yes, Change Type"}
              </button>
            </div>
          }
        >
          <p className="text-sm d">
            Are you sure you want to change the catalog type of{" "}
            <span className="font-semibold text-foreground">
              {selectedCatalog.name}
            </span>{" "}
            from{" "}
            <span className="font-semibold text-foreground">
              {catalogTypeLabel(selectedCatalog.catalogType)}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-foreground">
              {catalogTypeLabel(pendingCatalogTypeChange)}
            </span>
            ?
          </p>
        </Modal>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Catalog Creation Wizard – Full Page
  ════════════════════════════════════════════════════════ */
  if (showWizard) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={closeWizard}
            className="flex items-center gap-1.5 text-sm d hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Catalog
          </button>
          <ChevronRight className="size-3.5 d" />
          <span className="text-sm font-medium">New Catalog</span>
        </div>

        {/* Header */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">New Catalog</h2>
              <p className="text-sm d mt-0.5">
                Follow the steps to Create your OSCAL catalog
              </p>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="rounded-lg border border-border bg-card px-6 py-5">
          <div className="flex items-center justify-between">
            {wizardSteps.map((s, i) => (
              <div
                key={s.key}
                className="flex items-center flex-1 last:flex-initial"
              >
                {/* Step circle + label */}
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`size-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${i < currentStepIndex
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 ring-2 ring-emerald-200 dark:ring-emerald-500/30"
                        : i === currentStepIndex
                          ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-500/30"
                          : "bg-muted d"
                      }`}
                  >
                    {i < currentStepIndex ? (
                      <CheckCircle className="size-5" />
                    ) : (
                      <span>{s.num}</span>
                    )}
                  </div>
                  <span
                    className={`text-sm font-semibold transition-colors ${i < currentStepIndex
                        ? "text-emerald-700 dark:text-emerald-400"
                        : i === currentStepIndex
                          ? "text-indigo-700 dark:text-indigo-400"
                          : "d"
                      }`}
                  >
                    {s.label}
                  </span>
                </div>
                {/* Connecting line */}
                {i < wizardSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-3 mb-6 rounded-full transition-colors ${i < currentStepIndex
                        ? "bg-emerald-300 dark:bg-emerald-500/40"
                        : "bg-border"
                      }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {wizardError && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
            <AlertTriangle className="size-4 flex-shrink-0" />
            <span className="text-xs">{wizardError}</span>
          </div>
        )}

        {/* Step content card */}
        <div className="rounded-lg border border-border bg-card p-6">
          {/* ── Step 1: Catalog Shell ── */}
          {wizardStep === "catalog" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h3 className="text-sm font-semibold mb-1">
                  Step 1: Create Catalog
                </h3>
                <p className="text-xs d">
                  Create the catalog{" "}
                  <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded"></code>
                </p>
              </div>
              <FormField
                label="Catalog Title"
                required
                hint="e.g. NIST SP 800-53 Rev 5, FedRAMP Moderate Baseline"
              >
                <FormInput
                  placeholder="Type catalog title"
                  value={wizCatalogTitle}
                  onChange={(v) => setWizCatalogTitle(v)}
                />
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Organization" required>
                {accessibleOrgsLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="size-4 animate-spin d" />
                    <span className="text-sm d">Loading</span>
                  </div>
                ) : accessibleOrgs.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      No accessible organizations.
                    </span>
                  </div>
                ) : accessibleOrgs.length === 1 ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                    <span className="font-medium">
                      {accessibleOrgs[0].name}
                    </span>
                  </div>
                ) : (
                  <FormSelect
                    options={accessibleOrgs.map((o) => ({
                      value: o.id,
                      // Name only: the short code is an internal identifier and
                      // the org id is what the backend is given.
                      label: o.name,
                    }))}
                    value={wizCatalogOrgId}
                    onChange={setWizCatalogOrgId}
                    placeholder="Select Organization"
                  />
                )}
              </FormField>
              <FormField
                label="Catalog Type"
                required
                hint="Regulatory for compliance frameworks, Technology for product/platform controls, Integration for cross-framework mapping references"
              >
                <FormSelect
                  options={CATALOG_TYPE_OPTIONS}
                  value={wizCatalogType}
                  onChange={(v) => setWizCatalogType(v as CatalogType)}
                  placeholder="Select catalog type"
                />
              </FormField>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Country Name"
                  hint="Optional — the country this catalog applies to"
                >
                  <FormInput
                    placeholder="e.g. Singapore"
                    value={wizCountryName}
                    onChange={(v) => setWizCountryName(v)}
                  />
                </FormField>
                <FormField
                  label="Country Code"
                  hint="Optional — 2-letter ISO code"
                >
                  <FormInput
                    placeholder="e.g. SG"
                    value={wizCountryCode}
                    maxLength={2}
                    onChange={(v) =>
                      setWizCountryCode(
                        v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2),
                      )
                    }
                  />
                </FormField>
              </div>
            </div>
          )}

          {/* ── Step 2: Groups ── */}
          {wizardStep === "groups" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h3 className="text-sm font-semibold mb-1">
                  Step 2: Add Control Groups (Families)
                </h3>
                <p className="text-xs d">
                  Groups are folders for controls (e.g. AC = Access Control, AU
                  = Audit){" "}
                  <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded"></code>
                </p>
              </div>

              <div className="space-y-3">
                {wizGroups.map((g, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-border p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold d">Group {i + 1}</span>
                      <div className="flex items-center gap-1">
                        {g.savedId && (
                          <CheckCircle className="size-3.5 text-emerald-500" />
                        )}
                        {wizGroups.length > 1 && (
                          <Tip label="Remove Group" side="bottom">
                            <button
                              onClick={() => removeGroup(i)}
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </Tip>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <FormField label="Group ID" required>
                        <FormInput
                          placeholder="e.g. ac"
                          value={g.groupId}
                          onChange={(v) => updateGroup(i, "groupId", v)}
                        />
                        {groupIdErrors[i] && (
                          <p className="text-[10px] text-red-500 mt-1">
                            {groupIdErrors[i]}
                          </p>
                        )}
                      </FormField>
                      <FormField label="Title" required>
                        <FormInput
                          placeholder="e.g. Access Control"
                          value={g.title}
                          onChange={(v) => updateGroup(i, "title", v)}
                        />
                      </FormField>
                      <FormField label="Class">
                        <FormInput
                          placeholder="family"
                          value={g.clazz}
                          onChange={(v) => updateGroup(i, "clazz", v)}
                        />
                      </FormField>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <RBACAction module="catalog" action="update">
                  <button
                    onClick={addGroup}
                    className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    <Plus className="size-3.5" /> Add another group
                  </button>
                </RBACAction>
              </div>
            </div>
          )}

          {/* ── Step 3: Controls ── */}
          {wizardStep === "controls" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h3 className="text-sm font-semibold mb-1">
                  Step 3: Add Controls
                </h3>
                <p className="text-xs d">
                  Add controls into groups with parts (statement, guidance) and
                  parameters. Calls{" "}
                  <code className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded"></code>
                </p>
              </div>

              {/* Group tabs */}
              {wizGroups
                .filter((g) => g.savedId)
                .map((g, gi) => {
                  const groupControls = wizControls
                    .map((c, ci) => ({ ...c, _idx: ci }))
                    .filter((c) => c.groupIndex === gi);
                  return (
                    <div
                      key={gi}
                      className="rounded-lg border border-border overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/50 border-b border-border">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">
                            {g.groupId.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium">{g.title}</span>
                          <span className="text-xs d">
                            ({groupControls.length} controls)
                          </span>
                        </div>
                        <button
                          onClick={() => addControl(gi)}
                          className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          <Plus className="size-3" /> Add Control
                        </button>
                      </div>

                      {groupControls.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs d">
                          No controls yet. Click "Add Control" to get started.
                        </div>
                      ) : (
                        <div className="divide-y divide-border">
                          {groupControls.map((ctrl) => {
                            const isExpanded = expandedControl === ctrl._idx;
                            return (
                              <div key={ctrl._idx} className="bg-card">
                                {/* Control header */}
                                <button
                                  onClick={() =>
                                    setExpandedControl(
                                      isExpanded ? null : ctrl._idx,
                                    )
                                  }
                                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-accent/50 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                      <ChevronUp className="size-3.5 d" />
                                    ) : (
                                      <ChevronDown className="size-3.5 d" />
                                    )}
                                    {ctrl.controlId && (
                                      <span className="font-mono text-xs font-bold">
                                        {ctrl.controlId?.toUpperCase()}
                                      </span>
                                    )}
                                    <span className="text-sm">
                                      {ctrl.title || `Control ${ctrl._idx + 1}`}
                                    </span>
                                  </div>
                                  <Tip label="Remove Control" side="bottom">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeControl(ctrl._idx);
                                      }}
                                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-500 hover:text-red-600"
                                    >
                                      <Trash2 className="size-3.5" />
                                    </button>
                                  </Tip>
                                </button>

                                {/* Expanded control form */}
                                {isExpanded && (
                                  <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                                    <div className="grid grid-cols-3 gap-2">
                                      <FormField label="Control ID" required>
                                        <FormInput
                                          placeholder="e.g. ac-1"
                                          value={ctrl.controlId}
                                          onChange={(v) =>
                                            updateControl(
                                              ctrl._idx,
                                              "controlId",
                                              v,
                                            )
                                          }
                                        />
                                        {controlIdErrors[ctrl._idx] && (
                                          <p className="text-[10px] text-red-500 mt-1">
                                            {controlIdErrors[ctrl._idx]}
                                          </p>
                                        )}
                                      </FormField>
                                      <FormField label="Title" required>
                                        <FormInput
                                          placeholder="e.g. Policy and Procedures"
                                          value={ctrl.title}
                                          onChange={(v) =>
                                            updateControl(ctrl._idx, "title", v)
                                          }
                                        />
                                      </FormField>
                                      <FormField label="Class">
                                        <FormInput
                                          placeholder="SP800-53"
                                          value={ctrl.clazz}
                                          onChange={(v) =>
                                            updateControl(ctrl._idx, "clazz", v)
                                          }
                                        />
                                      </FormField>
                                    </div>

                                    {/* Parts */}
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold d">
                                          Parts
                                        </span>
                                        <button
                                          onClick={() =>
                                            addPartToControl(ctrl._idx)
                                          }
                                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                        >
                                          <Plus className="size-3" /> Add Part
                                        </button>
                                      </div>
                                      {ctrl.parts.map((part, pi) => (
                                        <div
                                          key={pi}
                                          className="rounded border border-border p-2.5 space-y-2 bg-muted/20"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-bold d">
                                              Part {pi + 1}
                                            </span>
                                            <button
                                              onClick={() =>
                                                removePart(ctrl._idx, pi)
                                              }
                                              className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                            >
                                              <X className="size-3" />
                                            </button>
                                          </div>
                                          <div className="grid grid-cols-2 gap-2">
                                            <FormField label="Part ID">
                                              <FormInput
                                                placeholder="e.g. ac-1_smt"
                                                value={part.partId}
                                                onChange={(v) =>
                                                  updatePart(
                                                    ctrl._idx,
                                                    pi,
                                                    "partId",
                                                    v,
                                                  )
                                                }
                                              />
                                            </FormField>
                                            <FormField label="Name">
                                              <FormInput
                                                placeholder="statement / guidance"
                                                value={part.name}
                                                onChange={(v) =>
                                                  updatePart(
                                                    ctrl._idx,
                                                    pi,
                                                    "name",
                                                    v,
                                                  )
                                                }
                                              />
                                            </FormField>
                                          </div>
                                          <div className="space-y-1.5">
                                            <label className="text-sm font-medium text-foreground">
                                              Prose{" "}
                                              <span className="text-[10px] font-normal d italic">
                                                (Use{" "}
                                                <code className="font-mono not-italic">
                                                  {
                                                    '{{ insert: param, id-ref="param-id" }}'
                                                  }
                                                </code>{" "}
                                                to add a parameter reference)
                                              </span>
                                            </label>
                                            <FormTextarea
                                              placeholder="Enter the control statement text"
                                              value={part.prose}
                                              onChange={(v) =>
                                                updatePart(
                                                  ctrl._idx,
                                                  pi,
                                                  "prose",
                                                  v,
                                                )
                                              }
                                              rows={4}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Params */}
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold d">
                                          Parameters
                                        </span>
                                        {(() => {
                                          const proseRefs = new Set(
                                            extractParamIdsFromProse(
                                              ctrl.parts,
                                            ),
                                          );
                                          const definedIds = new Set(
                                            ctrl.params
                                              .map((p) => p.paramId)
                                              .filter(Boolean),
                                          );
                                          const canAdd = Array.from(
                                            proseRefs,
                                          ).some((id) => !definedIds.has(id));
                                          return (
                                            <div className="relative group">
                                              <button
                                                disabled={!canAdd}
                                                onClick={() => {
                                                  const newParam: ParamPayload =
                                                  {
                                                    paramId: "",
                                                    label: "",
                                                    usage: "",
                                                    clazz: null,
                                                    constraints: [],
                                                    guidelines: [],
                                                    values: [],
                                                    selectConfig: null,
                                                    sortOrder:
                                                      ctrl.params.length + 1,
                                                  };
                                                  setWizControls((c) =>
                                                    c.map((ct, ci) =>
                                                      ci !== ctrl._idx
                                                        ? ct
                                                        : {
                                                          ...ct,
                                                          params: [
                                                            ...ct.params,
                                                            newParam,
                                                          ],
                                                        },
                                                    ),
                                                  );
                                                }}
                                                className={`text-xs flex items-center gap-1 ${canAdd ? "text-indigo-600 dark:text-indigo-400 hover:underline" : "d cursor-not-allowed opacity-50"}`}
                                              >
                                                <Plus className="size-3" /> Add
                                                Parameter
                                              </button>
                                              {!canAdd && (
                                                <div className="absolute right-0 top-5 z-10 hidden group-hover:block w-56 px-2.5 py-1.5 rounded-md bg-popover border border-border text-[11px] d shadow-md">
                                                  Add a{" "}
                                                  <code className="font-mono">
                                                    {
                                                      '{{ insert: param, id-ref="param-id" }}'
                                                    }
                                                  </code>{" "}
                                                  placeholder in the statement
                                                  prose first.
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                      {ctrl.params.map((param, pi) => {
                                        const proseRefs = new Set(
                                          extractParamIdsFromProse(
                                            ctrl.parts,
                                          ),
                                        );
                                        const selectCfg = param.selectConfig as
                                          | ParameterSelection
                                          | null
                                          | undefined;
                                        const constraints =
                                          (param.constraints ??
                                            []) as ParameterConstraint[];
                                        const isUnused =
                                          proseRefs.size > 0 &&
                                          param.paramId.trim() !== "" &&
                                          !proseRefs.has(param.paramId.trim());
                                        return (
                                          <div
                                            key={pi}
                                            className={`rounded border p-2.5 space-y-2 bg-muted/20 ${isUnused ? "border-amber-400 dark:border-amber-500" : "border-border"}`}
                                          >
                                            {isUnused && (
                                              <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 pb-1">
                                                <AlertTriangle className="size-3 shrink-0" />
                                                <span>
                                                  The Param ID does not match the
                                                  parameter reference in the
                                                  Statement. Please enter the
                                                  exact parameter ID used in the
                                                  Statement (e.g.,{" "}
                                                  <code className="font-mono">
                                                    ac-1_prm_1
                                                  </code>
                                                  )
                                                </span>
                                              </div>
                                            )}
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] font-bold d">
                                                Param {pi + 1}
                                              </span>
                                              <button
                                                onClick={() =>
                                                  setWizControls((c) =>
                                                    c.map((ct, ci) =>
                                                      ci !== ctrl._idx
                                                        ? ct
                                                        : {
                                                          ...ct,
                                                          params:
                                                            ct.params.filter(
                                                              (_, i) =>
                                                                i !== pi,
                                                            ),
                                                        },
                                                    ),
                                                  )
                                                }
                                                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                              >
                                                <X className="size-3" />
                                              </button>
                                            </div>
                                            {/* Row 1: Param ID, Label, Class */}
                                            <div className="grid grid-cols-3 gap-2">
                                              <FormField label="Param ID">
                                                <FormInput
                                                  placeholder="e.g. ac-1_prm_1"
                                                  value={param.paramId}
                                                  onChange={(v) =>
                                                    updateParam(
                                                      ctrl._idx,
                                                      pi,
                                                      "paramId",
                                                      v,
                                                    )
                                                  }
                                                />
                                              </FormField>
                                              <FormField label="Label">
                                                <FormInput
                                                  placeholder="e.g. organization-defined roles"
                                                  value={param.label}
                                                  onChange={(v) =>
                                                    updateParam(
                                                      ctrl._idx,
                                                      pi,
                                                      "label",
                                                      v,
                                                    )
                                                  }
                                                />
                                              </FormField>
                                              <FormField label="Class">
                                                <Dropdown
                                                  variant="form"
                                                  placeholder="Select class"
                                                  value={param.clazz ?? ""}
                                                  onChange={(v) =>
                                                    updateParam(
                                                      ctrl._idx,
                                                      pi,
                                                      "clazz",
                                                      v || null,
                                                    )
                                                  }
                                                  options={[
                                                    {
                                                      value: "str",
                                                      label: "str (text)",
                                                    },
                                                    {
                                                      value: "int",
                                                      label: "int (integer)",
                                                    },
                                                  ]}
                                                />
                                              </FormField>
                                            </div>

                                            {/* Row 2: Usage */}
                                            <FormField label="Usage">
                                              <FormInput
                                                placeholder="e.g. description of how this parameter is used"
                                                value={param.usage ?? ""}
                                                onChange={(v) =>
                                                  updateParam(
                                                    ctrl._idx,
                                                    pi,
                                                    "usage",
                                                    v,
                                                  )
                                                }
                                              />
                                            </FormField>

                                            {/* Row 3: Default Values */}
                                            <FormField label="Default Values">
                                              <input
                                                type="text"
                                                placeholder="e.g. 90, 180"
                                                defaultValue={(
                                                  param.values ?? []
                                                ).join(", ")}
                                                onBlur={(e) =>
                                                  updateParam(
                                                    ctrl._idx,
                                                    pi,
                                                    "values",
                                                    e.target.value
                                                      ? e.target.value
                                                        .split(",")
                                                        .map((s) => s.trim())
                                                        .filter(Boolean)
                                                      : [],
                                                  )
                                                }
                                                className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white"
                                              />
                                            </FormField>

                                            {/* Selection Config */}
                                            <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium d">
                                                  Selection Choices
                                                </span>
                                                {!selectCfg ? (
                                                  <button
                                                    onClick={() =>
                                                      updateParam(
                                                        ctrl._idx,
                                                        pi,
                                                        "selectConfig",
                                                        {
                                                          "how-many": "one",
                                                          choice: [],
                                                        },
                                                      )
                                                    }
                                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                                  >
                                                    <Plus className="size-3" />{" "}
                                                    Add Selection
                                                  </button>
                                                ) : (
                                                  <button
                                                    onClick={() =>
                                                      updateParam(
                                                        ctrl._idx,
                                                        pi,
                                                        "selectConfig",
                                                        null,
                                                      )
                                                    }
                                                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                                  >
                                                    <X className="size-3.5" />
                                                  </button>
                                                )}
                                              </div>
                                              {selectCfg && (
                                                <div className="space-y-2">
                                                  <div className="grid grid-cols-2 gap-2">
                                                    <div className="space-y-1">
                                                      <span className="text-[10px] font-medium d">
                                                        Choices
                                                      </span>
                                                      <div
                                                        className="flex flex-wrap items-center gap-1.5 min-h-[40px] px-3 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] focus-within:border-indigo-500 dark:focus-within:border-indigo-500 transition-all cursor-text"
                                                        onClick={(e) => {
                                                          const input = (
                                                            e.currentTarget as HTMLElement
                                                          ).querySelector(
                                                            "input",
                                                          );
                                                          input?.focus();
                                                        }}
                                                      >
                                                        {selectCfg.choice.map(
                                                          (c, ci) => (
                                                            <span
                                                              key={ci}
                                                              className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
                                                            >
                                                              {c}
                                                              <button
                                                                type="button"
                                                                onClick={(
                                                                  ev,
                                                                ) => {
                                                                  ev.stopPropagation();
                                                                  updateParam(
                                                                    ctrl._idx,
                                                                    pi,
                                                                    "selectConfig",
                                                                    {
                                                                      ...selectCfg,
                                                                      choice:
                                                                        selectCfg.choice.filter(
                                                                          (
                                                                            _,
                                                                            i,
                                                                          ) =>
                                                                            i !==
                                                                            ci,
                                                                        ),
                                                                    },
                                                                  );
                                                                }}
                                                                className="text-indigo-400 hover:text-red-500 transition-colors"
                                                              >
                                                                <X className="size-3" />
                                                              </button>
                                                            </span>
                                                          ),
                                                        )}
                                                        <input
                                                          type="text"
                                                          placeholder={
                                                            selectCfg.choice
                                                              .length === 0
                                                              ? "Type a choice and press Enter"
                                                              : ""
                                                          }
                                                          onKeyDown={(e) => {
                                                            if (
                                                              e.key === "Enter"
                                                            ) {
                                                              e.preventDefault();
                                                              const val = (
                                                                e.target as HTMLInputElement
                                                              ).value.trim();
                                                              if (
                                                                val &&
                                                                !selectCfg.choice.includes(
                                                                  val,
                                                                )
                                                              ) {
                                                                updateParam(
                                                                  ctrl._idx,
                                                                  pi,
                                                                  "selectConfig",
                                                                  {
                                                                    ...selectCfg,
                                                                    choice: [
                                                                      ...selectCfg.choice,
                                                                      val,
                                                                    ],
                                                                  },
                                                                );
                                                                (
                                                                  e.target as HTMLInputElement
                                                                ).value = "";
                                                              }
                                                            } else if (
                                                              e.key ===
                                                              "Backspace" &&
                                                              !(
                                                                e.target as HTMLInputElement
                                                              ).value &&
                                                              selectCfg.choice
                                                                .length > 0
                                                            ) {
                                                              updateParam(
                                                                ctrl._idx,
                                                                pi,
                                                                "selectConfig",
                                                                {
                                                                  ...selectCfg,
                                                                  choice:
                                                                    selectCfg.choice.slice(
                                                                      0,
                                                                      -1,
                                                                    ),
                                                                },
                                                              );
                                                            }
                                                          }}
                                                          className="flex-1 min-w-[120px] text-sm bg-transparent outline-none placeholder:text-[#94a3b8] dark:placeholder:text-white/25 text-[#0f172a] dark:text-white"
                                                        />
                                                      </div>
                                                    </div>
                                                    <FormField label="How Many">
                                                      <Dropdown
                                                        variant="form"
                                                        value={
                                                          selectCfg["how-many"]
                                                        }
                                                        onChange={(v) =>
                                                          updateParam(
                                                            ctrl._idx,
                                                            pi,
                                                            "selectConfig",
                                                            {
                                                              ...selectCfg,
                                                              "how-many": v,
                                                            },
                                                          )
                                                        }
                                                        options={[
                                                          {
                                                            value: "one",
                                                            label:
                                                              "Pick exactly one",
                                                          },
                                                          {
                                                            value:
                                                              "one-or-more",
                                                            label:
                                                              "Pick one or more",
                                                          },
                                                        ]}
                                                      />
                                                    </FormField>
                                                  </div>
                                                </div>
                                              )}
                                            </div>

                                            {/* Constraints */}
                                            <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium d">
                                                  Constraints
                                                </span>
                                                <button
                                                  onClick={() =>
                                                    updateParam(
                                                      ctrl._idx,
                                                      pi,
                                                      "constraints",
                                                      [
                                                        ...constraints,
                                                        {
                                                          description: "",
                                                          tests: [
                                                            {
                                                              expression: "",
                                                              remarks: "",
                                                            },
                                                          ],
                                                        },
                                                      ],
                                                    )
                                                  }
                                                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                                >
                                                  <Plus className="size-3" />{" "}
                                                  Add Constraint
                                                </button>
                                              </div>
                                              {constraints.map((con, ci) => (
                                                <div
                                                  key={ci}
                                                  className="rounded border border-border p-2 space-y-2 bg-background/50"
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold d">
                                                      Constraint {ci + 1}
                                                    </span>
                                                    <button
                                                      onClick={() =>
                                                        updateParam(
                                                          ctrl._idx,
                                                          pi,
                                                          "constraints",
                                                          constraints.filter(
                                                            (_, i) => i !== ci,
                                                          ),
                                                        )
                                                      }
                                                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded"
                                                    >
                                                      <X className="size-3" />
                                                    </button>
                                                  </div>
                                                  <FormField label="Description">
                                                    <FormInput
                                                      placeholder="e.g. Password minimum length must be between 8 and 128"
                                                      value={
                                                        con.description ?? ""
                                                      }
                                                      onChange={(v) => {
                                                        const updated = [
                                                          ...constraints,
                                                        ];
                                                        updated[ci] = {
                                                          ...updated[ci],
                                                          description: v,
                                                        };
                                                        updateParam(
                                                          ctrl._idx,
                                                          pi,
                                                          "constraints",
                                                          updated,
                                                        );
                                                      }}
                                                    />
                                                  </FormField>
                                                  {(con.tests ?? []).map(
                                                    (t, ti) => (
                                                      <div
                                                        key={ti}
                                                        className="flex items-end gap-2"
                                                      >
                                                        <div className="grid grid-cols-2 gap-2 flex-1">
                                                          <FormField label="Expression">
                                                            <FormInput
                                                              placeholder="e.g. value >= 8"
                                                              value={
                                                                t.expression ??
                                                                ""
                                                              }
                                                              onChange={(v) => {
                                                                const updatedTests =
                                                                  [
                                                                    ...(con.tests ??
                                                                      []),
                                                                  ];
                                                                updatedTests[
                                                                  ti
                                                                ] = {
                                                                  ...updatedTests[
                                                                  ti
                                                                  ],
                                                                  expression: v,
                                                                };
                                                                const updated =
                                                                  [
                                                                    ...constraints,
                                                                  ];
                                                                updated[ci] = {
                                                                  ...updated[
                                                                  ci
                                                                  ],
                                                                  tests:
                                                                    updatedTests,
                                                                };
                                                                updateParam(
                                                                  ctrl._idx,
                                                                  pi,
                                                                  "constraints",
                                                                  updated,
                                                                );
                                                              }}
                                                            />
                                                          </FormField>
                                                          <FormField label="Remarks">
                                                            <FormInput
                                                              placeholder="e.g. Minimum 8 characters"
                                                              value={
                                                                t.remarks ?? ""
                                                              }
                                                              onChange={(v) => {
                                                                const updatedTests =
                                                                  [
                                                                    ...(con.tests ??
                                                                      []),
                                                                  ];
                                                                updatedTests[
                                                                  ti
                                                                ] = {
                                                                  ...updatedTests[
                                                                  ti
                                                                  ],
                                                                  remarks: v,
                                                                };
                                                                const updated =
                                                                  [
                                                                    ...constraints,
                                                                  ];
                                                                updated[ci] = {
                                                                  ...updated[
                                                                  ci
                                                                  ],
                                                                  tests:
                                                                    updatedTests,
                                                                };
                                                                updateParam(
                                                                  ctrl._idx,
                                                                  pi,
                                                                  "constraints",
                                                                  updated,
                                                                );
                                                              }}
                                                            />
                                                          </FormField>
                                                        </div>
                                                        {(con.tests ?? [])
                                                          .length > 1 && (
                                                            <button
                                                              onClick={() => {
                                                                const updatedTests =
                                                                  (
                                                                    con.tests ??
                                                                    []
                                                                  ).filter(
                                                                    (_, i) =>
                                                                      i !== ti,
                                                                  );
                                                                const updated = [
                                                                  ...constraints,
                                                                ];
                                                                updated[ci] = {
                                                                  ...updated[ci],
                                                                  tests:
                                                                    updatedTests,
                                                                };
                                                                updateParam(
                                                                  ctrl._idx,
                                                                  pi,
                                                                  "constraints",
                                                                  updated,
                                                                );
                                                              }}
                                                              className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mb-2"
                                                            >
                                                              <X className="size-3.5" />
                                                            </button>
                                                          )}
                                                      </div>
                                                    ),
                                                  )}
                                                  <button
                                                    onClick={() => {
                                                      const updated = [
                                                        ...constraints,
                                                      ];
                                                      updated[ci] = {
                                                        ...updated[ci],
                                                        tests: [
                                                          ...(updated[ci]
                                                            .tests ?? []),
                                                          {
                                                            expression: "",
                                                            remarks: "",
                                                          },
                                                        ],
                                                      };
                                                      updateParam(
                                                        ctrl._idx,
                                                        pi,
                                                        "constraints",
                                                        updated,
                                                      );
                                                    }}
                                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 ml-auto"
                                                  >
                                                    <Plus className="size-3" />{" "}
                                                    Add Text
                                                  </button>
                                                </div>
                                              ))}
                                            </div>

                                            {/* Guidelines */}
                                            <div className="space-y-1.5 rounded border border-border p-2 bg-muted/10">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium d">
                                                  Guidelines
                                                </span>
                                                <button
                                                  onClick={() =>
                                                    updateParam(
                                                      ctrl._idx,
                                                      pi,
                                                      "guidelines",
                                                      [
                                                        ...(param.guidelines ??
                                                          []),
                                                        { prose: "" },
                                                      ],
                                                    )
                                                  }
                                                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                                >
                                                  <Plus className="size-3" />{" "}
                                                  Add Guideline
                                                </button>
                                              </div>
                                              {(param.guidelines ?? []).map(
                                                (
                                                  g: { prose: string },
                                                  gi: number,
                                                ) => (
                                                  <div
                                                    key={gi}
                                                    className="flex items-start gap-2"
                                                  >
                                                    <FormTextarea
                                                      placeholder="Enter guideline prose"
                                                      value={g.prose ?? ""}
                                                      onChange={(v) => {
                                                        const updated = [
                                                          ...(param.guidelines ??
                                                            []),
                                                        ];
                                                        updated[gi] = {
                                                          prose: v,
                                                        };
                                                        updateParam(
                                                          ctrl._idx,
                                                          pi,
                                                          "guidelines",
                                                          updated,
                                                        );
                                                      }}
                                                      rows={2}
                                                    />
                                                    <button
                                                      onClick={() => {
                                                        const updated = (
                                                          param.guidelines ?? []
                                                        ).filter(
                                                          (
                                                            _: {
                                                              prose: string;
                                                            },
                                                            i: number,
                                                          ) => i !== gi,
                                                        );
                                                        updateParam(
                                                          ctrl._idx,
                                                          pi,
                                                          "guidelines",
                                                          updated,
                                                        );
                                                      }}
                                                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 p-0.5 rounded mt-1"
                                                    >
                                                      <X className="size-3" />
                                                    </button>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

              {wizGroups.filter((g) => g.savedId).length === 0 && (
                <div className="py-8 text-center text-sm d">
                  No saved groups. Go back to Step 2 to create groups first, or
                  skip this step.
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Review ── */}
          {wizardStep === "review" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3">
                <CheckCircle className="size-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                    Catalog Created Successfully
                  </h3>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5"></p>
                </div>
              </div>

              <div className="rounded-lg border border-border p-4 space-y-3">
                <h4 className="text-sm font-semibold">Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="d">Catalog</span>
                    <span className="font-medium">{wizCatalogTitle}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="d">Catalog ID</span>
                    <code className="font-mono text-xs">{wizCatalogId}</code>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="d">Type</span>
                    <span className="font-medium">
                      {catalogTypeLabel(wizCatalogType || "REGULATORY")}
                    </span>
                  </div>
                  {(wizCountryName.trim() || wizCountryCode.trim()) && (
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="d">Country</span>
                      <span className="font-medium">
                        {wizCountryName.trim()}
                        {wizCountryCode.trim() && (
                          <span className="d ml-1">
                            ({wizCountryCode.trim()})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="d">Groups</span>
                    <span className="font-medium">
                      {wizGroups.filter((g) => g.savedId).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="d">Controls</span>
                    <span className="font-medium">{wizControls.length}</span>
                  </div>
                </div>

                {/* Group details */}
                {wizGroups
                  .filter((g) => g.savedId)
                  .map((g, i) => {
                    const ctrls = wizControls.filter((c) => c.groupIndex === i);
                    return (
                      <div
                        key={i}
                        className="rounded border border-border p-3 bg-muted/20"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">
                            {g.groupId.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium">{g.title}</span>
                        </div>
                        {ctrls.length > 0 ? (
                          <div className="ml-4 mt-1 space-y-0.5">
                            {ctrls.map((c, ci) => (
                              <div
                                key={ci}
                                className="flex items-center gap-2 text-xs d"
                              >
                                <span className="font-mono font-bold">
                                  {c.controlId.toUpperCase()}
                                </span>
                                <span>{c.title}</span>
                                <span className="text-[10px]">
                                  ({c.parts.length} parts, {c.params.length}{" "}
                                  params)
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs d ml-4">No controls</p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between w-full gap-3 pt-6 mt-6 border-t border-border">
            {wizardStep === "catalog" ? (
              <>
                <button
                  onClick={closeWizard}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleWizardSaveAsDraft}
                    disabled={!wizCatalogTitle.trim() || wizardSaving}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
                  >
                    <Save className="size-3.5" />
                    Save as Draft
                  </button>
                  <button
                    onClick={handleWizardCreateCatalog}
                    disabled={
                      !wizCatalogTitle.trim() || !wizCatalogType || wizardSaving
                    }
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  >
                    {wizardSaving ? "Next…" : "Next"}
                  </button>
                </div>
              </>
            ) : wizardStep === "groups" ? (
              <>
                <button
                  onClick={() => setWizardStep("catalog")}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Previous
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleWizardDone}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                  >
                    <Save className="size-3.5" />
                    Save as Draft
                  </button>
                  <button
                    onClick={handleWizardSaveGroups}
                    disabled={
                      wizGroups.every((g) => !g.groupId || !g.title) ||
                      Object.keys(groupIdErrors).length > 0 ||
                      wizardSaving
                    }
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  >
                    {wizardSaving ? "Saving…" : "Next"}
                  </button>
                </div>
              </>
            ) : wizardStep === "controls" ? (
              <>
                <button
                  onClick={() => setWizardStep("groups")}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Previous
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleWizardDone}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                  >
                    <Save className="size-3.5" />
                    Save as Draft
                  </button>
                  <button
                    onClick={handleWizardSaveControls}
                    disabled={wizControls.length === 0 || wizardSaving}
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  >
                    {wizardSaving ? "Saving…" : "Next"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={closeWizard}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWizardDone}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Catalog List View
  ════════════════════════════════════════════════════════ */
  return (
    <div className="h-full min-h-0 flex flex-col gap-6">
      <div className="flex-shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Catalog</h1>
          <p className="text-sm d mt-0.5">
            Central repository for OSCAL security control catalogs used to build
            compliance profiles.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <RBACAction module="catalog" action="create">
            <button
              onClick={() => {
                resetImport();
                setShowImport(true);
                fetchAccessibleOrgs("catalog", "create");
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
            >
              <Import className="size-4" /> Import Catalog
            </button>
            <button
              onClick={() => {
                resetWizard();
                setShowWizard(true);
                fetchAccessibleOrgs("catalog", "create");
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14pt_rgba(139,92,246,0.3)] hover:-translate-y-px"
            >
              <Plus className="size-4" /> New Catalog
            </button>
          </RBACAction>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm d">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <span>Something went wrong.</span>
          <button
            onClick={fetchCatalogs}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Type tabs — All / Regulatory / Technology / Integration.
          Scrolls sideways on a phone rather than overflowing the viewport, and
          stands 38px tall so it lines up with the search box, status filter and
          rows-per-card field in the toolbar below. */}
      <div className="flex-shrink-0 -mx-1 overflow-x-auto px-1 py-px scrollbar-neutral">
      <div className="inline-flex h-[38px] w-max items-center gap-2 px-1 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06]">
        {/* Counts come from the backend response — they can't be derived on the
            client any more now that only one page of rows is loaded at a time,
            so a tab whose total isn't known yet simply shows no badge. */}
        {([
          { key: "", label: "All Catalogs", count: catTypeCounts.ALL },
          { key: "REGULATORY", label: "Regulatory", count: catTypeCounts.REGULATORY },
          { key: "TECHNOLOGY", label: "Technology", count: catTypeCounts.TECHNOLOGY },
          { key: "INTEGRATION", label: "Integration", count: catTypeCounts.INTEGRATION },
        ] as const).map((t) => {
          const active = catTypeFilter === t.key;
          return (
            <button
              key={t.key || "all"}
              type="button"
              onClick={() => {
                setCatTypeFilter(t.key as "" | CatalogType);
                setCatPage(0);
                // Reset all filters when switching tabs to prevent cross-tab filter bleed
                setCatStatusFilter("");
                setSearch("");
                setCatKeyword("");
                setCatSortKey(null);
                setCatSortDir("asc");
              }}
              className={`flex h-[30px] flex-shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap px-3.5 rounded-lg text-sm font-semibold transition-all ${active
                  ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-[#64748b] dark:text-white/60 hover:text-foreground"
                }`}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span
                  className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full ${active
                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                    : "bg-[#e2e8f0] text-[#64748b] dark:bg-white/[0.08] dark:text-white/60"
                    }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      </div>

      {/* Catalog card grid (search + status filter + rows-per-card + pagination bundled) */}
      <EntityCardGrid
        items={paginatedCatalogs}
        loading={loading}
        getKey={(row) => row.id}
        getStatus={(row) => row.lifecycleStatus}
        moduleIcon={(row) => {
          const brand = catalogBrand(row);
          if (brand) return <CloudBrandBadge brand={brand} />;
          return row.catalogType === "TECHNOLOGY" ? (
            <Cpu />
          ) : row.catalogType === "INTEGRATION" ? (
            <Workflow />
          ) : (
            <FlagChip countryCode={row.countryCode} countryName={row.countryName} />
          );
        }}
        moduleColor={(row) =>
          row.catalogType === "TECHNOLOGY" ? "sky" : row.catalogType === "INTEGRATION" ? "violet" : "slate"
        }
        leadingIconShape={(row) =>
          // Branded cards render their own vendor-coloured badge, so the
          // shared module tint/ring is skipped ("none") for those.
          catalogBrand(row)
            ? "none"
            : row.catalogType === "TECHNOLOGY" || row.catalogType === "INTEGRATION"
              ? "circle"
              : "none"
        }
        showStatusAccentBar
        accentBarRgb={catalogRowAccentRgb}
        // Hover border/glow/sheen only on cards that carry an identity colour
        // (vendor brand or country flag), painted in that colour. Every other
        // card gets no hover border or sheen at all — the lifecycle status is
        // never what tints them.
        hoverAccentRgb={catalogRowAccentRgb}
        hoverAccentOnly
        hideStatusBadge
        gridCols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
        gridGap={{ base: 2, sm: 2, lg: 3 }}
        onOpen={(row) => handleSelectCatalog(row as unknown as CatalogRow)}
        page={catPage}
        pageSize={catPageSize}
        totalItems={catTotal}
        onPageChange={setCatPage}
        onPageSizeChange={(s) => {
          setCatPageSize(s);
          setCatPage(0);
        }}
        viewMode={catViewMode}
        onViewModeChange={(mode) => {
          setCatViewMode(mode);
          setCatPage(0);
        }}
        itemLabel="catalogs"
        emptyIcon={<BookOpen className="size-8 text-[#cbd5e1] dark:text-white/30" />}
        emptyText="No catalogs found"
        // The whole toolbar row is the TableToolbar below — search, filters,
        // view toggle and rows-per-page all come from it.
        hideBuiltInControls
        toolbarLeft={
          <TableToolbar
            search={{
              value: search,
              onChange: setSearch,
              onSubmit: submitCatSearch,
              placeholder: "Search catalogs",
              ariaLabel: "Search catalogs",
            }}
            viewMode={catViewMode}
            onViewModeChange={(mode) => {
              setCatViewMode(mode);
              setCatPage(0);
            }}
            pageSize={catPageSize}
            onPageSizeChange={(s) => {
              setCatPageSize(s);
              setCatPage(0);
            }}
            pageSizeOptions={
              catViewMode === "table" ? [10, 25, 50, 100] : [12, 24, 48, 96]
            }
            pageSizeLabel={
              catViewMode === "table" ? "Rows per page" : "Cards per page"
            }
            filters={[
              {
                key: "status",
                value: catStatusFilter,
                onChange: (v) => {
                  setCatStatusFilter(v);
                  setCatPage(0);
                },
                options: [
                  { value: "", label: "All Status" },
                  { value: "draft", label: "Draft", dot: "bg-slate-400" },
                  { value: "in_review", label: "In Review", dot: "bg-blue-500" },
                  { value: "approved", label: "Approved", dot: "bg-violet-500" },
                  { value: "published", label: "Published", dot: "bg-emerald-500" },
                  { value: "rejected", label: "Rejected", dot: "bg-red-500" },
                  { value: "superseded", label: "Superseded", dot: "bg-orange-500" },
                  { value: "withdrawn", label: "Withdrawn", dot: "bg-rose-700" },
                ],
              },
            ]}
          />
        }
        renderTitle={(row) => (
          <TitleTooltip text={row.name}>
            {/* Title keeps `text-foreground` on hover too (black on light,
              * white on dark) — no colour shift. */}
            <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate">
              {row.name}
            </h3>
          </TitleTooltip>
        )}
        renderHeaderRight={(row) => (
          // Lifecycle status keeps its own semantic colour (draft/published/...)
          // rather than the card's brand colour — the whole point of the badge
          // is that it means the same thing on every card. It is rendered
          // compact here so it stops competing with the branded card art and
          // leaves the title more room.
          <StatusBadge
            status={row.lifecycleStatus as Parameters<typeof StatusBadge>[0]["status"]}
            dot
            size="sm"
          />
        )}
        renderCardDecoration={(row) => {
          const brand = catalogBrand(row);
          if (brand) return <CloudBrandArt brand={brand} />;
          if (row.catalogType === "TECHNOLOGY" || row.catalogType === "INTEGRATION") return null;
          return <JurisdictionArt countryCode={row.countryCode} countryName={row.countryName} />;
        }}
        renderBadges={(row) => {
          // One colour for both chips - the card's own identity colour when it
          // has one, its module tint otherwise.
          const accent = catalogChipAccent(row);
          return (
            <>
              {row.catalogType && (
                <TypeBadge
                  label={catalogTypeLabel(row.catalogType)}
                  accent={accent}
                />
              )}
              <VersionChip version={row.version} accent={accent} />
            </>
          );
        }}
        renderStats={(row) => {
          const accent = catalogAccent(row);
          const iconAccentRgb =
            accent?.rgb ??
            (row.catalogType === "TECHNOLOGY"
              ? "2,132,199"
              : row.catalogType === "INTEGRATION"
                ? "124,58,237"
                : "100,116,139");
          const iconStyle = {
            color: `rgb(${iconAccentRgb})`,
            backgroundColor: `rgba(${iconAccentRgb},0.12)`,
            boxShadow: `inset 0 0 0 1px rgba(${iconAccentRgb},0.18)`,
          };
          const boxStyle = accent
            ? {
              borderColor: `rgba(${accent.rgb},0.22)`,
              backgroundColor: `rgba(${accent.rgb},0.05)`,
            }
            : undefined;
          return (
            <div
              style={boxStyle}
              className="inline-flex w-fit self-start items-stretch rounded-xl border border-[#edf0f4] dark:border-white/[0.07] bg-white/10 dark:bg-white/[0.02]"
            >
              <div className="flex items-center gap-2 px-2.5 py-2">
                <span className="flex size-6 flex-shrink-0 items-center justify-center rounded-full" style={iconStyle}>
                  <Layers className="size-3.5" />
                </span>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="text-[15px] font-bold text-foreground tabular-nums leading-none whitespace-nowrap">
                    {row.group}
                  </span>
                  <span className="text-[10px] font-semibold capitalize tracking-normal text-[#94a3b8] dark:text-white/45 whitespace-nowrap">
                    Groups
                  </span>
                </div>
              </div>
              <div
                style={accent ? { backgroundColor: `rgba(${accent.rgb},0.22)` } : undefined}
                className="w-px self-stretch my-2.5 bg-[#e6eaef] dark:bg-white/[0.08]"
              />
              <div className="flex items-center gap-2 px-2.5 py-2">
                <span className="flex size-6 flex-shrink-0 items-center justify-center rounded-full" style={iconStyle}>
                  <ShieldCheck className="size-3.5" />
                </span>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="text-[15px] font-bold text-foreground tabular-nums leading-none whitespace-nowrap">
                    {row.controls}
                  </span>
                  <span className="text-[10px] font-semibold capitalize tracking-normal text-[#94a3b8] dark:text-white/45 whitespace-nowrap">
                    Controls
                  </span>
                </div>
              </div>
            </div>
          );
        }}
        renderDates={(row) => (
          // min-h-8 keeps this footer row the same height on every card even
          // when RBAC hides an action, so the icons never shift up or down
          // relative to their neighbours.
          <div className="flex min-h-8 items-center justify-between gap-2">
            <MetaDates
              created={row.created}
              updated={row.lastUpdated}
              accent={catalogAccent(row)}
            />
            <div className="flex-shrink-0">
              <CardActions
                deleteModule="catalog"
                exportTooltip="Export catalog"
                deleteTooltip="Delete catalog"
                deleteDisabledTooltip="Only draft catalogs can be deleted"
                canDelete={row.lifecycleStatus === "draft"}
                exporting={exportingId === row.documentId}
                deleting={deleting === row.id}
                onExport={() => {
                  setExportModalDocId(row.documentId);
                  setExportModalName(row.name);
                }}
                onDelete={() => handleDeleteCatalog(row.id)}
              />
            </div>
          </div>
        )}
        serverSideTable
        renderTable={() => (
          <DataTable<CatalogRow>
            serverResponse={catalogs}
            totalItems={catTotal}
            page={catPage}
            pageSize={catPageSize}
            onPageChange={setCatPage}
            itemLabel="catalogs"
            // Cell text is cut at this many characters, with the full value on
            // hover. Drop the prop and the table's own default (100) applies.
            maxCellChars={100}
            error={error}
            suppressErrorAlert
            onRetry={fetchCatalogs}
            rowKey={(row) => row.id}
            onRowClick={(row) => handleSelectCatalog(row)}
            emptyText="No catalogs found"
            emptyIcon={
              <BookOpen className="size-4 text-[#64748b] dark:text-white/50" />
            }
            sortKey={catSortKey}
            sortDir={catSortDir}
            onSort={handleCatSort}
            sortIcon="arrows"
            columns={[
              {
                key: "_sno",
                header: "#",
                align: "center",
                width: "60px",
                render: (_row, index) => (
                  <span className="font-medium d">{catRowSerial(index)}</span>
                ),
              },
              {
                key: "name",
                header: "Title",
                align: "left",
                headerAlign: "center",
                sortable: true,
                ellipsis: 2,
                render: (row) => (
                  <span className="font-medium text-foreground">{row.name}</span>
                ),
              },
              {
                key: "catalogType",
                header: "Type",
                align: "left",
                sortable: true,
                width: "130px",
                render: (row) =>
                  row.catalogType ? (
                    <TypeBadge
                      label={catalogTypeLabel(row.catalogType)}
                      variant={
                        row.catalogType === "TECHNOLOGY"
                          ? "sky"
                          : row.catalogType === "INTEGRATION"
                            ? "violet"
                            : "indigo"
                      }
                    />
                  ) : null,
              },
              {
                key: "version",
                header: "Version",
                sortable: true,
                width: "118px",
                render: (row) => <VersionChip version={row.version} />,
              },
              {
                key: "group",
                header: "Groups",
                sortable: true,
                width: "110px",
                render: (row) => (
                  <span className="font-medium">{row.group}</span>
                ),
              },
              {
                key: "controls",
                header: "Controls",
                sortable: true,
                width: "124px",
                render: (row) => (
                  <span className="font-medium">{row.controls}</span>
                ),
              },
              {
                key: "created",
                header: "Created",
                sortable: true,
                width: "124px",
                cellClassName: "whitespace-nowrap",
                render: (row) => (
                  <span className="font-medium text-[#475569] dark:text-white/70">
                    {row.created}
                  </span>
                ),
              },
              {
                key: "lastUpdated",
                header: "Updated",
                sortable: true,
                width: "122px",
                cellClassName: "whitespace-nowrap",
                render: (row) => (
                  <span className="font-medium text-[#475569] dark:text-white/70">
                    {row.lastUpdated}
                  </span>
                ),
              },
              {
                key: "lifecycleStatus",
                header: "Status",
                sortable: true,
                width: "132px",
                render: (row) => (
                  // Same compact pill the cards use, so one status reads
                  // identically in both views.
                  <StatusBadge
                    status={row.lifecycleStatus as Parameters<typeof StatusBadge>[0]["status"]}
                    size="sm"
                  />
                ),
              },
              {
                key: "actions",
                header: "Actions",
                width: "108px",
                render: (row) => (
                  <div
                    className="flex items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CardActions
                      deleteModule="catalog"
                      exportTooltip="Export catalog"
                      deleteTooltip="Delete catalog"
                      deleteDisabledTooltip="Only draft catalogs can be deleted"
                      canDelete={row.lifecycleStatus === "draft"}
                      exporting={exportingId === row.documentId}
                      deleting={deleting === row.id}
                      onExport={() => {
                        setExportModalDocId(row.documentId);
                        setExportModalName(row.name);
                      }}
                      onDelete={() => handleDeleteCatalog(row.id)}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      />

      {/* ── OSCAL Import Pipeline Modal ── */}
      <Modal
        open={showImport}
        onClose={() => {
          setShowImport(false);
          resetImport();
        }}
        title="Import Catalog"
        size="md"
        footer={
          importStep === "select" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runImport}
                disabled={!validateResult || validating || !canPickImportSource}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!validateResult || validating || !canPickImportSource
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  }`}
              >
                Import
              </button>
            </div>
          ) : importStep === "url" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setImportStep("select");
                  setImportUrl("");
                  setImportFile(null);
                  setValidateResult(null);
                  setImportError(null);
                  setValidating(false);
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runImport}
                disabled={!validateResult || validating || !canPickImportSource}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!validateResult || validating || !canPickImportSource
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  }`}
              >
                Import
              </button>
            </div>
          ) : importStep === "findings" ? (
            <div className="flex items-center justify-end w-full gap-3">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  resetImport();
                }}
                className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          ) : null
        }
      >
        {/* Step 0: Select source */}
        {importStep === "select" && (
          <div className="py-6 flex flex-col items-center gap-6">
            <input
              id="oscal-file-input"
              type="file"
              accept=".json,.yaml,.yml,.xml"
              className="hidden"
              // Clear the value on each open so re-selecting the SAME file
              // still fires onChange (browsers skip change when value is equal).
              onClick={(e) => {
                (e.target as HTMLInputElement).value = "";
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImportFile(file);
                  // Auto-validate on file selection
                  runValidation(file);
                }
              }}
            />

            {/* Organization + Catalog Type selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <div className="w-full">
                <p className="text-xs font-medium d mb-2">
                  Organization<span className="text-red-500 ml-0.5">*</span>
                </p>
                {accessibleOrgsLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="size-4 animate-spin d" />
                    <span className="text-sm d">Loading</span>
                  </div>
                ) : accessibleOrgs.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      No accessible organizations.
                    </span>
                  </div>
                ) : accessibleOrgs.length === 1 ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                    <span className="font-medium">
                      {accessibleOrgs[0].name}
                    </span>
                  </div>
                ) : (
                  <FormSelect
                    options={accessibleOrgs.map((o) => ({
                      value: o.id,
                      // Name only: the short code is an internal identifier and
                      // the org id is what the backend is given.
                      label: o.name,
                    }))}
                    value={importOrgId}
                    onChange={setImportOrgId}
                    placeholder="Select Organization"
                  />
                )}
              </div>
              <div className="w-full">
                <p className="text-xs font-medium d mb-2">
                  Catalog Type<span className="text-red-500 ml-0.5">*</span>
                </p>
                <FormSelect
                  options={CATALOG_TYPE_OPTIONS}
                  value={importCatalogType}
                  onChange={(v) => {
                    const next = v as CatalogType;
                    setImportCatalogType(next);
                    // Technology/Integration catalogs aren't tied to a
                    // jurisdiction — clear any previously-picked country so
                    // a stale value can't get silently submitted once the
                    // field is hidden.
                    if (next !== "REGULATORY") setImportCountryCode("");
                    // Re-run the catalog-type gate against the loaded file
                    if (importFile) runValidation(importFile, next);
                  }}
                  placeholder="Select catalog type"
                />
              </div>
            </div>

            {/* Country selector — only meaningful for Regulatory catalogs
             * (Technology/Integration catalogs aren't tied to a jurisdiction). */}
            {importCatalogType === "REGULATORY" && (
              <div className="w-full">
                <p className="text-xs font-medium d mb-2">Country</p>
                <FormSelect
                  options={COUNTRIES.map((c) => ({
                    value: c.code,
                    label: c.name,
                  }))}
                  value={importCountryCode}
                  onChange={(v) => {
                    setImportCountryCode(v);
                    // Re-run validation with the new country (mirrors the
                    // catalog-type selector's re-validate-on-change behavior).
                    if (importFile) runValidation(importFile, importCatalogType, v);
                  }}
                  placeholder="Select country"
                  searchable
                />
              </div>
            )}

            {/* Import type is always "full" until Delta ships, so the picker
                is not shown. */}

            <p className="text-sm font-semibold text-foreground">
              Select a source to add from
            </p>
            <div className="flex items-center justify-center gap-8">
              {/* Upload file - opens file picker directly (gated until Organization + Catalog Type are chosen) */}
              <Tip label={canPickImportSource ? "" : "Select Organization and catalog type first"}>
                <button
                  onClick={() =>
                    document.getElementById("oscal-file-input")?.click()
                  }
                  disabled={!canPickImportSource}
                  className="flex flex-col items-center gap-3 group disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Upload className="size-8 d group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm d group-hover:text-foreground transition-colors">
                    Upload file
                  </span>
                </button>
              </Tip>
              {/* URL link */}
              <Tip label={canPickImportSource ? "" : "Select Organization and catalog type first"}>
                <button
                  onClick={() => setImportStep("url")}
                  disabled={!canPickImportSource}
                  className="flex flex-col items-center gap-3 group disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Link2 className="size-8 d group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm d group-hover:text-foreground transition-colors">
                    Source link
                  </span>
                </button>
              </Tip>
            </div>
            {/* Show selected file inline with validation status */}
            {importFile && (
              <div
                className={`rounded-lg border w-full ${validateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : importError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <FileText
                    className={`size-6 flex-shrink-0 ${validateResult
                        ? "text-emerald-500"
                        : importError
                          ? "text-red-500"
                          : "d"
                      }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {importFile.name}
                    </p>
                    <p className="text-xs d">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                    {validating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Validating…
                      </p>
                    )}
                    {!validating && validateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {validateResult.documentType}, OSCAL{" "}
                        {validateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setImportFile(null);
                      setValidateResult(null);
                      setImportError(null);
                      setValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 d" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1b: URI link */}
        {importStep === "url" && (
          <div className="space-y-4">
            <FormField
              label="Document URL"
              required
              hint="URL must end in .json, .yaml, or .xml"
            >
              <div className="flex gap-2">
                <div className="flex-1">
                  <FormInput
                    placeholder="https://csrc.nist.gov/projects/oscal/"
                    value={importUrl}
                    onChange={(v) => {
                      setImportUrl(v);
                      setValidateResult(null);
                      setImportError(null);
                    }}
                    disabled={validating}
                  />
                </div>
                <button
                  onClick={fetchUrl}
                  disabled={!importUrl.trim() || validating}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${!importUrl.trim() || validating
                      ? "bg-muted d cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  {validating ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="size-3.5 animate-spin" /> Fetching…
                    </span>
                  ) : (
                    "Fetch"
                  )}
                </button>
              </div>
            </FormField>

            {/* Inline file card - mirrors the file upload card */}
            {(validating || validateResult || importError) && (
              <div
                className={`rounded-lg border w-full ${validateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : importError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <Link2
                    className={`size-6 flex-shrink-0 ${validateResult ? "text-emerald-500" : importError ? "text-red-500" : "d"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {importUrl.split("/").pop() || importUrl}
                    </p>
                    <p className="text-xs d truncate">{importUrl}</p>
                    {validating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Fetching &
                        validating…
                      </p>
                    )}
                    {!validating && validateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {validateResult.documentType}, OSCAL{" "}
                        {validateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setValidateResult(null);
                      setImportError(null);
                      setValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 d" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2a: Validating */}
        {importStep === "validating" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Validating document…</p>
            <p className="text-xs d text-center">
              Checking OSCAL schema and document structure.
            </p>
          </div>
        )}

        {/* Step 2b: Validated successfully */}
        {importStep === "validated" && validateResult && (
          <div className="py-8 flex flex-col items-center gap-4 text-center">
            <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="size-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold">Validation Passed</p>
            <div className="px-4 py-3 rounded-lg bg-muted border border-border w-full text-left space-y-1.5">
              <p className="text-xs">
                <span className="d">Document Type:</span>{" "}
                <span className="font-medium">
                  {validateResult.documentType}
                </span>
              </p>
              <p className="text-xs">
                <span className="d">OSCAL Version:</span>{" "}
                <span className="font-medium">
                  {validateResult.oscalVersion}
                </span>
              </p>
              {importFile && (
                <p className="text-xs">
                  <span className="d">File:</span>{" "}
                  <span className="font-medium">{importFile.name}</span>
                </p>
              )}
              {!importFile && importUrl && (
                <p className="text-xs">
                  <span className="d">Source:</span>{" "}
                  <span className="font-medium truncate">{importUrl}</span>
                </p>
              )}
              <p className="text-xs">
                <span className="d">Import Mode:</span>{" "}
                <span className="font-medium capitalize">{importMode}</span>
              </p>
              {validateResult.findings.length > 0 && (
                <div className="pt-1.5 border-t border-border mt-1.5">
                  <p className="text-xs font-semibold d mb-2">
                    Findings ({validateResult.findings.length})
                  </p>
                  <div className="space-y-1.5">
                    {validateResult.findings.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span
                          className={`px-1.5 py-0.5 rounded font-bold text-[10px] flex-shrink-0 ${f.severity?.toLowerCase() === "warning"
                              ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                              : f.severity?.toLowerCase() === "info"
                                ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                                : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                            }`}
                        >
                          {f.severity}
                        </span>
                        <p className="d">{f.title || f.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2c: Importing */}
        {importStep === "importing" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Importing document…</p>
            <p className="text-xs d text-center">
              Uploading and processing your OSCAL catalog. This may take a
              moment for large files.
            </p>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmDialog
        open={deleteModalOpen}
        type="Catalog"
        name={catalogs.find((c) => c.id === deleteTargetId)?.name || ""}
        deleting={!!deleting}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={confirmDeleteCatalog}
      />


      {/* ── Import Session Modal ── */}
      <Modal
        open={showSession}
        onClose={() => {
          setShowSession(false);
          setSessionData(null);
        }}
        title={
          sessionData
            ? sessionData.jobStatus.toLowerCase() === "committed"
              ? "Import Complete"
              : sessionData.jobStatus.toLowerCase() === "validated"
                ? "Save Catalog"
                : sessionData.jobStatus.toLowerCase() === "failed" ||
                  sessionData.jobStatus.toLowerCase() === "aborted"
                  ? "Import Failed"
                  : "Importing Catalog"
            : "Import Catalog"
        }
        size="md"
        closable={false}
        footer={
          sessionData ? (
            <div className="flex justify-end w-full gap-2">
              {sessionData.jobStatus.toLowerCase() === "committed" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  Done
                </button>
              ) : sessionData.jobStatus.toLowerCase() === "failed" ||
                sessionData.jobStatus.toLowerCase() === "aborted" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleAbortSession}
                    disabled={aborting || committing}
                    className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {aborting ? "Cancelling…" : "Cancel"}
                  </button>
                  <button
                    onClick={handleCommitSession}
                    disabled={
                      committing ||
                      aborting ||
                      sessionData.jobStatus.toLowerCase() !== "validated"
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${committing ||
                        aborting ||
                        sessionData.jobStatus.toLowerCase() !== "validated"
                        ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                        : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                      }`}
                  >
                    {committing ? "Saving…" : "Save Catalog"}
                  </button>
                </>
              )}
            </div>
          ) : null
        }
      >
        {sessionLoading && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Loading session…</p>
          </div>
        )}

        {sessionError && (
          <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{sessionError}</p>
          </div>
        )}

        {sessionData && (
          <div className="space-y-5">
            {/* Commit progress overlay */}
            {committing && (
              <div className="py-6 flex flex-col items-center gap-3">
                <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
                <p className="text-sm font-medium">Committing import…</p>
                <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                    style={{ width: "70%" }}
                  />
                </div>
              </div>
            )}

            {/* Session details */}
            {!committing && (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                  <div className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-4 py-1.5 border-b border-border">
                    <span className="text-xs d">Status</span>
                    <span
                      className={`text-sm font-semibold capitalize text-right min-w-0 ${sessionData.jobStatus.toLowerCase() === "committed"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : sessionData.jobStatus.toLowerCase() === "failed" ||
                            sessionData.jobStatus.toLowerCase() === "aborted"
                            ? "text-red-600 dark:text-red-400"
                            : sessionData.jobStatus.toLowerCase() ===
                              "validated"
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                      {sessionData.jobStatus}
                    </span>
                  </div>
                  <div className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-4 py-1.5 border-b border-border">
                    <span className="text-xs d">Doc Type</span>
                    <span className="text-sm font-medium capitalize text-right min-w-0">
                      {sessionData.documentType}
                    </span>
                  </div>
                  <div className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-4 py-1.5 border-b border-border">
                    <span className="text-xs d">Name</span>
                    <span className="text-sm font-medium text-right min-w-0 break-words whitespace-normal">
                      {sessionData.sourceFilename}
                    </span>
                  </div>
                  {importCatalogType && (
                    <div className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-4 py-1.5 border-b border-border">
                      <span className="text-xs d">Catalog Type</span>
                      <span className="flex justify-end min-w-0">
                        <CatalogTypeBadge type={importCatalogType} />
                      </span>
                    </div>
                  )}
                </div>

                {/* Findings */}
                {sessionData.findings.length > 0 && (
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-bold d mb-2">
                      Findings ({sessionData.findings.length})
                    </h4>
                    <div className="space-y-2">
                      {sessionData.findings.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-start gap-2 text-xs"
                        >
                          <span
                            className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "warning"
                                ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                : f.severity === "info"
                                  ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                                  : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                              }`}
                          >
                            {f.severity}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{f.title}</p>
                            <p className="d">{f.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Export Pulse Overlay ── */}
      {exportingId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-white dark:bg-zinc-900 rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting as {exportFormat.toUpperCase()}
            </p>
            <p className="text-xs d">Please wait while we prepare your file</p>
          </div>
        </div>
      )}

      {/* ── Export Format Modal ── */}
      <Modal
        open={!!exportModalDocId}
        onClose={() => setExportModalDocId(null)}
        title="Export Catalog"
        description={`Choose a file format to export "${exportModalName}".`}
        size="sm"
      >
        <div className="grid grid-cols-3 gap-3">
          {(["json", "xml", "yaml"] as const).map((fmt) => (
            <button
              key={fmt}
              onClick={() => {
                if (exportModalDocId)
                  handleExportCatalog(exportModalDocId, fmt);
              }}
              disabled={exportingId === exportModalDocId}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
            >
              {exportingId === exportModalDocId ? (
                <Loader2 className="size-6 animate-spin d" />
              ) : (
                <Upload className="size-6 d group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
              )}
              <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {fmt.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}


function renderProseWithParamValues(
  prose: string,
  params?: { paramId?: string; values?: (string | null)[] }[],
): React.ReactNode {
  if (!prose) return null;
  const tokenRe =
    /\{\{\s*insert:\s*param\s*,\s*(?:id-ref\s*=\s*")?([\w.-]+)"?\s*\}\}/gi;
  if (!tokenRe.test(prose)) return prose;
  tokenRe.lastIndex = 0;
  const paramMap = new Map(
    (params ?? []).map((p) => [(p.paramId ?? "").toLowerCase(), p]),
  );
  const GREEN =
    "inline-flex rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-sm font-medium text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300";
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(prose)) !== null) {
    if (m.index > cursor) nodes.push(prose.slice(cursor, m.index));
    const param = paramMap.get(m[1].toLowerCase());
    const value = (param?.values ?? [])
      .filter((v) => v != null && String(v).trim() !== "")
      .map(String)
      .join(", ");
    nodes.push(
      value ? (
        <Tooltip key={i}>
          <TooltipTrigger asChild>
            <span className={GREEN}>{value}</span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={6}
            align="start"
            collisionPadding={8}
            className="w-max max-w-none whitespace-nowrap font-mono text-xs"
          >
            {m[1]}
          </TooltipContent>
        </Tooltip>
      ) : (
        <span key={i}>{m[0]}</span>
      ),
    );
    cursor = m.index + m[0].length;
    i++;
  }
  if (cursor < prose.length) nodes.push(prose.slice(cursor));
  return nodes;
}

/* ── Part block: recursive rendering of control parts ── */
function PartBlock({
  part,
  depth,
  params,
}: {
  part: PartResponse;
  depth: number;
  params?: { paramId?: string; values?: (string | null)[] }[];
}) {
  const nameLabels: Record<string, string> = {
    statement: "Statement",
    guidance: "Guidance",
    objective: "Objective",
    assessment: "Assessment",
    item: "",
  };
  const label = nameLabels[part.name] ?? part.name;

  if (depth === 0) {
    return (
      <div className="space-y-2">
        {label && (
          <h4 className="text-sm font-semibold text-foreground capitalize">
            {label}
          </h4>
        )}
        <div className="rounded-md border border-border bg-muted/20 p-3">
          {part.prose && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {renderProseWithParamValues(part.prose, params)}
            </p>
          )}
          {part.parts && part.parts.length > 0 && (
            <div className="mt-2 space-y-1 pl-3 border-l-2 border-border">
              {part.parts.map((child) => (
                <PartBlock
                  key={child.partId}
                  part={child as PartResponse}
                  depth={depth + 1}
                  params={params}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {part.prose && (
        <p className="text-sm d leading-relaxed whitespace-pre-wrap">
          {part.partId && (
            <span className="font-mono text-[10px] d mr-1.5">
              {part.partId.split(".").pop()}
            </span>
          )}
          {renderProseWithParamValues(part.prose, params)}
        </p>
      )}
      {part.parts && part.parts.length > 0 && (
        <div className="space-y-1 pl-3 border-l-2 border-border mt-1">
          {part.parts.map((child) => (
            <PartBlock
              key={child.partId}
              part={child as PartResponse}
              depth={depth + 1}
              params={params}
            />
          ))}
        </div>
      )}
    </div>
  );
}
